import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AutoComplete, Button, Card, Checkbox, Divider, Drawer, Empty, Input, List, Modal, Popconfirm, Select, Space, Steps, Table, Tabs, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, DeleteOutlined, DownloadOutlined, EditOutlined, InboxOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import request from '../../utils/request';
import { getAllWarehouses } from '../../services/warehouse';
import { getCurrentUserProjects, getCustomerList, getProjectList } from '../../services/project';
import { getLogisticsProviders } from '../../services/logistics';
import {
  cancelInstructionImportJob,
  createInstructionImportJob,
  createInstructionImportMappingProfile,
  deleteInstructionImportMappingProfile,
  executeInstructionImportJob,
  listInstructionImportJobItems,
  listInstructionImportJobs,
  listInstructionImportMappingProfiles,
  precheckInstructionImport,
  resolveProjectByTag,
  updateInstructionImportMappingProfile,
  type InstructionImportJob,
  type InstructionImportJobItem,
  type InstructionImportMappingProfile,
  type InstructionImportPrecheckResult,
  type ResolveProjectByTagResult,
} from '../../services/instruction-import';

type ScenarioKey = 'upload_immediate' | 'upload_after_pack' | 'warehouse_provide';

type ScenarioMeta = {
  label: string;
  description: string;
  requiredFields: string[];
  optionalFields: string[];
};

const SCENARIO_META: Record<ScenarioKey, ScenarioMeta> = {
  upload_immediate: {
    label: '由客户提供运单-立即上传',
    description: '创建指令时同步上传运单附件，适用于客户已提供运单文件的场景。',
    requiredFields: ['项目编码', '仓库编码', '库存单位编码', '数量', '收件人', '地址1', '城市', '州/省', '邮编', '国家', '承运商', '物流跟踪号'],
    optionalFields: ['地址2', '电话', '邮箱', '买家备注', '指令名称', '备注'],
  },
  upload_after_pack: {
    label: '由客户提供运单-先申请包装数据',
    description: '先创建待包装指令，等待包装结果后再上传运单信息。',
    requiredFields: ['项目编码', '仓库编码', '库存单位编码', '数量', '收件人', '地址1', '城市', '州/省', '邮编', '国家'],
    optionalFields: ['承运商', '物流跟踪号', '地址2', '电话', '邮箱', '买家备注', '指令名称', '备注'],
  },
  warehouse_provide: {
    label: '由仓库提供运单',
    description: '由仓库完成运单采购与回传，导入时无需填写客户运单信息。',
    requiredFields: ['项目编码', '仓库编码', '库存单位编码', '数量', '收件人', '地址1', '城市', '州/省', '邮编', '国家'],
    optionalFields: ['地址2', '电话', '邮箱', '买家备注', '指令名称', '备注'],
  },
};

// 高级设置：字段映射功能已重新启用（2026-09-08）。注意"一单多件分隔符展开"（explode）
// 规则仍不支持：后端执行阶段会直接拦截报错，见 tasks.py 的 explode_rule_not_supported 分支，
// 前端这里也没有对应的配置界面——目前只有列映射/固定值/自动生成/值映射/数值清洗这几种能力可用。
const ADVANCED_MAPPING_ENABLED = true;
const SCENARIO_KEYS: ScenarioKey[] = ['upload_immediate', 'upload_after_pack', 'warehouse_provide'];
const MANUAL_WAYBILL_UNMATCHED_SENTINEL = '__MANUAL_UNMATCHED__';
const IMPORT_CREATE_DRAFT_STORAGE_KEY_PREFIX = 'instruction_import_create_draft_v1';
const IMPORT_CREATE_DRAFT_DB_NAME = 'instruction_import_create_draft_db';
const IMPORT_CREATE_DRAFT_DB_STORE = 'files';
const DEBUG_IMPORT_DRAFT = true;
const IMPORT_DISPLAY_ALL_FIELDS: string[] = [
  '仓库',
  '项目',
  '库存单元标识',
  '发货数量',
  '收件人姓名',
  '公司名称',
  '地址第一行',
  '地址第二行',
  '城市',
  '州/省',
  '邮编',
  '国家',
  '电话',
  '邮箱',
  '买家备注',
  '是否需要签名服务',
  '保险金额',
  '偏好物流承运商',
  '运单所属物流承运商',
  '物流跟踪号',
  '参考号',
  '指令命名',
  '指令备注',
  '物流运单附件文件名',
];
const SCENARIO_DISPLAY_REQUIRED_FIELDS: Record<ScenarioKey, string[]> = {
  // “物流运单附件文件名”不是必填：它只是加速运单附件匹配的辅助字段，留空时自动走跟踪号/
  // 条码/OCR 等智能匹配。
  upload_immediate: ['指令序号', '仓库', '项目', '库存单元标识', '发货数量', '运单所属物流承运商', '物流跟踪号'],
  upload_after_pack: ['指令序号', '仓库', '项目', '库存单元标识', '发货数量'],
  warehouse_provide: ['指令序号', '仓库', '项目', '库存单元标识', '发货数量', '收件人姓名', '地址第一行', '城市', '州/省', '邮编', '国家', '是否需要签名服务', '保险金额'],
};
const SCENARIO_DISPLAY_USAGE_TEXT: Record<ScenarioKey, string> = {
  upload_immediate: '由客户提供物流运单，并在创建指令时提交运单附件。',
  upload_after_pack: '创建代发指令时无需提交运单附件，仓库将在打包后回填包裹精确重量和尺寸（可至指令详情页查看结果），然后由客户依据包装结果追加出库指令并同时上传运单。',
  warehouse_provide: '由仓库购买运单并上传。会产生物流相关费用，如运费、保险费等。',
};
const SCENARIO_TEMPLATE_FILE_NAME: Record<ScenarioKey, string> = {
  upload_immediate: '一件代发批量上传模板-立即上传运单',
  upload_after_pack: '一件代发批量上传模板-先申请包装数据',
  warehouse_provide: '一件代发批量上传模板-仓库提供运单',
};
const SCENARIO_OPTIONAL_EXCLUDED_FIELDS: Partial<Record<ScenarioKey, string[]>> = {
  upload_after_pack: ['运单所属物流承运商', '物流跟踪号', '物流运单附件文件名'],
  warehouse_provide: ['运单所属物流承运商', '物流跟踪号', '物流运单附件文件名'],
};
const getScenarioDisplayOptionalFields = (scenarioKey: ScenarioKey) => {
  const required = SCENARIO_DISPLAY_REQUIRED_FIELDS[scenarioKey] || [];
  const excluded = SCENARIO_OPTIONAL_EXCLUDED_FIELDS[scenarioKey] || [];
  return IMPORT_DISPLAY_ALL_FIELDS.filter((field) => !required.includes(field) && !excluded.includes(field));
};
const getScenarioValidationRequiredFields = (scenarioKey: ScenarioKey) => {
  const required = SCENARIO_DISPLAY_REQUIRED_FIELDS[scenarioKey] || [];
  // 保险金额允许留空（留空或输入0均视为不需要保险），因此不参与“必填值”校验。
  // 是否需要签名服务同理：留空按 _to_bool 的定义会被当作“否/不需要签名”这个合法默认值
  // 处理（跟填“否”效果一样），不是数据缺失，同样不该被必填校验拦下来。
  if (scenarioKey === 'warehouse_provide') {
    return required.filter((field) => field !== '保险金额' && field !== '是否需要签名服务');
  }
  return required;
};
type TemplateDropdownOptions = {
  warehouseOptions: string[];
  projectOptions: string[];
  providerOptions: string[];
  countryOptions: string[];
  signatureOptions: string[];
};

// “国家”字段的合法值不是导入这边定义的，是“一件代发(系统级标准指令)”这份指令模板问卷里
// key 为 country 的那道题的 options——跟手工创建指令时（Create.tsx）下拉框读的是同一份数据、
// 同一个模板详情接口。这里现读现用，不在导入这边写死一份副本：以后模板问卷改了国家的可选项，
// 这里下次拉取时会自动跟着变，不需要为此改动导入页面的代码。
const fetchDropshipTemplateCountryOptions = async (): Promise<string[]> => {
  try {
    const listResp = await request.get('/api/v1/instruction/templates/', {
      params: { search: '一件代发(系统级标准指令)', is_active: true, page_size: 20 },
    });
    const listData = listResp?.data;
    const results = Array.isArray(listData?.results) ? listData.results : (Array.isArray(listData) ? listData : []);
    const matched = results
      .filter((t: any) => String(t?.name || '').trim() === '一件代发(系统级标准指令)')
      .sort((a: any, b: any) => Number(b?.id || 0) - Number(a?.id || 0));
    const tplId = matched[0]?.id;
    if (!tplId) return [];
    const detailResp = await request.get(`/api/v1/instruction/templates/${tplId}/`);
    const questions = Array.isArray(detailResp?.data?.questionnaire?.questions) ? detailResp.data.questionnaire.questions : [];
    const countryQuestion = questions.find((q: any) => String(q?.key || '').trim() === 'country');
    const options = Array.isArray(countryQuestion?.options) ? countryQuestion.options : [];
    return Array.from(new Set(
      options.map((o: any) => String(o?.value ?? '').trim()).filter(Boolean),
    ));
  } catch {
    return [];
  }
};

// 管理员代客户操作时，“项目”列表必须按这个客户过滤，不能沿用管理员账号自己能看到的
// 全部项目——项目本来就是归属到具体某个客户的（Project.customer_id），跟仓库/承运商/
// 国家这几个全平台共享、不分客户的主数据性质不一样。跟 Create.tsx 手工创建指令页面
// 里管理员选好客户后加载项目列表（loadAdminProjects）用的是同一个 customer 过滤参数。
const buildTemplateDropdownOptions = async (actingAsCustomerId?: number): Promise<TemplateDropdownOptions> => {
  const loadProjects = async (): Promise<any[]> => {
    if (!actingAsCustomerId) return getCurrentUserProjects();
    const resp: any = await getProjectList({
      customer: actingAsCustomerId,
      page: 1,
      page_size: 1000,
      ordering: 'project_code',
    });
    const data = resp?.data;
    return Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
  };
  const [warehouses, projects, providersResp, countryOptions] = await Promise.all([
    getAllWarehouses(),
    loadProjects(),
    getLogisticsProviders({ page: 1, page_size: 1000 }),
    fetchDropshipTemplateCountryOptions(),
  ]);
  const warehouseOptions = (warehouses || [])
    .map((item: any) => {
      const code = String(item?.code || '').trim();
      const name = String(item?.name || '').trim();
      if (!code && !name) return '';
      return [code, name].filter(Boolean).join(' - ');
    })
    .filter(Boolean);
  const projectOptions = (projects || [])
    .map((item: any) => {
      const code = String(item?.project_code || '').trim();
      const name = String(item?.name || item?.project_name || '').trim();
      if (!code && !name) return '';
      return [code, name].filter(Boolean).join(' - ');
    })
    .filter(Boolean);
  const providerResults = Array.isArray((providersResp as any)?.data?.results) ? (providersResp as any).data.results : [];
  const providerOptions = (providerResults || [])
    .map((item: any) => {
      const code = String(item?.code || '').trim();
      const name = String(item?.name || '').trim();
      if (!code && !name) return '';
      return [code, name].filter(Boolean).join(' - ');
    })
    .filter(Boolean);

  return {
    warehouseOptions: Array.from(new Set(warehouseOptions)),
    projectOptions: Array.from(new Set(projectOptions)),
    providerOptions: Array.from(new Set(providerOptions)),
    countryOptions,
    signatureOptions: ['是', '否'],
  };
};

const getScenarioFromSearch = (search: string): ScenarioKey => {
  const params = new URLSearchParams(search || '');
  const scenario = String(params.get('scenario') || '').trim() as ScenarioKey;
  if (SCENARIO_KEYS.includes(scenario)) {
    return scenario;
  }
  return 'upload_immediate';
};

const buildStyledScenarioTemplate = async (scenarioKey: ScenarioKey, dropdownOptions: TemplateDropdownOptions) => {
  const requiredHeaders = SCENARIO_DISPLAY_REQUIRED_FIELDS[scenarioKey] || [];
  const optionalHeaders = getScenarioDisplayOptionalFields(scenarioKey);
  const headers = [...requiredHeaders, ...optionalHeaders];

  const ExcelJSImport = await import('exceljs');
  const ExcelJS: any = (ExcelJSImport as any)?.default || ExcelJSImport;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('模板');
  const buildSampleValues = (serialNo?: number) => headers.map((h) => {
    if (h === '指令序号' && Number.isFinite(serialNo)) return String(serialNo);
    if (h === '国家' && serialNo === 1) return 'USA';
    return '';
  });
  const emptyOptionKinds: string[] = [];
  if ((dropdownOptions.warehouseOptions || []).length === 0) emptyOptionKinds.push('仓库');
  if ((dropdownOptions.projectOptions || []).length === 0) emptyOptionKinds.push('项目');
  if ((dropdownOptions.providerOptions || []).length === 0) emptyOptionKinds.push('物流服务商');
  if ((dropdownOptions.countryOptions || []).length === 0) emptyOptionKinds.push('国家');
  if (emptyOptionKinds.length > 0) {
    const topWarningRow = sheet.addRow([`提示：当前账号暂无可用${emptyOptionKinds.join('、')}，对应下拉选项将为空。`]);
    sheet.mergeCells(topWarningRow.number, 1, topWarningRow.number, Math.max(1, headers.length));
    const topCell = sheet.getCell(topWarningRow.number, 1);
    topCell.font = { bold: true, color: { argb: 'FFAD4E00' }, size: 12 };
    topCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    topCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFF7E6' },
    };
    topCell.border = {
      top: { style: 'thin', color: { argb: 'FFFFBB96' } },
      left: { style: 'thin', color: { argb: 'FFFFBB96' } },
      bottom: { style: 'thin', color: { argb: 'FFFFBB96' } },
      right: { style: 'thin', color: { argb: 'FFFFBB96' } },
    };
    sheet.getRow(topWarningRow.number).height = 28;
  }
  const headerRow = sheet.addRow(headers);
  const sampleRow = sheet.addRow(buildSampleValues(1));
  const sampleRow2 = sheet.addRow(buildSampleValues(2));
  const headerNotes: Record<string, string> = {
    指令序号: '每个指令应具有唯一的纯数字序号,相同序号行表示同一个指令的多条产品记录,同一指令除第一行外,其他行只需填写指令序号和产品信息,其他信息应与第一行保持一致或留空.',
    仓库: '请从下拉菜单选择仓库',
    项目: '请从下拉菜单选择项目',
    库存单元标识: '指的是产品的分拣条码(如"X00123ABCD")或客户自定义编码(如"ABC-123"),不可填写产品名称(如"新款键盘-蓝黑")',
    运单所属物流承运商: '请从下拉菜单选择物流承运商',
    偏好物流承运商: '请从下拉菜单选择物流承运商',
    物流运单附件文件名: '用于匹配运单附件文件,可填写完整文件名(如1.pdf)或不含扩展名(如1).系统会先按完整文件名匹配(自动移除首尾空格并忽略大小写),未命中时再按去扩展名匹配',
    是否需要签名服务: '留空或选择"否"表示不需要签名服务',
    国家: '美国请填写USA',
    保险金额: '必须为0到1000之间的整数,留空或输入0表示不需要保险',
    参考号: '可添加销售订单号等参考号,便于后续查单。',
    指令命名: '为指令添加一个命名,便于后续查单',
    指令备注: '可在此处备注其他要求',
  };

  headerRow.height = 28;
  sampleRow.height = 22;
  sampleRow2.height = 22;
  headerRow.eachCell((cell: any, colNumber: number) => {
    const isRequired = colNumber <= requiredHeaders.length;
    cell.font = { color: { argb: isRequired ? 'FFCF1322' : 'FF237804' }, bold: true, size: 12 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: isRequired ? 'FFFFEAEA' : 'FFEFFBEF' },
    };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
      left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
      bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
      right: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    };
    const headerText = String(headers[colNumber - 1] || '').trim();
    const note = headerNotes[headerText];
    if (note) {
      cell.note = note;
    }
  });
  const columnWidthOverrides: Record<string, number> = {
    仓库: 30,
    项目: 30,
    库存单元标识: 32,
    运单所属物流承运商: 34,
    物流跟踪号: 40,
    物流运单附件文件名: 46,
  };
  headers.forEach((header, idx) => {
    const col = sheet.getColumn(idx + 1);
    const title = String(header || '').trim();
    const baseWidth = Math.max(20, Math.min(34, title.length + 10));
    col.width = columnWidthOverrides[title] || baseWidth;
  });
  const instructionNoColIndex = headers.findIndex((h) => h === '指令序号') + 1;
  if (instructionNoColIndex > 0) {
    sheet.getCell(sampleRow.number, instructionNoColIndex).alignment = { vertical: 'middle', horizontal: 'center' };
    sheet.getCell(sampleRow2.number, instructionNoColIndex).alignment = { vertical: 'middle', horizontal: 'center' };
  }

  const noteSheet = workbook.addWorksheet('填写说明');
  noteSheet.getColumn(1).width = 110;
  const noteTitleRow = noteSheet.addRow(['填写说明']);
  noteTitleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF1677FF' } };
  noteTitleRow.height = 26;
  const noteLines = [
    '1. 本模板仅用于批量导入创建一件代发指令，不能用于创建其他指令，如"换标装箱""常规出库"等。',
    '2. 在导入记录后，将由系统自动判断是否需要打包，且默认使用仓库提供的免费包材，若需要使用特定包材请使用手动创建指令页面。',
    '3. 默认不提供其他附件打印需求，如装箱单、发票等，若有其他附件需要打印，请使用手动创建指令页面。',
    '4. 三种业务场景（由客户提供运单-立即上传、由客户提供运单-先申请包装数据、由仓库提供运单）分别提供对应的模板文件，它们的必填项不同，模板中的红色列为必填项，绿色列为选填项。',
    '5. 每列的具体说明请参考列头单元格说明信息。',
    '6. 对于含有多条产品信息的指令（一票多款），请使用相同的"指令序号"来标识，每行表示一条产品信息。',
    '7. 请不要修改任何列名。',
    '8. 每个文件最多填写1000行记录，超过1000行请分多次提交。',
  ];
  noteLines.forEach((line) => {
    const row = noteSheet.addRow([line]);
    row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    row.getCell(1).font = { size: 11 };
    row.height = 36;
  });

  const optionSheet = workbook.addWorksheet('options');
  optionSheet.state = 'hidden';
  const writeOptionsToColumn = (colIndex: number, values: string[]) => {
    values.forEach((val, idx) => {
      optionSheet.getCell(idx + 1, colIndex).value = val;
    });
    optionSheet.getColumn(colIndex).width = 46;
  };
  const warehouseList = dropdownOptions.warehouseOptions.length > 0 ? dropdownOptions.warehouseOptions : [''];
  const projectList = dropdownOptions.projectOptions.length > 0 ? dropdownOptions.projectOptions : [''];
  const providerList = dropdownOptions.providerOptions.length > 0 ? dropdownOptions.providerOptions : [''];
  const signatureList = dropdownOptions.signatureOptions.length > 0 ? dropdownOptions.signatureOptions : ['是', '否'];
  const countryList = dropdownOptions.countryOptions.length > 0 ? dropdownOptions.countryOptions : [''];
  writeOptionsToColumn(1, warehouseList);
  writeOptionsToColumn(2, projectList);
  writeOptionsToColumn(3, providerList);
  writeOptionsToColumn(4, signatureList);
  writeOptionsToColumn(5, countryList);
  const firstDataRow = sampleRow.number;
  const maxDataRows = 1000;
  const lastDataRow = firstDataRow + maxDataRows - 1;
  headers.forEach((header, idx) => {
    const colIndex = idx + 1;
    const horizontal = 'center';
    for (let row = firstDataRow; row <= lastDataRow; row += 1) {
      const cell = sheet.getCell(row, colIndex);
      cell.numFmt = '@';
      cell.alignment = { vertical: 'middle', horizontal };
    }
  });
  const applyValidation = (headerName: string, formulaRange: string) => {
    const colIndex = headers.findIndex((h) => h === headerName) + 1;
    if (colIndex <= 0) return;
    for (let row = firstDataRow; row <= lastDataRow; row += 1) {
      sheet.getCell(row, colIndex).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [formulaRange],
        errorStyle: 'stop',
        showErrorMessage: true,
        errorTitle: '输入无效',
        error: '请从下拉列表中选择可用选项。',
      };
    }
  };
  const applyIntegerRangeValidation = (headerName: string, min: number, max: number) => {
    const colIndex = headers.findIndex((h) => h === headerName) + 1;
    if (colIndex <= 0) return;
    const toExcelCol = (index: number) => {
      let n = index;
      let col = '';
      while (n > 0) {
        const m = (n - 1) % 26;
        col = String.fromCharCode(65 + m) + col;
        n = Math.floor((n - 1) / 26);
      }
      return col;
    };
    const colLetter = toExcelCol(colIndex);
    for (let row = firstDataRow; row <= lastDataRow; row += 1) {
      const cellRef = `${colLetter}${row}`;
      sheet.getCell(row, colIndex).dataValidation = {
        type: 'custom',
        allowBlank: true,
        formulae: [`=OR(LEN(TRIM(${cellRef}))=0,AND(ISNUMBER(--${cellRef}),--${cellRef}=INT(--${cellRef}),--${cellRef}>=${min},--${cellRef}<=${max}))`],
        errorStyle: 'stop',
        showErrorMessage: true,
        errorTitle: '输入无效',
        error: `保险金额必须为${min}到${max}之间的整数（可留空或输入0）。`,
      };
    }
  };
  applyValidation('仓库', `'options'!$A$1:$A$${warehouseList.length}`);
  applyValidation('项目', `'options'!$B$1:$B$${projectList.length}`);
  applyValidation('运单所属物流承运商', `'options'!$C$1:$C$${providerList.length}`);
  applyValidation('偏好物流承运商', `'options'!$C$1:$C$${providerList.length}`);
  applyValidation('是否需要签名', `'options'!$D$1:$D$${signatureList.length}`);
  applyValidation('是否需要签名服务', `'options'!$D$1:$D$${signatureList.length}`);
  applyValidation('国家', `'options'!$E$1:$E$${countryList.length}`);
  applyIntegerRangeValidation('保险金额', 0, 1000);

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

const normalizeHeader = (value: unknown) => String(value ?? '').trim();

const normalizeCellText = (value: unknown) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

// 通用的“忽略大小写和首尾空格”比对——用于两处：①值映射的取值比对（源文件里
// “UPS”和“ups”应该被当成同一个值）；②来源列名/表头的比对（配置映射时保存的列名
// 跟这次上传文件的实际表头，只有大小写不同也应该算匹配，不应该报“来源列不存在”）。
// 只用于比对，不用于展示或存储（展示/存储仍然保留原始写法，避免不必要的数据迁移）。
// 后端 tasks.py 的 _normalize_for_case_insensitive_match 是同一套逻辑，两边要保持一致。
const normalizeForCaseInsensitiveMatch = (value: unknown) => normalizeCellText(value).toLowerCase();

// 值映射查表：先尝试精确匹配（多数情况下键本身就是标准写法，直接命中最快），
// 精确匹配不到时，再按“忽略大小写和首尾空格”扫描一遍已配置的 key。找不到就返回
// undefined，调用方按“查不到就用原始值”处理。
const lookupValueMapEntry = (valueMap: Record<string, string>, rawVal: string): string | undefined => {
  if (Object.prototype.hasOwnProperty.call(valueMap, rawVal)) {
    return valueMap[rawVal];
  }
  const normalized = normalizeForCaseInsensitiveMatch(rawVal);
  const matchedKey = Object.keys(valueMap).find((k) => normalizeForCaseInsensitiveMatch(k) === normalized);
  return matchedKey !== undefined ? valueMap[matchedKey] : undefined;
};

// 按“来源列”名字从一行数据里取值：先精确取（多数情况下列名就是标准写法），取不到
// 再按“忽略大小写和首尾空格”找这一行里实际存在、名字相同（忽略大小写）的那一列。
// 这样映射配置里保存的列名跟这次上传文件的表头只有大小写不同时，也能正确取到值——
// 不能只改表头存在性校验（runHeaderCheck）却不改这里，否则会出现“校验通过了，但取值
// 是空的”这种更隐蔽的问题。
const getRowValueByHeaderName = (row: ParsedRow, headerName: string): unknown => {
  if (Object.prototype.hasOwnProperty.call(row, headerName)) {
    return row[headerName];
  }
  const normalized = normalizeForCaseInsensitiveMatch(headerName);
  const matchedKey = Object.keys(row).find((k) => normalizeForCaseInsensitiveMatch(k) === normalized);
  return matchedKey !== undefined ? row[matchedKey] : undefined;
};

// 表头是否存在：同样忽略大小写和首尾空格，跟上面两个查表函数保持同一套语义。
const headersIncludeCaseInsensitive = (headers: string[], target: string): boolean => {
  const normalized = normalizeForCaseInsensitiveMatch(target);
  return headers.some((h) => normalizeForCaseInsensitiveMatch(h) === normalized);
};

// 把预审接口返回的 base64 文件内容还原成真正的 File 对象——用于"多页合并运单PDF被
// 自动拆分"之后，把拆出来的单页文件顶替浏览器里原来那份合并PDF。
const base64ToFile = (base64: string, filename: string, mime: string): File => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i += 1) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new File([byteArray], filename, { type: mime });
};

const readHeaderRowFromFile = async (file: File): Promise<string[]> => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return [];
  const sheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false }) as unknown[][];
  const headerRow = rows.find((row) => Array.isArray(row) && row.some((cell) => normalizeHeader(cell) !== '')) || [];
  return (headerRow as unknown[]).map((cell) => normalizeHeader(cell)).filter(Boolean);
};

type ParsedSheetResult = {
  headers: string[];
  rows: Array<ParsedRow>;
};
type ParsedRow = { __sourceRowNo: number; [key: string]: string | number };
type DraftFileMeta = {
  uid: string;
  name: string;
  type: string;
  lastModified: number;
  kind: 'list' | 'waybill';
};
type ImportSessionStatus = 'draft' | 'running' | 'finished' | 'abandoned';
const getImportDraftStorageKey = (scope: string) => `${IMPORT_CREATE_DRAFT_STORAGE_KEY_PREFIX}:${scope || 'anonymous'}`;
const getScopedFileUid = (scope: string, rawUid: string) => `${scope || 'anonymous'}::${rawUid}`;
const isScopedFileUid = (scope: string, key: string) => String(key || '').startsWith(`${scope || 'anonymous'}::`);
const logImportDraft = (phase: string, data?: Record<string, unknown>) => {
  if (!DEBUG_IMPORT_DRAFT) return;
  try {
    console.log(`[ImportDraft][${phase}]`, data || {});
  } catch {}
};

const parseSheetFromFile = async (file: File): Promise<ParsedSheetResult> => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return { headers: [], rows: [] };
  const sheet = workbook.Sheets[firstSheetName];
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' }) as unknown[][];
  const headerIndex = matrix.findIndex((row) => Array.isArray(row) && row.some((cell) => normalizeHeader(cell) !== ''));
  if (headerIndex < 0) return { headers: [], rows: [] };
  const headers = (matrix[headerIndex] || []).map((cell) => normalizeHeader(cell));
  const validHeaders = headers.filter(Boolean);
  const rows = matrix.slice(headerIndex + 1).map((row, rawIdx) => {
    const sourceRowNo = headerIndex + 2 + rawIdx;
    const obj: ParsedRow = { __sourceRowNo: sourceRowNo };
    headers.forEach((header, idx) => {
      if (!header) return;
      obj[header] = normalizeCellText((row || [])[idx]);
    });
    return obj;
  }).filter((rowObj) => {
    const values = headers.map((h) => String(rowObj[h] || '').trim());
    if (!values.some(Boolean)) return false;
    // 兼容旧模板的警告行：该行不是业务数据，直接忽略
    if (values.some((v) => /模板最多支持填写\s*1000\s*条数据/.test(v))) return false;
    return true;
  });
  return { headers: validHeaders, rows };
};

const openImportDraftDb = () => new Promise<IDBDatabase>((resolve, reject) => {
  const request = indexedDB.open(IMPORT_CREATE_DRAFT_DB_NAME, 1);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(IMPORT_CREATE_DRAFT_DB_STORE)) {
      db.createObjectStore(IMPORT_CREATE_DRAFT_DB_STORE, { keyPath: 'uid' });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const idbPutImportFiles = async (scope: string, items: Array<{ uid: string; blob: Blob; name: string; type: string; lastModified: number }>) => {
  if (items.length === 0) return;
  logImportDraft('IDB.Put.Start', { scope, count: items.length, sample_uids: items.slice(0, 5).map((it) => it.uid) });
  const db = await openImportDraftDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IMPORT_CREATE_DRAFT_DB_STORE, 'readwrite');
    const store = tx.objectStore(IMPORT_CREATE_DRAFT_DB_STORE);
    items.forEach((item) => store.put({
      ...item,
      uid: getScopedFileUid(scope, item.uid),
      raw_uid: item.uid,
      scope,
    }));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  logImportDraft('IDB.Put.Done', { scope, count: items.length });
};

const idbGetImportFiles = async (scope: string, uids: string[]) => {
  if (uids.length === 0) return new Map<string, Blob>();
  logImportDraft('IDB.Get.Start', { scope, count: uids.length, sample_uids: uids.slice(0, 8) });
  const db = await openImportDraftDb();
  const result = new Map<string, Blob>();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IMPORT_CREATE_DRAFT_DB_STORE, 'readonly');
    const store = tx.objectStore(IMPORT_CREATE_DRAFT_DB_STORE);
    uids.forEach((uid) => {
      const req = store.get(getScopedFileUid(scope, uid));
      req.onsuccess = () => {
        if (req.result?.blob) result.set(uid, req.result.blob as Blob);
      };
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  logImportDraft('IDB.Get.Done', { scope, requested: uids.length, hit: result.size });
  return result;
};

const idbDeleteImportMissing = async (scope: string, keepUids: Set<string>) => {
  logImportDraft('IDB.DeleteMissing.Start', { scope, keep_count: keepUids.size });
  const db = await openImportDraftDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IMPORT_CREATE_DRAFT_DB_STORE, 'readwrite');
    const store = tx.objectStore(IMPORT_CREATE_DRAFT_DB_STORE);
    const req = store.getAllKeys();
    req.onsuccess = () => {
      (req.result || []).forEach((key) => {
        const uid = String(key || '');
        if (!uid || !isScopedFileUid(scope, uid)) return;
        const rawUid = uid.replace(`${scope || 'anonymous'}::`, '');
        if (rawUid && !keepUids.has(rawUid)) store.delete(uid);
      });
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  logImportDraft('IDB.DeleteMissing.Done', { scope, keep_count: keepUids.size });
};

const idbClearImportFiles = async (scope: string) => {
  logImportDraft('IDB.Clear.Start', { scope });
  const db = await openImportDraftDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IMPORT_CREATE_DRAFT_DB_STORE, 'readwrite');
    const store = tx.objectStore(IMPORT_CREATE_DRAFT_DB_STORE);
    const req = store.getAllKeys();
    req.onsuccess = () => {
      (req.result || []).forEach((key) => {
        const uid = String(key || '');
        if (uid && isScopedFileUid(scope, uid)) {
          store.delete(uid);
        }
      });
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  logImportDraft('IDB.Clear.Done', { scope });
};

type MissingFieldDiagnosis = {
  field: string;
  // unmapped：既没有设置来源列映射，文件里也没有同名列；
  // profile_drift：映射配置指向的来源列，在本次上传的文件里找不到（多半是源文件表头变了）。
  reason: 'unmapped' | 'profile_drift';
  mappedColumn?: string;
};
type HeaderCheckState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'ok'; headers: string[] }
  | { status: 'missing'; headers: string[]; missingFields: string[]; missingDetails: MissingFieldDiagnosis[] }
  | { status: 'error'; reason: string };

type ValidationSummary = {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  invalidDetails: Array<{ rowIndex: number; issues: string[] }>;
};
type WaybillMatchGroup = {
  groupKey: string;
  serialNo: string;
  rowIndexes: number[];
  trackingNo: string;
  expectedFileName: string;
  autoCandidates: string[];
  matchedFileName?: string;
  status: 'matched_auto' | 'matched_manual' | 'unmatched' | 'missing_expected' | 'conflict_multi_candidates' | 'conflict_duplicate_use';
  issueText: string;
};
type ExecutionGroupRecord = {
  groupKey: string;
  serialNo: string;
  rowNos: number[];
  qty: number;
  createdCodes: string[];
  errors: string[];
};

type FieldMapping = Record<string, string>;
type WaybillArchiveEntry = { path: string; dir: boolean; size: number };

// 高级设置：字段映射的“值层”规则。全部是可选、按需生效的旁路——
// 不配置时（默认 {}），getMappedValue/runHeaderCheck 的行为和标准模板导入完全一致。
//
// “按规则映射”（mode: 'rule'）：跟 constant/auto_serial/value_map 不一样，这几种都是
// “只看当前这一行”就能算出结果的旁路；规则不是——group_by_column 需要看来源列在全部行
// 里的取值序列才能算出分组编号，属于“整列一次性预处理成一份虚拟列，再按行查表”的性质。
// RULE_TYPE_REGISTRY 是这里的规则类型注册表：以后新增规则类型，只需要在这个数组里加一条
// 元数据（含 applicableFields，即这条规则允许出现在哪些标准字段的“映射方式”下拉框里）+
// 在 computeRuleResolutions 里加一条计算分支，不需要改“映射方式”下拉框本身的渲染代码。
type GroupByColumnRuleParams = { sourceColumn: string; forwardFill: boolean };
// lookup_project：按来源列取值（如产品条码）反查这个客户名下的库存单元、找出它归属的
// 项目。唯一匹配自动采用；命中多个不同项目属于歧义，manualOverrides 存人工选定的
// 取值→项目编码对照（跟“值映射”是同一种持久化方式），预审/执行阶段优先读它。
type LookupProjectRuleParams = { sourceColumn: string; manualOverrides?: Record<string, string> };
type RuleModeConfig =
  | { mode: 'rule'; ruleType: 'group_by_column'; params: GroupByColumnRuleParams }
  | { mode: 'rule'; ruleType: 'lookup_project'; params: LookupProjectRuleParams };
type RuleTypeMeta = { id: RuleModeConfig['ruleType']; label: string; applicableFields: string[] };
const RULE_TYPE_REGISTRY: RuleTypeMeta[] = [
  {
    id: 'group_by_column',
    label: '按取值分组生成序号',
    applicableFields: ['指令序号'],
  },
  {
    id: 'lookup_project',
    label: '按库存单元反查项目',
    applicableFields: ['项目'],
  },
];
const getApplicableRuleTypes = (standardField: string) =>
  RULE_TYPE_REGISTRY.filter((r) => r.applicableFields.includes(standardField));

// 不同规则类型的参数结构不一样（分组规则多一个 forwardFill 开关），切换规则类型/首次选中
// "按规则映射"时，要按目标规则类型给出对应形状的默认参数，不能不分类型地塞同一份默认值。
const buildDefaultRuleModeConfig = (ruleType: RuleModeConfig['ruleType']): RuleModeConfig => {
  if (ruleType === 'lookup_project') {
    return { mode: 'rule', ruleType: 'lookup_project', params: { sourceColumn: '' } };
  }
  return { mode: 'rule', ruleType: 'group_by_column', params: { sourceColumn: '', forwardFill: false } };
};

type RuleResolutionResult = {
  valueByRowNo: Map<number, string>;
  // 同一个取值在文件里没有连续出现（中间被别的取值打断过），不算错误，但预审阶段要
  // 显式提示出来，避免使用者没注意到这几行被合并成了同一个指令。
  nonContiguousWarnings: Array<{ value: string; rowNos: number[] }>;
};

// group_by_column 规则的核心算法：按来源列在文件里出现的先后顺序，第一次见到某个取值就
// 分配一个新的递增编号，再次见到就复用——forwardFill 开启时，空值先沿用上一个非空值再参与
// 分组。前端这份用于预审实时展示；后端 tasks.py 里有语义完全一致的 Python 实现用于真正执行，
// 两边改动需要同步，并用同一组测试数据核对结果一致，避免出现“预审显示对、执行结果不对”。
const computeGroupByColumnRule = (
  rows: ParsedRow[],
  sourceColumn: string,
  forwardFill: boolean,
): RuleResolutionResult => {
  const valueByRowNo = new Map<number, string>();
  const groupIdByValue = new Map<string, number>();
  const rowNosByValue = new Map<string, number[]>();
  let nextGroupId = 1;
  let lastNonEmptyValue = '';
  let lastAssignedValue: string | null = null;

  if (sourceColumn) {
    rows.forEach((row) => {
      const rowNo = Number(row.__sourceRowNo || 0);
      let rawVal = normalizeCellText(getRowValueByHeaderName(row, sourceColumn));
      if (!rawVal && forwardFill) {
        rawVal = lastNonEmptyValue;
      }
      if (rawVal) lastNonEmptyValue = rawVal;

      if (!rawVal) {
        valueByRowNo.set(rowNo, '');
        lastAssignedValue = null;
        return;
      }

      if (!groupIdByValue.has(rawVal)) {
        groupIdByValue.set(rawVal, nextGroupId);
        nextGroupId += 1;
        rowNosByValue.set(rawVal, [rowNo]);
      } else {
        rowNosByValue.get(rawVal)!.push(rowNo);
      }
      valueByRowNo.set(rowNo, String(groupIdByValue.get(rawVal)));
      lastAssignedValue = rawVal;
    });
  }

  const nonContiguousWarnings: Array<{ value: string; rowNos: number[] }> = [];
  rowNosByValue.forEach((rowNos, val) => {
    if (rowNos.length <= 1) return;
    const isContiguous = (rowNos[rowNos.length - 1] - rowNos[0] + 1) === rowNos.length;
    if (!isContiguous) {
      nonContiguousWarnings.push({ value: val, rowNos: [...rowNos] });
    }
  });

  return { valueByRowNo, nonContiguousWarnings };
};

type FieldModeConfig =
  | { mode: 'constant'; value: string }
  | { mode: 'auto_serial' }
  | { mode: 'value_map'; valueMap: Record<string, string> }
  | RuleModeConfig;
type TransformType = 'strip_currency' | 'trim' | 'digits_only' | 'uppercase';
type ExplodeRule = {
  sourceColumn: string;
  itemDelimiter: string;
  segmentDelimiter?: string;
  segmentFields: string[];
};
type MappingTransformRules = {
  field_modes?: Record<string, FieldModeConfig>;
  field_transforms?: Record<string, TransformType>;
  explode?: ExplodeRule | null;
};

// 下面两个纯函数是组件内 ruleResolutions / lookupProjectFieldSourceValues 这两个 useMemo
// 的算法本体，抽成组件外的纯函数，是为了能在 handleRunValidation 里对“这一次刚解析出来的
// 行数据”当场重新算一遍、拿到新鲜结果——而不是依赖这两个 useMemo 算出来存在 state 里的值
// （它们只有等组件重新渲染一次之后才会用上新数据，在同一次事件处理函数里读会读到上一次
// 渲染时的旧结果，见 handleRunValidation 里的用法说明）。两处（渲染路径的 useMemo、这里
// 的即时计算）共用同一份算法，不会出现两份实现分叉导致结果不一致的风险。
const computeRuleResolutionsFrom = (
  rows: ParsedRow[],
  rules: MappingTransformRules,
): Record<string, RuleResolutionResult> => {
  const result: Record<string, RuleResolutionResult> = {};
  const fieldModes = rules.field_modes || {};
  Object.keys(fieldModes).forEach((field) => {
    const cfg = fieldModes[field];
    if (!cfg || cfg.mode !== 'rule') return;
    if (cfg.ruleType === 'group_by_column') {
      result[field] = computeGroupByColumnRule(
        rows,
        cfg.params?.sourceColumn || '',
        !!cfg.params?.forwardFill,
      );
    }
  });
  return result;
};

const computeLookupProjectSourceValuesFrom = (
  rows: ParsedRow[],
  rules: MappingTransformRules,
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  const fieldModes = rules.field_modes || {};
  Object.keys(fieldModes).forEach((field) => {
    const cfg = fieldModes[field];
    if (!cfg || cfg.mode !== 'rule' || cfg.ruleType !== 'lookup_project') return;
    const col = cfg.params?.sourceColumn;
    if (!col) { result[field] = []; return; }
    const values = new Set<string>();
    rows.forEach((row) => {
      const v = normalizeCellText(getRowValueByHeaderName(row, col));
      if (v) values.add(v);
    });
    result[field] = Array.from(values);
  });
  return result;
};

type GetMappedValueOverrides = {
  ruleResolutions?: Record<string, RuleResolutionResult>;
  projectLookupResults?: Record<string, ResolveProjectByTagResult>;
};

const InstructionImportCreatePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState<ScenarioKey>(getScenarioFromSearch(location.search));
  const [selectedFileName, setSelectedFileName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [waybillBundleFiles, setWaybillBundleFiles] = useState<File[]>([]);
  const [waybillBundleFileList, setWaybillBundleFileList] = useState<UploadFile[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [mappingDrawerOpen, setMappingDrawerOpen] = useState(false);
  const [mappingDrawerView, setMappingDrawerView] = useState<'list' | 'editor'>('list');
  // 编辑器视图顶部那段“来源列/值映射/固定值/...”说明文字很长，默认收起，避免每次打开
  // 抽屉都要先划过一大段文字才能看到实际配置表格；需要时点开看一眼即可。
  const [mappingHelpExpanded, setMappingHelpExpanded] = useState(false);
  // 管理员代客户新建/编辑映射配置（阶段一）：跟 Create.tsx 手工创建指令页面用的是
  // 同一套判断方式——按当前页面路径是不是管理员入口，不是查用户角色字段（角色校验
  // 交给后端 resolve_import_tenant_id 做，前端这里只是决定要不要显示"客户"选择器）。
  // 客户账号自己打开这个页面，isAdminContainer 恒为 false，完全不受这次改动影响。
  const isAdminContainer = useMemo(() => {
    try { return String((window as any)?.location?.pathname || '').startsWith('/dashboard/admin'); } catch { return false; }
  }, []);
  const [adminActingCustomerId, setAdminActingCustomerId] = useState<number | undefined>(undefined);
  const [adminCustomerOptions, setAdminCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [adminCustomerLoading, setAdminCustomerLoading] = useState(false);
  const ensureAdminCustomers = async () => {
    if (!isAdminContainer) return;
    if (adminCustomerOptions.length > 0) return;
    setAdminCustomerLoading(true);
    try {
      const list = await getCustomerList({ onlyMain: true });
      const opts = (list || []).map((c: any) => {
        const code = c.customer_code || '';
        const name = c.name || c.username || '';
        const label = code ? `${name} · ${code}` : name;
        return { value: Number(c.id), label };
      });
      setAdminCustomerOptions(opts);
    } catch {
      setAdminCustomerOptions([]);
    } finally {
      setAdminCustomerLoading(false);
    }
  };
  // 管理员打开页面时就把客户列表拉好，不用等用户点开下拉框才现拉——这是页面加载后
  // 第一件要看到结果的事。
  useEffect(() => {
    if (!isAdminContainer) return;
    void ensureAdminCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminContainer]);
  const [saveNameModalOpen, setSaveNameModalOpen] = useState(false);
  const [saveNameModalMode, setSaveNameModalMode] = useState<'save' | 'saveAs'>('save');
  // 从"字段映射配置"抽屉点"应用到当前上传"后置为 true：下面那个跟着 fieldMapping/
  // transformRules 变化重新校验的 effect 看到这个标记为 true，这一次就要把校验结果
  // 用弹窗show出来（而不是像编辑器里手动改字段那样只静默更新 headerCheck）。
  const [pendingApplyModalCheck, setPendingApplyModalCheck] = useState(false);
  // “反查项目”规则的批量查询结果，按来源列的原始取值（不是行号）做 key——同一个取值
  // 在多行出现时只查一次、共用一份结果。
  const [projectLookupResults, setProjectLookupResults] = useState<Record<string, ResolveProjectByTagResult>>({});
  const [projectLookupLoading, setProjectLookupLoading] = useState(false);
  const [projectLookupModalField, setProjectLookupModalField] = useState<string | null>(null);
  const [projectLookupProjectOptions, setProjectLookupProjectOptions] = useState<string[]>([]);
  // “固定值”映射方式下，仓库/项目/承运商这几个字段的输入框允许直接从候选列表里选——跟
  // “值映射”弹窗、“设置项目归属”弹窗用的是同一份数据源（buildTemplateDropdownOptions）。
  // 在“字段映射设置”抽屉打开时拉一次，抽屉里这几个字段共用；拉取失败就是候选为空，
  // 退化成只能手动输入，不影响原有的“固定值”功能。
  const [constantDropdownOptions, setConstantDropdownOptions] = useState<TemplateDropdownOptions | null>(null);
  const [requiredExpanded, setRequiredExpanded] = useState(true);
  const [optionalExpanded, setOptionalExpanded] = useState(false);
  const [headerCheck, setHeaderCheck] = useState<HeaderCheckState>({ status: 'idle' });
  const [headerMismatchModalOpen, setHeaderMismatchModalOpen] = useState(false);
  const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
  const [parsedRows, setParsedRows] = useState<Array<ParsedRow>>([]);
  const [validationSummary, setValidationSummary] = useState<ValidationSummary | null>(null);
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({});
  const [transformRules, setTransformRules] = useState<MappingTransformRules>({});
  const [valueMapModalField, setValueMapModalField] = useState<string | null>(null);
  const [valueMapDistinctValues, setValueMapDistinctValues] = useState<string[]>([]);
  // 已经配置过对照、但这次上传的文件里没有出现的取值——弹窗里仍然展示这些行（对照
  // 依旧生效），只是标注一下“未出现在本次文件中”，避免用户误以为这条配置丢失了。
  const [valueMapKeysNotInFile, setValueMapKeysNotInFile] = useState<Set<string>>(new Set());
  const [valueMapCanonicalOptions, setValueMapCanonicalOptions] = useState<string[]>([]);
  const [newValueMapKeyInput, setNewValueMapKeyInput] = useState('');
  const [newValueMapTargetInput, setNewValueMapTargetInput] = useState<string | undefined>(undefined);
  const [profiles, setProfiles] = useState<InstructionImportMappingProfile[]>([]);
  const [jobs, setJobs] = useState<InstructionImportJob[]>([]);
  const [jobItems, setJobItems] = useState<InstructionImportJobItem[]>([]);
  const [activeJobId, setActiveJobId] = useState<number>();
  const [sessionJobId, setSessionJobId] = useState<number>();
  const [activeProfileId, setActiveProfileId] = useState<number>();
  const [profileNameInput, setProfileNameInput] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [submittingJob, setSubmittingJob] = useState(false);
  const [executingJob, setExecutingJob] = useState(false);
  const [executeResultError, setExecuteResultError] = useState('');
  const [executionListView, setExecutionListView] = useState<'created' | 'failed' | 'skipped' | null>(null);
  const [executionDetailTabKey, setExecutionDetailTabKey] = useState<'list' | 'stats' | 'productStats'>('list');
  const [executionDetailListPage, setExecutionDetailListPage] = useState(1);
  const [executionDetailListPageSize, setExecutionDetailListPageSize] = useState(10);
  const [productStatsPage, setProductStatsPage] = useState(1);
  const [productStatsPageSize, setProductStatsPageSize] = useState(10);
  const [cancellingJob, setCancellingJob] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [rowLimitExceeded, setRowLimitExceeded] = useState(false);
  const [hasValidated, setHasValidated] = useState(false);
  const [waybillFileFilterText, setWaybillFileFilterText] = useState('');
  const [waybillPreviewVisible, setWaybillPreviewVisible] = useState(false);
  const [waybillPreviewType, setWaybillPreviewType] = useState<'image' | 'pdf' | 'zip' | ''>('');
  const [waybillPreviewName, setWaybillPreviewName] = useState('');
  const [waybillPreviewUrl, setWaybillPreviewUrl] = useState('');
  const [waybillPreviewEntryName, setWaybillPreviewEntryName] = useState('');
  const [waybillPreviewLoading, setWaybillPreviewLoading] = useState(false);
  const [waybillPreviewError, setWaybillPreviewError] = useState('');
  const [waybillArchiveEntries, setWaybillArchiveEntries] = useState<WaybillArchiveEntry[]>([]);
  const [waybillArchiveFilterText, setWaybillArchiveFilterText] = useState('');
  const [waybillArchivePage, setWaybillArchivePage] = useState(1);
  const [waybillArchivePageSize, setWaybillArchivePageSize] = useState(20);
  const [attachmentCandidateNames, setAttachmentCandidateNames] = useState<string[]>([]);
  const [attachmentCandidatesLoading, setAttachmentCandidatesLoading] = useState(false);
  const [attachmentCandidatesWarning, setAttachmentCandidatesWarning] = useState('');
  const [manualWaybillMatches, setManualWaybillMatches] = useState<Record<string, string>>({});
  const [manualMatchSelectResetTick, setManualMatchSelectResetTick] = useState(0);
  const [precheckRunning, setPrecheckRunning] = useState(false);
  const [precheckDone, setPrecheckDone] = useState(false);
  const [precheckResult, setPrecheckResult] = useState<InstructionImportPrecheckResult | null>(null);
  const [precheckErrorText, setPrecheckErrorText] = useState('');
  const [validationDetailVisible, setValidationDetailVisible] = useState(false);
  const [validationDetailPage, setValidationDetailPage] = useState(1);
  const [validationDetailPageSize, setValidationDetailPageSize] = useState(20);
  const [validationRecordVisible, setValidationRecordVisible] = useState(false);
  const [activeValidationRowIndex, setActiveValidationRowIndex] = useState<number | null>(null);
  const [listFilePreviewVisible, setListFilePreviewVisible] = useState(false);
  const [listFilePreviewLoading, setListFilePreviewLoading] = useState(false);
  const [listFilePreviewHeaders, setListFilePreviewHeaders] = useState<string[]>([]);
  const [listFilePreviewRows, setListFilePreviewRows] = useState<ParsedRow[]>([]);
  const [listFilePreviewPage, setListFilePreviewPage] = useState(1);
  const [listFilePreviewPageSize, setListFilePreviewPageSize] = useState(20);
  const [inventoryTablePage, setInventoryTablePage] = useState(1);
  const [inventoryTablePageSize, setInventoryTablePageSize] = useState(8);
  const [waybillMatchTablePage, setWaybillMatchTablePage] = useState(1);
  const [waybillMatchTablePageSize, setWaybillMatchTablePageSize] = useState(10);
  const [inventoryStatsExpanded, setInventoryStatsExpanded] = useState(false);
  const [waybillMatchListExpanded, setWaybillMatchListExpanded] = useState(false);
  const waybillZipRef = useRef<any>(null);
  const hasRestoredDraftRef = useRef(false);
  // 预审接口检测到“整份附件包只有一份多页合并PDF”时，会自动按页拆分并用拆分结果替换
  // waybillBundleFiles（见 handleRunPrecheck 里 generated_waybill_files 那段）——这次替换是
  // 系统自己做的，不是用户重新上传了附件包，但下面那个“附件变了就让预审结果失效”的 effect
  // 只看 waybillBundleFiles.length 变没变，分不清这两种情况，会把刚刚才成功设置的
  // precheckResult 立刻清空。用这个标记告诉那个 effect“这一次文件数量变化不算数”，跳过一次
  // 清空；下一次真正的“文件数量变化”（无论是不是由用户触发）会消费掉标记后恢复正常行为。
  const skipNextWaybillInvalidateRef = useRef(false);
  const pendingExecutionUiRestoreRef = useRef<null | {
    tabKey: 'list' | 'stats' | 'productStats';
    listPage: number;
    listPageSize: number;
    productStatsPage: number;
    productStatsPageSize: number;
  }>(null);
  const [draftRestoreReady, setDraftRestoreReady] = useState(false);
  const [sessionScope, setSessionScope] = useState('anonymous');
  const [sessionScopeReady, setSessionScopeReady] = useState(false);

  const clearImportSession = async () => {
    setCurrentStep(0);
    setSelectedFileName('');
    setUploadedFile(null);
    setFileList([]);
    setWaybillBundleFiles([]);
    setWaybillBundleFileList([]);
    setHeaderCheck({ status: 'idle' });
    setParsedHeaders([]);
    setParsedRows([]);
    setValidationSummary(null);
    setFieldMapping({});
    setTransformRules({});
    setActiveProfileId(undefined);
    setRowLimitExceeded(false);
    setHasValidated(false);
    setManualWaybillMatches({});
    setPrecheckRunning(false);
    setPrecheckDone(false);
    setPrecheckResult(null);
    setPrecheckErrorText('');
    setExecuteResultError('');
    setExecutionListView(null);
    setJobItems([]);
    setActiveJobId(undefined);
    setSessionJobId(undefined);
    try {
      localStorage.removeItem(getImportDraftStorageKey(sessionScope));
    } catch {}
    try {
      await idbClearImportFiles(sessionScope);
    } catch {}
  };

  const scenarioProfiles = useMemo(
    () => profiles.filter((p) => p.scenario === scenario).sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || ''))),
    [profiles, scenario],
  );
  const activeProfileName = useMemo(
    () => scenarioProfiles.find((p) => p.id === activeProfileId)?.name || '',
    [scenarioProfiles, activeProfileId],
  );
  const allScenarioFields = useMemo(
    // 注意：这里必须用 SCENARIO_DISPLAY_REQUIRED_FIELDS / getScenarioDisplayOptionalFields
    // （和模板生成、runHeaderCheck、buildValidationSummary、后端 FIELD_ALIASES 同一套字段名，
    // 如“项目”“仓库”“库存单元标识”），不能用 scenarioMeta.requiredFields/optionalFields——
    // 那是另一套已经不再使用的旧字段名（“项目编码”“仓库编码”“库存单位编码”等），
    // 用它配置出来的映射永远不会被 getMappedValue 实际读取到，等于配置了也不生效。
    () => [...SCENARIO_DISPLAY_REQUIRED_FIELDS[scenario], ...getScenarioDisplayOptionalFields(scenario)],
    [scenario],
  );
  const displayRequiredFields = useMemo(
    () => SCENARIO_DISPLAY_REQUIRED_FIELDS[scenario],
    [scenario],
  );
  const displayOptionalFields = useMemo(
    () => getScenarioDisplayOptionalFields(scenario),
    [scenario],
  );
  const displayScenarioUsageText = useMemo(
    () => SCENARIO_DISPLAY_USAGE_TEXT[scenario],
    [scenario],
  );
  const normalizedWaybillFilter = useMemo(
    () => String(waybillFileFilterText || '').trim().toLowerCase(),
    [waybillFileFilterText],
  );
  const filteredWaybillFileList = useMemo(() => {
    if (!normalizedWaybillFilter) return waybillBundleFileList;
    return waybillBundleFileList.filter((file) => {
      const fileName = String(file.name || '').trim().toLowerCase();
      return fileName.includes(normalizedWaybillFilter);
    });
  }, [waybillBundleFileList, normalizedWaybillFilter]);
  const normalizedWaybillArchiveFilter = useMemo(
    () => String(waybillArchiveFilterText || '').trim().toLowerCase(),
    [waybillArchiveFilterText],
  );
  const filteredWaybillArchiveEntries = useMemo(() => {
    if (!normalizedWaybillArchiveFilter) return waybillArchiveEntries;
    return waybillArchiveEntries.filter((entry) => {
      const normalized = String(entry.path || '').replace(/\\/g, '/');
      const seg = normalized.split('/').filter(Boolean);
      const fileName = (seg.length > 0 ? seg[seg.length - 1] : normalized).trim().toLowerCase();
      return fileName.includes(normalizedWaybillArchiveFilter);
    });
  }, [waybillArchiveEntries, normalizedWaybillArchiveFilter]);

  // 规则模式需要“看完来源列在全部行里的取值”才能算出每一行的结果，跟其它几种模式
  // “只看当前这一行”的性质不同，所以单独预计算成一份 rowNo -> value 的表，供
  // getMappedValue 按行查表。只对配置了 mode: 'rule' 的字段计算，不影响其它字段。
  // 必须放在 waybillMatchGroups 之前：下面 waybillMatchGroups 的 useMemo 会调用
  // getMappedValue，而 getMappedValue 内部读取 ruleResolutions，放在后面会导致
  // “Cannot access 'ruleResolutions' before initialization”。
  const ruleResolutions = useMemo(
    () => computeRuleResolutionsFrom(parsedRows, transformRules),
    [parsedRows, transformRules],
  );

  // 汇总所有规则字段产生的"取值不连续"提醒，用于预审1面板展示——不阻断流程，只是让
  // 使用者知道这些行被合并成了同一个指令，可以自行确认是否符合预期。
  const ruleNonContiguousWarnings = useMemo(() => {
    const list: Array<{ field: string; value: string; rowNos: number[] }> = [];
    Object.keys(ruleResolutions).forEach((field) => {
      (ruleResolutions[field].nonContiguousWarnings || []).forEach((w) => {
        list.push({ field, value: w.value, rowNos: w.rowNos });
      });
    });
    return list;
  }, [ruleResolutions]);

  const waybillMatchGroups = useMemo<WaybillMatchGroup[]>(() => {
    if (scenario !== 'upload_immediate' || !precheckDone || !precheckResult) return [];
    const sourceGroups = Array.isArray(precheckResult.match_groups) ? precheckResult.match_groups : [];
    const serialTrackingMap = new Map<string, string>();
    const rowTrackingMap = new Map<number, string>();
    (parsedRows || []).forEach((row, idx) => {
      const rowNo = Number(row.__sourceRowNo || idx + 2);
      const serialNo = String(getMappedValue(row, '指令序号', fieldMapping) || '').trim();
      const trackingNo = String(getMappedValue(row, '物流跟踪号', fieldMapping) || '').trim();
      if (trackingNo) {
        rowTrackingMap.set(rowNo, trackingNo);
      }
      if (serialNo && trackingNo && !serialTrackingMap.has(serialNo)) {
        serialTrackingMap.set(serialNo, trackingNo);
      }
    });
    const groups = sourceGroups.map((g, idx) => {
      const serialNo = String(g.serial_no || '-').trim() || '-';
      const rowIndexes = Array.isArray(g.row_nos) ? g.row_nos.map((n) => Number(n || 0)).filter((n) => n > 0) : [];
      const groupKey = serialNo !== '-' ? `serial-${serialNo}` : `rows-${rowIndexes.join('_') || idx}`;
      const manualPickedRaw = String(manualWaybillMatches[groupKey] || '').trim();
      const manualCleared = manualPickedRaw === MANUAL_WAYBILL_UNMATCHED_SENTINEL;
      const manualPicked = manualCleared ? '' : manualPickedRaw;
      const matchedByPrecheck = String(g.matched_file_name || '').trim();
      const backendStatus = String(g.status || '').trim();
      const firstRowTracking = rowIndexes.length > 0 ? String(rowTrackingMap.get(rowIndexes[0]) || '').trim() : '';
      const trackingNo = serialNo !== '-' ? String(serialTrackingMap.get(serialNo) || firstRowTracking) : firstRowTracking;
      const status: WaybillMatchGroup['status'] = manualPicked
        ? 'matched_manual'
        : (manualCleared ? 'unmatched' : (backendStatus === 'matched_auto' ? 'matched_auto' : 'unmatched'));
      return {
        groupKey,
        serialNo,
        rowIndexes,
        trackingNo,
        expectedFileName: String(g.expected_file_name || '').trim(),
        autoCandidates: [],
        matchedFileName: manualPicked || (manualCleared ? '' : matchedByPrecheck) || undefined,
        status,
        issueText: String(g.issue_text || g.match_reason || '').trim(),
      };
    });
    // 兜底保证“同一个附件文件仅可被一个序号占用”：
    // 若发生重复占用，优先保留“人工匹配”记录，其余记录回退为未匹配。
    const ownership = new Map<string, string>();
    const sorted = [...groups].sort((a, b) => {
      const pa = a.status === 'matched_manual' ? 0 : 1;
      const pb = b.status === 'matched_manual' ? 0 : 1;
      return pa - pb;
    });
    sorted.forEach((g) => {
      const fileName = String(g.matchedFileName || '').trim();
      if (!fileName) return;
      const key = fileName.toLowerCase();
      if (!ownership.has(key)) {
        ownership.set(key, g.groupKey);
        return;
      }
      if (ownership.get(key) !== g.groupKey) {
        g.matchedFileName = undefined;
        g.status = 'unmatched';
        g.issueText = '附件已被其他指令序号占用，请重新人工匹配。';
      }
    });
    return groups;
  }, [scenario, precheckDone, precheckResult, manualWaybillMatches, parsedRows, fieldMapping]);
  const waybillMatchPendingCount = useMemo(
    () => waybillMatchGroups.filter((g) => !['matched_auto', 'matched_manual'].includes(g.status)).length,
    [waybillMatchGroups],
  );
  const waybillMatchTotalCount = useMemo(() => waybillMatchGroups.length, [waybillMatchGroups]);
  const waybillMatchCompletedCount = useMemo(
    () => waybillMatchGroups.filter((g) => ['matched_auto', 'matched_manual'].includes(g.status)).length,
    [waybillMatchGroups],
  );
  useEffect(() => {
    if (scenario !== 'upload_immediate' || !precheckDone) return;
    if (waybillMatchTotalCount <= 0) {
      setWaybillMatchListExpanded(false);
      return;
    }
    // 默认行为：存在未匹配时展开；全部匹配成功时收起。
    setWaybillMatchListExpanded(waybillMatchPendingCount > 0);
  }, [scenario, precheckDone, waybillMatchTotalCount, waybillMatchPendingCount]);
  const waybillMatchMeta = useMemo(() => {
    const serialFileMap: Record<string, string> = {};
    waybillMatchGroups.forEach((g) => {
      if (g.matchedFileName && g.serialNo && g.serialNo !== '-') {
        serialFileMap[g.serialNo] = g.matchedFileName;
      }
    });
    return serialFileMap;
  }, [waybillMatchGroups]);
  const occupiedAttachmentByFile = useMemo(() => {
    const map = new Map<string, { groupKey: string; serialNo: string }>();
    waybillMatchGroups.forEach((g) => {
      const name = String(g.matchedFileName || '').trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { groupKey: g.groupKey, serialNo: g.serialNo });
      }
    });
    return map;
  }, [waybillMatchGroups]);
  const handleManualWaybillMatchChange = (group: WaybillMatchGroup, value?: string) => {
    const selected = String(value || '').trim();
    const currentGroupKey = String(group.groupKey || '');
    if (!selected) {
      setManualWaybillMatches((prev) => {
        const next = { ...prev };
        delete next[currentGroupKey];
        return next;
      });
      return;
    }
    const occupied = occupiedAttachmentByFile.get(selected.toLowerCase());
    if (occupied && occupied.groupKey && occupied.groupKey !== currentGroupKey) {
      const occupiedGroup = waybillMatchGroups.find((it) => it.groupKey === occupied.groupKey);
      const occupiedSerial = String(occupiedGroup?.serialNo || occupied.serialNo || '-').trim() || '-';
      const targetSerial = String(group.serialNo || '-').trim() || '-';
      Modal.confirm({
        title: '附件已被占用，是否更换匹配关系？',
        content: `附件“${selected}”当前已匹配到指令序号 ${occupiedSerial}。确认后将改为匹配到指令序号 ${targetSerial}，并解除旧匹配关系。`,
        okText: '确认',
        cancelText: '取消',
        onOk: () => {
          setManualWaybillMatches((prev) => {
            const next = { ...prev };
            next[currentGroupKey] = selected;
            // 被换绑方始终写入“强制未匹配”标记，防止回落到后端自动匹配结果。
            next[occupied.groupKey] = MANUAL_WAYBILL_UNMATCHED_SENTINEL;
            return next;
          });
        },
      });
      return;
    }
    setManualWaybillMatches((prev) => ({ ...prev, [currentGroupKey]: selected }));
  };
  const loadProfiles = async (scenarioKey: ScenarioKey) => {
    try {
      const list = await listInstructionImportMappingProfiles({
        source_type: 'excel_csv',
        scenario: scenarioKey,
        acting_as_customer_id: isAdminContainer ? adminActingCustomerId : undefined,
      });
      setProfiles((prev) => {
        const others = prev.filter((p) => p.scenario !== scenarioKey);
        return [...others, ...list];
      });
    } catch {
      message.warning('映射配置后端暂不可用，仍可继续使用页面手动映射。');
      setProfiles((prev) => prev.filter((p) => p.scenario !== scenarioKey));
    }
  };

  const loadJobs = async (scenarioKey: ScenarioKey) => {
    try {
      const list = await listInstructionImportJobs({
        source_type: 'excel_csv',
        scenario: scenarioKey,
        acting_as_customer_id: isAdminContainer ? adminActingCustomerId : undefined,
      });
      logImportDraft('Jobs.Loaded', {
        scenario: scenarioKey,
        count: list.length,
        statuses: list.slice(0, 10).map((j) => `${j.id}:${String(j.status || '')}`),
      });
      setJobs(list.slice(0, 20));
      if (list.length > 0) {
        setActiveJobId((prev) => {
          const next = prev && list.some((j) => j.id === prev) ? prev : list[0].id;
          logImportDraft('Jobs.ActiveJob.Select', {
            scenario: scenarioKey,
            prev_active_job_id: prev,
            next_active_job_id: next,
            auto_selected_head_status: String((list[0] || {}).status || ''),
          });
          return next;
        });
      } else {
        logImportDraft('Jobs.ActiveJob.Clear', { scenario: scenarioKey });
        setActiveJobId(undefined);
      }
    } catch {
      logImportDraft('Jobs.Load.Error', { scenario: scenarioKey });
      setJobs([]);
      setActiveJobId(undefined);
    }
  };

  const loadJobItems = async (jobId: number) => {
    try {
      const items = await listInstructionImportJobItems(jobId, isAdminContainer ? adminActingCustomerId : undefined);
      setJobItems(items);
    } catch {
      setJobItems([]);
    }
  };

  function applyValueTransform(value: string, transformType: TransformType): string {
    // 和后端 tasks.py 的 _apply_value_transform 保持相同的清洗规则语义。
    const text = String(value || '');
    if (transformType === 'strip_currency') return text.replace(/[^0-9.\-]/g, '').trim();
    if (transformType === 'trim') return text.trim();
    if (transformType === 'digits_only') return text.replace(/[^0-9]/g, '');
    if (transformType === 'uppercase') return text.trim().toUpperCase();
    return text;
  }

  // 找出所有配置了“反查项目”规则的字段，及其来源列在当前文件里出现过的全部不同取值——
  // 用于触发批量查询、以及给“设置项目归属”弹窗展示完整取值列表（不只是有歧义的那些）。
  const lookupProjectFieldSourceValues = useMemo(
    () => computeLookupProjectSourceValuesFrom(parsedRows, transformRules),
    [parsedRows, transformRules],
  );

  useEffect(() => {
    const allValues = new Set<string>();
    Object.values(lookupProjectFieldSourceValues).forEach((values) => {
      values.forEach((v) => allValues.add(v));
    });
    if (allValues.size === 0) return;
    let cancelled = false;
    setProjectLookupLoading(true);
    resolveProjectByTag(Array.from(allValues), isAdminContainer ? adminActingCustomerId : undefined)
      .then((results) => {
        if (cancelled) return;
        setProjectLookupResults((prev) => {
          const next = { ...prev };
          results.forEach((r) => { next[r.tag] = r; });
          return next;
        });
      })
      .catch(() => {
        // 静默失败：反查失败时这个字段就是解析不出结果，会在校验阶段自然报
        // “缺少必填字段：项目”，不需要额外弹窗打断当前操作。
      })
      .finally(() => {
        if (!cancelled) setProjectLookupLoading(false);
      });
    return () => { cancelled = true; };
  }, [lookupProjectFieldSourceValues]);

  function getMappedValue(
    row: ParsedRow,
    standardField: string,
    mapping: FieldMapping,
    overrides?: GetMappedValueOverrides,
  ) {
    // transformRules 为 {}（标准模板导入、未配置高级映射）时，下面的 modeCfg/transformType
    // 都会是 undefined，函数退化成和原来完全一致的“按映射表取源列原始值”逻辑——
    // 这是保证不破坏现有标准模板导入功能的核心不变式。
    // overrides 不传时（现在绝大多数渲染路径的调用都是这样）沿用组件 state 里的
    // ruleResolutions/projectLookupResults——跟原来行为完全一样。只有 handleRunValidation
    // 里“这一次刚解析出新文件、state 还没来得及更新”的场景，才会显式传入当场算好的新鲜值，
    // 避免读到上一次渲染的过期结果（这两份数据依赖 parsedRows，而 parsedRows 正是这次刚被
    // setParsedRows 更新、还没触发重渲染的那个 state）。
    const effectiveRuleResolutions = overrides?.ruleResolutions ?? ruleResolutions;
    const effectiveProjectLookupResults = overrides?.projectLookupResults ?? projectLookupResults;
    const modeCfg = (transformRules.field_modes || {})[standardField];
    const pickRaw = () => {
      const mappedHeader = String(mapping[standardField] || standardField);
      return normalizeCellText(getRowValueByHeaderName(row, mappedHeader));
    };
    let resolved: string;
    if (!modeCfg) {
      resolved = pickRaw();
    } else if (modeCfg.mode === 'constant') {
      resolved = String(modeCfg.value || '').trim();
    } else if (modeCfg.mode === 'auto_serial') {
      resolved = String(row.__sourceRowNo ?? '').trim();
    } else if (modeCfg.mode === 'value_map') {
      const rawVal = pickRaw();
      const mapped = lookupValueMapEntry(modeCfg.valueMap, rawVal);
      resolved = mapped !== undefined ? mapped : rawVal;
    } else if (modeCfg.mode === 'rule' && modeCfg.ruleType === 'group_by_column') {
      const rowNo = Number(row.__sourceRowNo ?? -1);
      resolved = effectiveRuleResolutions[standardField]?.valueByRowNo.get(rowNo) || '';
    } else if (modeCfg.mode === 'rule' && modeCfg.ruleType === 'lookup_project') {
      const srcCol = modeCfg.params?.sourceColumn || '';
      const rawVal = srcCol ? normalizeCellText(getRowValueByHeaderName(row, srcCol)) : '';
      const manualOverride = rawVal ? modeCfg.params?.manualOverrides?.[rawVal] : undefined;
      if (manualOverride) {
        resolved = manualOverride;
      } else {
        const lookupResult = rawVal ? effectiveProjectLookupResults[rawVal] : undefined;
        resolved = (lookupResult && lookupResult.status === 'resolved') ? lookupResult.project_code : '';
      }
    } else {
      resolved = pickRaw();
    }
    // 固定值/自动生成/规则算出来的已经是结构化的确定值（如常量、行号、分组序号），
    // 不应该再套字符串清洗规则——配置界面上也不给这几种模式展示“数值清洗”这一列，
    // 这里是防御性地再保证一次。
    const modeSkipsTransform = !!modeCfg && ['rule', 'constant', 'auto_serial'].includes(modeCfg.mode);
    const transformType = modeSkipsTransform ? undefined : (transformRules.field_transforms || {})[standardField];
    if (transformType && resolved) {
      resolved = applyValueTransform(resolved, transformType);
    }
    return resolved;
  }

  const buildValidationSummary = (
    rows: Array<ParsedRow>,
    scenarioKey: ScenarioKey,
    mapping: FieldMapping,
    overrides?: GetMappedValueOverrides,
  ): ValidationSummary => {
    // “物流运单附件文件名”始终是可选的辅助字段：填了走文件名快速匹配，没填（整列没有、
    // 整列留空、或部分行留空）就交给运单匹配级联（文件名→PDF文本→条码→OCR→姓名地址）
    // 去处理，匹配不上的在“运单附件匹配”面板里人工处理，不作为必填校验拦截。
    const required = getScenarioValidationRequiredFields(scenarioKey);
    const invalidDetails: Array<{ rowIndex: number; issues: string[] }> = [];
    const rowIssuesMap = new Map<number, string[]>();
    const addIssue = (rowIndex: number, issue: string) => {
      const cur = rowIssuesMap.get(rowIndex) || [];
      cur.push(issue);
      rowIssuesMap.set(rowIndex, cur);
    };
    let invalidRows = 0;

    // 先按指令序号分组：单行组视为主行，多行组第一行为主行，其他为副行。
    const serialGroups = new Map<string, Array<{ row: ParsedRow; rowIndex: number; serialNo: string; serialValid: boolean }>>();
    rows.forEach((row, idx) => {
      const serialNo = getMappedValue(row, '指令序号', mapping, overrides);
      const serialValid = /^\d+$/.test(serialNo);
      const sourceRowNo = Number(row.__sourceRowNo || idx + 2);
      const groupKey = serialValid ? serialNo : `__row_${sourceRowNo}`;
      const arr = serialGroups.get(groupKey) || [];
      arr.push({ row, rowIndex: sourceRowNo, serialNo, serialValid });
      serialGroups.set(groupKey, arr);
    });

    const detailRequiredFields = ['项目', '库存单元标识', '发货数量'];
    const scenarioFields = [
      '指令序号',
      ...(SCENARIO_DISPLAY_REQUIRED_FIELDS[scenarioKey] || []),
      ...getScenarioDisplayOptionalFields(scenarioKey),
    ];
    const uniqueScenarioFields = Array.from(new Set(scenarioFields));
    const consistencyFields = uniqueScenarioFields.filter(
      (field) => field !== '指令序号' && !detailRequiredFields.includes(field),
    );
    serialGroups.forEach((groupRows) => {
      if (groupRows.length <= 0) return;
      const [first, ...others] = groupRows;

      // 主行：执行场景完整必填校验
      const mainMissing = required.filter((field) => getMappedValue(first.row, field, mapping, overrides) === '');
      if (mainMissing.length > 0) {
        addIssue(first.rowIndex, `缺少必填字段：${mainMissing.join('、')}`);
      }
      if (first.serialNo && !first.serialValid) {
        addIssue(first.rowIndex, '指令序号必须为纯数字');
      }

      // 副行：仅“项目/库存单元标识/发货数量”必填；其他字段留空或与主行一致
      others.forEach(({ row, rowIndex, serialNo, serialValid }) => {
        if (serialNo && !serialValid) {
          addIssue(rowIndex, '指令序号必须为纯数字');
        }
        detailRequiredFields.forEach((field) => {
          const val = getMappedValue(row, field, mapping, overrides);
          if (!val) {
            addIssue(rowIndex, `同一指令序号下的明细行必须填写「${field}」`);
          }
        });
        consistencyFields.forEach((field) => {
          const firstVal = getMappedValue(first.row, field, mapping, overrides);
          const curVal = getMappedValue(row, field, mapping, overrides);
          if (curVal && curVal !== firstVal) {
            addIssue(rowIndex, `字段「${field}」需与该序号首行一致或留空`);
          }
        });
      });
    });

    const sortedInvalidRows = Array.from(rowIssuesMap.entries()).sort((a, b) => a[0] - b[0]);
    invalidRows = sortedInvalidRows.length;
    sortedInvalidRows.forEach(([rowIndex, issues]) => {
      invalidDetails.push({ rowIndex, issues });
    });
    return {
      totalRows: rows.length,
      validRows: rows.length - invalidRows,
      invalidRows,
      invalidDetails,
    };
  };

  const runHeaderCheck = async (file: File, scenarioKey: ScenarioKey, options?: { silent?: boolean }) => {
    setHeaderCheck({ status: 'checking' });
    try {
      const headers = await readHeaderRowFromFile(file);
      const required = getScenarioValidationRequiredFields(scenarioKey);
      // 先套用已保存的映射表（含常量值/自动生成指令序号）再判断必填列是否满足，而不是直接拿
      // 标准字段名去比对原始表头——这是让“直接上传源文件”能真正跑通的关键一步。
      // fieldMapping/transformRules 都为空时（标准模板导入），下面的判断退化成和原来完全一致的
      // “headers.includes(field)”字面量比较，不影响现有标准模板导入的任何行为。
      const missingDetails: MissingFieldDiagnosis[] = [];
      required.forEach((field) => {
        const modeCfg = (transformRules.field_modes || {})[field];
        if (modeCfg && (modeCfg.mode === 'constant' || modeCfg.mode === 'auto_serial')) {
          return; // 常量值/自动生成的字段不依赖源文件表头，始终视为满足
        }
        if (modeCfg && modeCfg.mode === 'rule') {
          // 规则模式下，标准字段名本身不需要作为表头存在——真正需要在表头里找到的是
          // 规则参数里配置的“来源列”（分组规则/反查项目规则目前都只需要这一个参数）。
          const ruleSourceColumn = modeCfg.params?.sourceColumn || '';
          if (ruleSourceColumn && !headers.includes(ruleSourceColumn)) {
            missingDetails.push({ field, reason: 'profile_drift', mappedColumn: ruleSourceColumn });
          }
          return;
        }
        const mappedColumn = fieldMapping[field];
        if (mappedColumn) {
          if (!headersIncludeCaseInsensitive(headers, mappedColumn)) {
            missingDetails.push({ field, reason: 'profile_drift', mappedColumn });
          }
          return;
        }
        if (!headers.includes(field)) {
          missingDetails.push({ field, reason: 'unmapped' });
        }
      });
      if (missingDetails.length > 0) {
        const missing = missingDetails.map((d) => d.field);
        setHeaderCheck({ status: 'missing', headers, missingFields: missing, missingDetails });
        if (!options?.silent) {
          message.warning(`当前场景缺少必填列：${missing.join('、')}`);
        }
        return { ok: false, headers, missingFields: missing, missingDetails };
      }
      setHeaderCheck({ status: 'ok', headers });
      return { ok: true, headers };
    } catch (error: any) {
      setHeaderCheck({ status: 'error', reason: error?.message || '读取文件失败' });
      if (!options?.silent) {
        message.error('文件解析失败，请检查文件格式后重试。');
      }
      return { ok: false, headers: [] as string[], reason: error?.message || '读取文件失败' };
    }
  };

  const runAutoHeaderCheck = async (file: File, scenarioKey: ScenarioKey, options?: { showModal?: boolean }) => {
    const result = await runHeaderCheck(file, scenarioKey, { silent: true });
    if (result.ok) return result;
    if (!options?.showModal) return result;
    // 不再用一次性的 Modal.warning——改成打开一个持续展示的弹窗（headerMismatchModalOpen +
    // 下面的 <Modal> JSX），这样用户在弹窗里换一份映射配置重试时，弹窗不需要关闭重开，
    // 内容会随着 headerCheck 状态实时刷新，可以反复换着试。
    setHeaderMismatchModalOpen(true);
    return result;
  };

  // "查看错误"：不依赖重新上传文件——用当前已上传的文件重新跑一次表头校验并打开
  // "表头校验未通过"弹窗，用于弹窗被关闭之后还想再看一次错误详情，或者在“字段映射设置”
  // 抽屉里改完映射想立刻确认问题是否解决的场景。
  const handleViewHeaderError = async () => {
    if (!uploadedFile) return;
    const result = await runAutoHeaderCheck(uploadedFile, scenario, { showModal: true });
    if (result.ok) {
      message.success('表头校验已通过，无需修正。');
    }
  };

  useEffect(() => {
    // 管理员打开页面的那一刻还没选客户（adminActingCustomerId 是 undefined），这个
    // effect 的第一次执行会用这个空值去查——查回来的是空列表/管理员自己的列表，都不是
    // 想要的结果。之前这里只依赖 [scenario]，管理员选定客户后不会重新触发，导致这份
    // 列表一直是错的/空的，一直要等到手动打开"字段映射配置"抽屉（另有专门的 effect
    // 会重新拉一次）才会变对——但表头校验失败弹窗里的"选择已保存的映射配置"下拉框
    // 读的就是这份一直没刷新对的列表，所以管理员看到的永远是空的。这里把
    // adminActingCustomerId 也加进依赖数组，客户一选定就重新拉一次。
    if (ADVANCED_MAPPING_ENABLED && !(isAdminContainer && !adminActingCustomerId)) {
      loadProfiles(scenario);
    }
    loadJobs(scenario);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, adminActingCustomerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await request.get('/api/v1/auth/me/');
        const uid = Number((resp?.data as any)?.id || 0);
        const scope = uid > 0 ? `u_${uid}` : 'anonymous';
        logImportDraft('Scope.Resolve.ByMe', { uid, scope });
        if (!cancelled) setSessionScope(scope);
      } catch {
        try {
          const token = String(localStorage.getItem('token') || '');
          const scope = token ? `t_${token.slice(0, 24)}` : 'anonymous';
          logImportDraft('Scope.Resolve.ByTokenFallback', { token_prefix: token.slice(0, 12), scope });
          if (!cancelled) setSessionScope(scope);
        } catch {
          logImportDraft('Scope.Resolve.FallbackAnonymous');
          if (!cancelled) setSessionScope('anonymous');
        }
      } finally {
        if (!cancelled) setSessionScopeReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!sessionScopeReady) return;
    if (sessionScope === 'anonymous') return;
    // 账号已识别后，自动清理匿名作用域历史草稿，避免早期测试数据干扰。
    logImportDraft('Anonymous.Cleanup', { current_scope: sessionScope });
    try {
      localStorage.removeItem(getImportDraftStorageKey('anonymous'));
    } catch {}
    idbClearImportFiles('anonymous').catch(() => {});
  }, [sessionScopeReady, sessionScope]);

  useEffect(() => {
    if (!sessionScopeReady) return;
    if (hasRestoredDraftRef.current) return;
    hasRestoredDraftRef.current = true;
    logImportDraft('Restore.Bootstrap', {
      scope: sessionScope,
      storage_key: getImportDraftStorageKey(sessionScope),
    });
    let raw = '';
    try {
      raw = String(localStorage.getItem(getImportDraftStorageKey(sessionScope)) || '');
    } catch {
      raw = '';
    }
    if (!raw) {
      logImportDraft('Restore.NoDraft', { scope: sessionScope });
      setDraftRestoreReady(true);
      return;
    }
    try {
      const parsed = JSON.parse(raw) as {
        updated_at?: number;
        scenario?: ScenarioKey;
        current_step?: number;
        field_mapping?: FieldMapping;
        active_job_id?: number;
        list_file?: DraftFileMeta | null;
        waybill_files?: DraftFileMeta[];
        session_status?: ImportSessionStatus;
        execution_list_view?: 'created' | 'failed' | 'skipped' | null;
        execution_detail_tab_key?: 'list' | 'stats' | 'productStats';
        execution_detail_list_page?: number;
        execution_detail_list_page_size?: number;
        product_stats_page?: number;
        product_stats_page_size?: number;
      };
      const updatedAt = Number(parsed?.updated_at || 0);
      const isExpired = !updatedAt || (Date.now() - updatedAt > 12 * 60 * 60 * 1000);
      logImportDraft('Restore.DraftFound', {
        scope: sessionScope,
        session_status: parsed?.session_status,
        current_step: parsed?.current_step,
        active_job_id: parsed?.active_job_id,
        has_field_mapping: !!(parsed?.field_mapping && Object.keys(parsed.field_mapping).length > 0),
        list_file_uid: parsed?.list_file?.uid || '',
        waybill_file_count: Array.isArray(parsed?.waybill_files) ? parsed.waybill_files.length : 0,
        is_expired: isExpired,
      });
      if (isExpired) {
        logImportDraft('Restore.Skip.Expired', { scope: sessionScope });
        try { localStorage.removeItem(getImportDraftStorageKey(sessionScope)); } catch {}
        idbClearImportFiles(sessionScope).catch(() => {});
        setDraftRestoreReady(true);
        return;
      }
      const sessionStatus = String(parsed?.session_status || 'draft') as ImportSessionStatus;
      if (sessionStatus === 'abandoned') {
        logImportDraft('Restore.Skip.TerminalStatus', { scope: sessionScope, session_status: sessionStatus });
        try { localStorage.removeItem(getImportDraftStorageKey(sessionScope)); } catch {}
        idbClearImportFiles(sessionScope).catch(() => {});
        setDraftRestoreReady(true);
        return;
      }
      if (sessionStatus === 'finished') {
        // 已完成的第3步结果是只读历史数据，没有“继续 or 丢弃”的选择，
        // 不需要弹“检测到未完成的导入会话”确认框，直接静默恢复即可。
        const finishedJobId = Number(parsed?.active_job_id || 0);
        if (finishedJobId <= 0) {
          logImportDraft('Restore.Skip.FinishedWithoutJob', { scope: sessionScope });
          try { localStorage.removeItem(getImportDraftStorageKey(sessionScope)); } catch {}
          idbClearImportFiles(sessionScope).catch(() => {});
          setDraftRestoreReady(true);
          return;
        }
        logImportDraft('Restore.Finished.Silent', { scope: sessionScope, active_job_id: finishedJobId });
        const nextScenario = (parsed?.scenario && SCENARIO_KEYS.includes(parsed.scenario)) ? parsed.scenario : scenario;
        setScenario(nextScenario);
        const params = new URLSearchParams(location.search || '');
        params.set('scenario', nextScenario);
        navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
        if (parsed?.field_mapping && typeof parsed.field_mapping === 'object') {
          setFieldMapping(parsed.field_mapping);
        }
        const restoredView = parsed?.execution_list_view ?? null;
        if (restoredView) {
          pendingExecutionUiRestoreRef.current = {
            tabKey: parsed?.execution_detail_tab_key || 'list',
            listPage: Number(parsed?.execution_detail_list_page || 1),
            listPageSize: Number(parsed?.execution_detail_list_page_size || 10),
            productStatsPage: Number(parsed?.product_stats_page || 1),
            productStatsPageSize: Number(parsed?.product_stats_page_size || 10),
          };
        }
        setSessionJobId(finishedJobId);
        setActiveJobId(finishedJobId);
        setCurrentStep(2);
        setExecutionListView(restoredView);
        setDraftRestoreReady(true);
        return;
      }
      const hasDraft = !!(parsed?.field_mapping && Object.keys(parsed.field_mapping).length > 0) || Number(parsed?.current_step || 0) > 0;
      const hasFileDraft = !!parsed?.list_file || ((parsed?.waybill_files || []).length > 0);
      if (!hasDraft && !hasFileDraft) {
        logImportDraft('Restore.Skip.EmptyDraft', { scope: sessionScope });
        setDraftRestoreReady(true);
        return;
      }
      logImportDraft('Restore.Prompt.Show', { scope: sessionScope, hasDraft, hasFileDraft });
      Modal.confirm({
        title: '检测到未完成的导入会话',
        content: '是否恢复上次未完成的导入进度？',
        okText: '恢复',
        cancelText: '丢弃',
        onOk: async () => {
          logImportDraft('Restore.Prompt.Confirm', { scope: sessionScope });
          const nextScenario = (parsed?.scenario && SCENARIO_KEYS.includes(parsed.scenario)) ? parsed.scenario : scenario;
          setScenario(nextScenario);
          const params = new URLSearchParams(location.search || '');
          params.set('scenario', nextScenario);
          navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
          const listMeta = parsed?.list_file || null;
          const waybillMetas = Array.isArray(parsed?.waybill_files) ? parsed.waybill_files : [];
          const restoreUids = [
            ...(listMeta ? [String(listMeta.uid || '')] : []),
            ...waybillMetas.map((m) => String(m.uid || '')).filter(Boolean),
          ].filter(Boolean);
          const blobMap = await idbGetImportFiles(sessionScope, restoreUids);
          if (listMeta && blobMap.has(String(listMeta.uid || ''))) {
            const blob = blobMap.get(String(listMeta.uid || '')) as Blob;
            const listFile = new File([blob], String(listMeta.name || 'list.xlsx'), {
              type: String(listMeta.type || blob.type || 'application/octet-stream'),
              lastModified: Number(listMeta.lastModified || Date.now()),
            });
            const listUpload = {
              uid: String(listMeta.uid || `${Date.now()}`),
              name: listFile.name,
              status: 'done',
              originFileObj: listFile,
            } as any as UploadFile;
            setSelectedFileName(listFile.name);
            setUploadedFile(listFile);
            setFileList([listUpload]);
            await runAutoHeaderCheck(listFile, nextScenario, { showModal: false });
          }
          const restoredWaybillUploads: UploadFile[] = [];
          const restoredWaybillFiles: File[] = [];
          waybillMetas.forEach((meta) => {
            const uid = String(meta.uid || '');
            const blob = blobMap.get(uid);
            if (!uid || !blob) return;
            const file = new File([blob], String(meta.name || 'attachment.bin'), {
              type: String(meta.type || blob.type || 'application/octet-stream'),
              lastModified: Number(meta.lastModified || Date.now()),
            });
            restoredWaybillFiles.push(file);
            restoredWaybillUploads.push(({
              uid,
              name: file.name,
              status: 'done',
              originFileObj: file,
            } as any as UploadFile));
          });
          if (restoredWaybillUploads.length > 0) {
            setWaybillBundleFileList(restoredWaybillUploads);
            setWaybillBundleFiles(restoredWaybillFiles);
          }
          logImportDraft('Restore.Files.Result', {
            scope: sessionScope,
            requested_uids: restoreUids.length,
            hit_count: blobMap.size,
            list_restored: !!(listMeta && blobMap.has(String(listMeta.uid || ''))),
            waybill_restored_count: restoredWaybillUploads.length,
          });
          const restoredJobId = Number(parsed?.active_job_id || 0);
          const requestedStep = Math.max(0, Math.min(2, Number(parsed?.current_step || 0)));
          if (restoredJobId > 0 && requestedStep >= 2) {
            setSessionJobId(restoredJobId);
            setActiveJobId(restoredJobId);
            setCurrentStep(2);
            logImportDraft('Restore.Step.Decision', { scope: sessionScope, target_step: 2, reason: 'active_job_id_and_step2', active_job_id: restoredJobId });
          } else {
            const hasListRestored = !!(listMeta && blobMap.has(String(listMeta.uid || '')));
            const hasWaybillRestored = restoredWaybillUploads.length > 0;
            const canReachStep1 = hasListRestored && (nextScenario !== 'upload_immediate' || hasWaybillRestored);
            if (requestedStep >= 1 && canReachStep1) {
              setCurrentStep(1);
              logImportDraft('Restore.Step.Decision', { scope: sessionScope, target_step: 1, reason: 'files_restored', requested_step: requestedStep });
            } else {
              setCurrentStep(0);
              logImportDraft('Restore.Step.Decision', { scope: sessionScope, target_step: 0, reason: 'insufficient_files', requested_step: requestedStep });
              if (requestedStep > 0 && !canReachStep1) {
                message.info('已恢复可持久化配置；未找到完整文件缓存，需重新上传文件后继续。');
              }
            }
          }
          if (parsed?.field_mapping && typeof parsed.field_mapping === 'object') {
            setFieldMapping(parsed.field_mapping);
          }
          setDraftRestoreReady(true);
        },
        onCancel: () => {
          logImportDraft('Restore.Prompt.Cancel', { scope: sessionScope });
          try { localStorage.removeItem(getImportDraftStorageKey(sessionScope)); } catch {}
          idbClearImportFiles(sessionScope).catch(() => {});
          setDraftRestoreReady(true);
        },
      });
    } catch {
      logImportDraft('Restore.Error.ParseOrApply', { scope: sessionScope });
      try { localStorage.removeItem(getImportDraftStorageKey(sessionScope)); } catch {}
      idbClearImportFiles(sessionScope).catch(() => {});
      setDraftRestoreReady(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionScopeReady, sessionScope]);

  useEffect(() => {
    if (!sessionScopeReady) return;
    if (!draftRestoreReady) return;
    const draftJob = sessionJobId ? (jobs.find((j) => j.id === sessionJobId) || null) : null;
    const statusText = String(draftJob?.status || '').toLowerCase();
    const sessionStatus: ImportSessionStatus = ['queued', 'running'].includes(statusText)
      ? 'running'
      : (['done', 'failed', 'cancelled'].includes(statusText) ? 'finished' : 'draft');
    const listMeta: DraftFileMeta | null = (() => {
      if (!uploadedFile) return null;
      const uid = String((fileList[0] as any)?.uid || `list-${uploadedFile.name}-${uploadedFile.lastModified}`);
      return {
        uid,
        name: uploadedFile.name,
        type: uploadedFile.type || 'application/octet-stream',
        lastModified: Number(uploadedFile.lastModified || Date.now()),
        kind: 'list',
      };
    })();
    const waybillMetas: DraftFileMeta[] = (waybillBundleFileList || [])
      .map((it) => {
        const f = it.originFileObj as File | undefined;
        if (!f) return null;
        return {
          uid: String(it.uid || `waybill-${f.name}-${f.lastModified}`),
          name: f.name,
          type: f.type || 'application/octet-stream',
          lastModified: Number(f.lastModified || Date.now()),
          kind: 'waybill',
        } as DraftFileMeta;
      })
      .filter((it): it is DraftFileMeta => !!it);

    const payload = {
      updated_at: Date.now(),
      scenario,
      current_step: currentStep,
      field_mapping: fieldMapping,
      active_job_id: Number(sessionJobId || 0) || undefined,
      list_file: listMeta,
      waybill_files: waybillMetas,
      session_status: sessionStatus,
      execution_list_view: sessionStatus === 'finished' ? executionListView : undefined,
      execution_detail_tab_key: sessionStatus === 'finished' ? executionDetailTabKey : undefined,
      execution_detail_list_page: sessionStatus === 'finished' ? executionDetailListPage : undefined,
      execution_detail_list_page_size: sessionStatus === 'finished' ? executionDetailListPageSize : undefined,
      product_stats_page: sessionStatus === 'finished' ? productStatsPage : undefined,
      product_stats_page_size: sessionStatus === 'finished' ? productStatsPageSize : undefined,
    };
    logImportDraft('Persist.Payload', {
      scope: sessionScope,
      storage_key: getImportDraftStorageKey(sessionScope),
      session_status: sessionStatus,
      current_step: currentStep,
      active_job_id: activeJobId || null,
      session_job_id: sessionJobId || null,
      active_job_status: statusText || '',
      has_list_file: !!listMeta,
      waybill_file_count: waybillMetas.length,
      has_field_mapping: Object.keys(fieldMapping || {}).length > 0,
    });
    try {
      localStorage.setItem(getImportDraftStorageKey(sessionScope), JSON.stringify(payload));
    } catch {}
    const persistFiles = async () => {
      const keepUids = new Set<string>();
      const items: Array<{ uid: string; blob: Blob; name: string; type: string; lastModified: number }> = [];
      if (listMeta && uploadedFile) {
        keepUids.add(listMeta.uid);
        items.push({
          uid: listMeta.uid,
          blob: uploadedFile,
          name: listMeta.name,
          type: listMeta.type,
          lastModified: listMeta.lastModified,
        });
      }
      (waybillBundleFileList || []).forEach((it) => {
        const f = it.originFileObj as File | undefined;
        const uid = String(it.uid || '');
        if (!f || !uid) return;
        keepUids.add(uid);
        items.push({
          uid,
          blob: f,
          name: f.name,
          type: f.type || 'application/octet-stream',
          lastModified: Number(f.lastModified || Date.now()),
        });
      });
      try {
        await idbPutImportFiles(sessionScope, items);
        await idbDeleteImportMissing(sessionScope, keepUids);
        logImportDraft('Persist.Files.Done', {
          scope: sessionScope,
          put_count: items.length,
          keep_count: keepUids.size,
        });
      } catch {}
    };
    void persistFiles();
  }, [sessionScopeReady, sessionScope, draftRestoreReady, scenario, currentStep, fieldMapping, activeJobId, sessionJobId, jobs, uploadedFile, fileList, waybillBundleFileList, executionListView, executionDetailTabKey, executionDetailListPage, executionDetailListPageSize, productStatsPage, productStatsPageSize]);

  useEffect(() => {
    if (!activeJobId) {
      setJobItems([]);
      return;
    }
    loadJobItems(activeJobId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobId]);

  useEffect(() => {
    setHasValidated(false);
    setHeaderCheck({ status: 'idle' });
    setValidationSummary(null);
    setRowLimitExceeded(false);
    setManualWaybillMatches({});
    setPrecheckDone(false);
    setPrecheckResult(null);
    setPrecheckRunning(false);
    setPrecheckErrorText('');
  }, [scenario]);

  useEffect(() => {
    if (skipNextWaybillInvalidateRef.current) {
      // 这次 waybillBundleFiles.length 变化是预审自己拆分合并PDF造成的替换，不是用户重新
      // 上传了附件包，不应该让刚设置好的 precheckResult 失效——见 skipNextWaybillInvalidateRef
      // 声明处的说明。消费掉标记，下一次真正的变化恢复正常清空行为。
      skipNextWaybillInvalidateRef.current = false;
      return;
    }
    setPrecheckDone(false);
    setPrecheckResult(null);
    setPrecheckErrorText('');
  }, [selectedFileName, waybillBundleFiles.length]);

  useEffect(() => {
    if (!uploadedFile) return;
    // fieldMapping/transformRules 也放进依赖里：这样在“表头校验未通过”弹窗里换一份映射配置
    // 重试时，这里会自动重新跑一次静默校验并更新 headerCheck，弹窗内容跟着实时刷新，
    // 不需要用户关掉弹窗再重新触发。
    // pendingApplyModalCheck 为 true 时（从“字段映射配置”抽屉点了“应用到当前上传”），
    // 这次不再是静默的——校验不通过要把“表头校验未通过”弹窗弹出来，而不是让用户毫无
    // 提示地以为应用成功了。用这个标记而不是在 handleApplyProfileFromList 里直接调用校验，
    // 是因为那时候 setFieldMapping/setTransformRules 刚调用完，state 还没更新，直接校验会
    // 拿到旧的映射规则；放进这个 effect 里，等 state 真正更新触发重新渲染后再跑，才能校验到
    // 这次真正选中的配置。
    const shouldShowModal = pendingApplyModalCheck;
    (async () => {
      if (shouldShowModal) setPendingApplyModalCheck(false);
      const result = await runAutoHeaderCheck(uploadedFile, scenario, { showModal: shouldShowModal });
      if (result.ok) {
        if (headerMismatchModalOpen) {
          setHeaderMismatchModalOpen(false);
          message.success('映射校验通过，可以继续下一步。');
        } else if (shouldShowModal) {
          message.success('已应用映射配置，表头校验通过。');
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, uploadedFile, fieldMapping, transformRules, pendingApplyModalCheck]);

  // profileId 为空（下拉框被清除）表示"不使用任何映射配置"，退回标准模板的直接取值方式。
  const applyProfileForRetry = (profileId?: number) => {
    if (!profileId) {
      setActiveProfileId(undefined);
      setFieldMapping({});
      setTransformRules({});
      setProfileNameInput('');
      return;
    }
    const profile = scenarioProfiles.find((p) => p.id === profileId);
    if (!profile) return;
    setActiveProfileId(profile.id);
    setFieldMapping((profile.field_mapping || {}) as FieldMapping);
    setTransformRules((profile.transform_rules || {}) as MappingTransformRules);
    setProfileNameInput(profile.name);
  };

  useEffect(() => {
    if (parsedHeaders.length === 0) return;
    if (Object.keys(fieldMapping).length > 0) return;
    const next: FieldMapping = {};
    allScenarioFields.forEach((field) => {
      if (parsedHeaders.includes(field)) next[field] = field;
    });
    if (Object.keys(next).length > 0) setFieldMapping(next);
  }, [parsedHeaders, fieldMapping, allScenarioFields]);
  useEffect(() => {
    return () => {
      if (waybillPreviewUrl) {
        URL.revokeObjectURL(waybillPreviewUrl);
      }
    };
  }, [waybillPreviewUrl]);
  useEffect(() => {
    if (scenario !== 'upload_immediate' || waybillBundleFileList.length === 0) {
      setAttachmentCandidateNames([]);
      setAttachmentCandidatesWarning('');
      setAttachmentCandidatesLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setAttachmentCandidatesLoading(true);
      const nameSet = new Set<string>();
      let warning = '';
      for (const item of waybillBundleFileList) {
        const rawFile = item.originFileObj as File | undefined;
        if (!rawFile) continue;
        const fileName = String(rawFile.name || '').trim();
        const lowerName = fileName.toLowerCase();
        if (!fileName) continue;
        if (lowerName.endsWith('.zip')) {
          try {
            const buf = await rawFile.arrayBuffer();
            const mod = await import('jszip');
            const JSZip = (mod as any).default || mod;
            const zip = await JSZip.loadAsync(buf);
            zip.forEach((relPath: string, entry: any) => {
              if (entry?.dir) return;
              const baseName = String(relPath || '').replace(/\\/g, '/').split('/').filter(Boolean).pop() || '';
              if (baseName) nameSet.add(baseName);
            });
          } catch {
            nameSet.add(fileName);
            warning = '部分ZIP文件预解压失败，自动匹配可能不完整，请人工确认。';
          }
          continue;
        }
        nameSet.add(fileName);
      }
      if (cancelled) return;
      setAttachmentCandidateNames(Array.from(nameSet));
      setAttachmentCandidatesWarning(warning);
      setAttachmentCandidatesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [scenario, waybillBundleFileList]);
  useEffect(() => {
    setWaybillArchivePage(1);
  }, [normalizedWaybillArchiveFilter]);

  const handleScenarioChange = (next: string) => {
    const nextScenario = next as ScenarioKey;
    setScenario(nextScenario);
    // 映射配置本身按场景隔离存储（profile 记录带 scenario 字段，列表/保存都按当前 scenario
    // 过滤），但页面上“正在编辑、还没保存”的映射状态不会随场景切换自动切换——如果不在这里清空，
    // 切场景 Tab 后，上一个场景的 fieldMapping/transformRules/已选中的映射配置 id 会被静默带到
    // 新场景里，可能被误存成新场景下的配置，或者误用于新场景的校验。
    setFieldMapping({});
    setTransformRules({});
    setActiveProfileId(undefined);
    setProfileNameInput('');
    setHasValidated(false);
    setValidationSummary(null);
    const params = new URLSearchParams(location.search || '');
    params.set('scenario', nextScenario);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const dropdownOptions = await buildTemplateDropdownOptions(isAdminContainer ? adminActingCustomerId : undefined);
      const blob = await buildStyledScenarioTemplate(scenario, dropdownOptions);
      const fileName = `${SCENARIO_TEMPLATE_FILE_NAME[scenario]}.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      message.success('模板已生成并开始下载。');
    } catch (error: any) {
      message.error(error?.message || '模板生成失败，请稍后重试。');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleRunValidation = async (): Promise<{
    ok: boolean;
    rows: ParsedRow[];
    mapping: FieldMapping;
    overrides: GetMappedValueOverrides;
    summary: ValidationSummary | null;
  }> => {
    if (!uploadedFile) {
      message.warning('请先上传订单文件。');
      return { ok: false, rows: [], mapping: { ...fieldMapping }, overrides: {}, summary: null };
    }
    if (scenario === 'upload_immediate' && waybillBundleFiles.length === 0) {
      message.warning('当前场景必须先上传运单附件后再执行校验。');
      return { ok: false, rows: [], mapping: { ...fieldMapping }, overrides: {}, summary: null };
    }
    setHasValidated(false);
    const headerResult = await runHeaderCheck(uploadedFile, scenario);
    if (!headerResult.ok) {
      setValidationSummary(null);
      setRowLimitExceeded(false);
      setHasValidated(true);
      setHeaderMismatchModalOpen(true);
      return { ok: false, rows: [], mapping: { ...fieldMapping }, overrides: {}, summary: null };
    }
    try {
      const parsed = await parseSheetFromFile(uploadedFile);
      setParsedHeaders(parsed.headers);
      setParsedRows(parsed.rows);
      const overLimit = parsed.rows.length > 1000;
      setRowLimitExceeded(overLimit);
      const nextMapping: FieldMapping = { ...fieldMapping };
      allScenarioFields.forEach((field) => {
        if (!nextMapping[field] && parsed.headers.includes(field)) {
          nextMapping[field] = field;
        }
      });
      setFieldMapping(nextMapping);
      // “规则模式”字段（按列分组 / 反查项目）依赖 parsedRows，而上面 setParsedRows 只是把这次
      // 刚解析出来的新数据排进下一次渲染——组件 state 里的 ruleResolutions/projectLookupResults
      // 要等重新渲染一轮之后才会更新成基于新数据算出的结果。如果这里继续用 getMappedValue
      // 默认读 state 的方式取值，会读到“上一次渲染”（也就是上传这份文件之前）的旧结果，
      // 导致规则字段被错误解析成空值——这正是“第一次预审库存/运单匹配面板为空、要点第二次
      // 才对”的根因。这里改成对这次刚解析出的 parsed.rows 当场重新算一遍，不依赖 state 时机。
      const freshRuleResolutions = computeRuleResolutionsFrom(parsed.rows, transformRules);
      const freshLookupSourceValues = computeLookupProjectSourceValuesFrom(parsed.rows, transformRules);
      const freshLookupTags = Array.from(
        new Set(Object.values(freshLookupSourceValues).flat()),
      );
      let freshProjectLookupResults = projectLookupResults;
      if (freshLookupTags.length > 0) {
        try {
          const results = await resolveProjectByTag(freshLookupTags, isAdminContainer ? adminActingCustomerId : undefined);
          const merged = { ...projectLookupResults };
          results.forEach((r) => { merged[r.tag] = r; });
          freshProjectLookupResults = merged;
          // 顺手把新鲜结果写回 state，这样界面上其它靠 state 渲染的地方（不经过这里的
          // overrides）也能马上看到正确结果，不需要用户再点一次预审。
          setProjectLookupResults(merged);
        } catch {
          // 静默失败：反查失败时这个字段就是解析不出结果，会在校验阶段自然报
          // “缺少必填字段：项目”，不需要额外弹窗打断当前操作——跟原有 useEffect 的失败处理一致。
        }
      }
      const overrides: GetMappedValueOverrides = {
        ruleResolutions: freshRuleResolutions,
        projectLookupResults: freshProjectLookupResults,
      };
      const freshSummary = buildValidationSummary(parsed.rows, scenario, nextMapping, overrides);
      setValidationSummary(freshSummary);
      if (overLimit) {
        message.error(`导入数据共 ${parsed.rows.length} 行，已超过上限 1000 行，请拆分文件后重试。`);
      }
      return { ok: !overLimit, rows: parsed.rows, mapping: nextMapping, overrides, summary: freshSummary };
    } catch (error: any) {
      setParsedHeaders([]);
      setParsedRows([]);
      setValidationSummary(null);
      setRowLimitExceeded(false);
      setHeaderCheck({ status: 'error', reason: error?.message || '读取文件失败' });
      message.error(error?.message || '校验失败，无法解析文件。');
      return { ok: false, rows: [], mapping: { ...fieldMapping }, overrides: {}, summary: null };
    } finally {
      setHasValidated(true);
    }
  };

  const handleRunPrecheck = async () => {
    if (!uploadedFile) {
      message.warning('请先上传订单文件。');
      return;
    }
    if (scenario === 'upload_immediate' && waybillBundleFiles.length === 0) {
      message.warning('当前场景必须先上传运单附件后再执行预审。');
      return;
    }
    setPrecheckRunning(true);
    setPrecheckDone(false);
    setPrecheckResult(null);
    setPrecheckErrorText('');
    // 每次点击“开始预审/重新预审”都清空人工匹配缓存，确保执行完整重新匹配。
    setManualWaybillMatches({});
    try {
      const validation = await handleRunValidation();
      if (!validation.ok) {
        return;
      }
      // 这里必须传 validation.overrides（handleRunValidation 里对这次刚解析出的行数据当场
      // 现算的新鲜结果），不能让 getMappedValue 退回去默认读 state——道理跟上面 buildValidationSummary
      // 那处一样：state 里的 ruleResolutions/projectLookupResults 要等重新渲染一轮才会用上
      // 新数据，这里如果读 state 会读到旧结果，导致发给预审接口的规则字段被错误解析成空值。
      const rowsPayload = (validation.rows || []).map((row, idx) => ({
        row_no: Number(row.__sourceRowNo || idx + 2),
        serial_no: getMappedValue(row, '指令序号', validation.mapping, validation.overrides),
        warehouse_code: getMappedValue(row, '仓库', validation.mapping, validation.overrides),
        project_code: getMappedValue(row, '项目', validation.mapping, validation.overrides),
        inventory_unit_code: getMappedValue(row, '库存单元标识', validation.mapping, validation.overrides),
        quantity: getMappedValue(row, '发货数量', validation.mapping, validation.overrides),
        carrier: getMappedValue(row, '运单所属物流承运商', validation.mapping, validation.overrides),
        tracking_no: getMappedValue(row, '物流跟踪号', validation.mapping, validation.overrides),
        recipient: getMappedValue(row, '收件人姓名', validation.mapping, validation.overrides),
        address_line_1: getMappedValue(row, '地址第一行', validation.mapping, validation.overrides),
        insurance_amount: getMappedValue(row, '保险金额', validation.mapping, validation.overrides),
        waybill_file_name: getMappedValue(row, '物流运单附件文件名', validation.mapping, validation.overrides),
      }));
      const precheck = await precheckInstructionImport({
        scenario,
        rows: rowsPayload,
        waybillBundleFiles: scenario === 'upload_immediate' ? waybillBundleFiles : undefined,
        acting_as_customer_id: isAdminContainer ? adminActingCustomerId : undefined,
      });
      setPrecheckResult(precheck);
      setPrecheckDone(true);
      if (scenario === 'upload_immediate' && Array.isArray(precheck.generated_waybill_files) && precheck.generated_waybill_files.length > 0) {
        // 后端检测到上传的是一份多页合并运单PDF并按页拆分了——用拆分出来的文件顶替浏览器
        // 里原来那份合并PDF，作为后续真正提交创建导入任务时的运单附件。执行阶段只按文件名
        // 匹配、不会做条码识别，必须让它看到的就是已经拆好命名的单页文件，不能只在这一次
        // 预审里"看起来匹配上了"就完事——不这么做，真正执行时会因为看到的还是原始合并PDF
        // 而重新匹配失败。
        const generatedFiles = precheck.generated_waybill_files.map((f) =>
          base64ToFile(f.content_base64, f.name, 'application/pdf'),
        );
        const generatedFileList: UploadFile[] = generatedFiles.map((file, idx) => ({
          uid: `generated-waybill-${Date.now()}-${idx}`,
          name: file.name,
          status: 'done',
          originFileObj: file as any,
        }));
        // 下面这次 setWaybillBundleFiles 会改变 waybillBundleFiles.length（比如从1份合并PDF
        // 变成2份拆分后的单页文件），但这是系统自己做的替换，不是用户重新上传了附件包——
        // 打上标记，让“附件变了就让预审结果失效”的那个 effect 跳过这一次，不要把上面刚
        // 设置好的 precheckResult 冲掉。
        skipNextWaybillInvalidateRef.current = true;
        setWaybillBundleFiles(generatedFiles);
        setWaybillBundleFileList(generatedFileList);
        message.info(`检测到上传的是一份多页合并运单PDF，已自动按页拆分为 ${generatedFiles.length} 个文件，后续创建导入任务时将使用这些拆分后的文件。`);
      }
      if (scenario === 'upload_immediate') {
        const expectedGroupKeys = new Set<string>();
        rowsPayload.forEach((row, idx) => {
          const serialNo = String(row.serial_no || '').trim();
          const rowNo = Number(row.row_no || idx + 2);
          expectedGroupKeys.add(serialNo ? `serial-${serialNo}` : `row-${rowNo}`);
        });
        const expectedGroupCount = expectedGroupKeys.size;
        const actualGroupCount = Array.isArray(precheck.match_groups) ? precheck.match_groups.length : 0;
        if (expectedGroupCount > 0 && actualGroupCount < expectedGroupCount) {
          const detail = `预审结果不完整（应返回${expectedGroupCount}组，实际${actualGroupCount}组），可能因超时或处理中断。请重试，或减少附件数量后再预审。`;
          setPrecheckErrorText(detail);
          message.error(detail);
          return;
        }
      }
      // precheck.ok 只反映后端 /precheck 接口自己负责的那些检查（库存/项目/运单匹配等），
      // 不包含"列表文件校验"（预审1）里"副行字段需与主行一致"这类纯前端本地计算的问题——
      // 两者互相独立，后端不知道前端这边还有没解决的问题。这里必须并进来一起判断，否则会
      // 出现"提示说可以进入下一步，但按钮其实还是禁用的"这种误导。
      // 注意：这里不能读组件 state 里的 validationSummary——上面 handleRunValidation() 内部
      // 调用 setValidationSummary 只是把这次刚算出的新结果排进下一次渲染，这个函数还没执行完、
      // 还没到下一次渲染，此刻读 state 拿到的是上一次的旧值（跟这次会话里反复出现的
      // ruleResolutions 过期闭包是同一类问题）。必须用 handleRunValidation 返回值里
      // 一并带回来的 validation.summary，才是这次刚计算出的新鲜结果。
      const hasLocalValidationIssue = !!validation.summary && Number(validation.summary.invalidRows || 0) > 0;
      if (precheck.ok && !hasLocalValidationIssue) {
        message.success('预审完成：未发现错误，可进入下一步。');
      } else if (precheck.ok && hasLocalValidationIssue) {
        message.warning('预审完成，但"列表文件校验"仍存在错误，请修正后才能进入下一步。');
      } else {
        message.warning(`预审完成：存在 ${precheck.blocking_count} 个错误，请修正后重新预审。`);
      }
    } catch (error: any) {
      const msg = String(error?.message || '').toLowerCase();
      const timeout = error?.code === 'ECONNABORTED' || msg.includes('timeout');
      const detail = timeout
        ? '预审请求超时，当前结果无效。请重试，或减少附件数量后再预审。'
        : (error?.response?.data?.detail || error?.message || '预审失败，请稍后重试。');
      setPrecheckDone(false);
      setPrecheckResult(null);
      setPrecheckErrorText(detail);
      message.error(detail);
    } finally {
      setPrecheckRunning(false);
    }
  };

  const uploadProps: UploadProps = {
    accept: '.xlsx,.xls,.csv',
    maxCount: 1,
    fileList,
    showUploadList: false,
    beforeUpload: async (file) => {
      const uploadUid = String((file as any)?.uid || `list-${Date.now()}`);
      setSelectedFileName(file.name);
      setUploadedFile(file as File);
      setFileList([({
        uid: uploadUid,
        name: file.name,
        status: 'done',
        originFileObj: file,
      } as any as UploadFile)]);
      message.success(`已选择文件：${file.name}`);
      setHasValidated(false);
      setHeaderCheck({ status: 'idle' });
      setValidationSummary(null);
      setRowLimitExceeded(false);
      setParsedHeaders([]);
      setParsedRows([]);
      // 每次选中新文件都强制清空映射状态：自动校验永远先只按标准模板检查，不管当前场景下
      // 是否存过映射配置、也不管“高级设置”里之前编辑到什么状态——只有校验失败后，用户在
      // 弹窗里显式选择一份已保存的映射配置，才会让映射参与校验。
      setFieldMapping({});
      setTransformRules({});
      setActiveProfileId(undefined);
      setProfileNameInput('');
      setHeaderMismatchModalOpen(false);
      await runAutoHeaderCheck(file as File, scenario, { showModal: true });
      if (ADVANCED_MAPPING_ENABLED) {
        // 无论表头必填校验是否通过，都尽早把文件解析出的列名/数据缓存到 parsedHeaders/parsedRows，
        // 这样“高级设置：字段映射”里的“来源列”下拉和“值映射”弹窗才有数据可用——否则对于还没配置
        // 映射的陌生源文件，用户会陷入“要配置映射得先看到列名，但要看到列名得先通过必填校验，
        // 必填校验又要先有映射”的死循环。这里不影响 handleRunValidation 里真正的校验判定逻辑。
        try {
          const parsedForMapping = await parseSheetFromFile(file as File);
          setParsedHeaders(parsedForMapping.headers);
          setParsedRows(parsedForMapping.rows);
        } catch {
          // 静默失败：真正的解析错误会在用户点击“开始校验/预审”时由 handleRunValidation 报出。
        }
      }
      return false;
    },
    onRemove: () => {
      setSelectedFileName('');
      setUploadedFile(null);
      setFileList([]);
      setHeaderCheck({ status: 'idle' });
      setParsedHeaders([]);
      setParsedRows([]);
      setHasValidated(false);
      setRowLimitExceeded(false);
      setValidationSummary(null);
    },
    onChange: ({ fileList: nextFileList }) => {
      setFileList(nextFileList.slice(-1));
    },
  };
  const waybillBundleUploadProps: UploadProps = {
    accept: '.zip,.pdf,.png,.jpg,.jpeg,.webp',
    maxCount: 1000,
    multiple: true,
    fileList: waybillBundleFileList,
    showUploadList: false,
    beforeUpload: () => {
      return false;
    },
    onRemove: (file) => {
      const remaining = waybillBundleFileList.filter((it) => it.uid !== file.uid);
      setWaybillBundleFileList(remaining);
      setWaybillBundleFiles(
        remaining
          .map((it) => it.originFileObj as File | undefined)
          .filter((it): it is File => !!it),
      );
      setHasValidated(false);
      setValidationSummary(null);
      setRowLimitExceeded(false);
      return true;
    },
    onChange: ({ fileList: next }) => {
      const allowedSuffixes = ['.zip', '.pdf', '.png', '.jpg', '.jpeg', '.webp'];
      const accepted = next.filter((it) => {
        const nm = String(it?.name || '').trim().toLowerCase();
        return allowedSuffixes.some((suffix) => nm.endsWith(suffix));
      });
      const rejected = next.length - accepted.length;
      if (rejected > 0) {
        message.warning('已移除不支持的附件类型，仅支持 ZIP/PDF/PNG/JPG/JPEG/WEBP。');
      }
      const nextList = accepted.slice(0, 1000);
      if (next.length > 1000) {
        message.warning('运单附件最多上传1000个文件，请删除后重试。');
      }
      setWaybillBundleFileList(nextList);
      setWaybillBundleFiles(
        nextList
          .map((it) => it.originFileObj as File | undefined)
          .filter((it): it is File => !!it),
      );
      setHasValidated(false);
      setValidationSummary(null);
      setRowLimitExceeded(false);
    },
  };
  const clearWaybillUploadList = () => {
    setWaybillBundleFileList([]);
    setWaybillBundleFiles([]);
    setHasValidated(false);
    setValidationSummary(null);
    setRowLimitExceeded(false);
  };
  const removeWaybillFileByUid = (uid: string) => {
    const remaining = waybillBundleFileList.filter((it) => it.uid !== uid);
    setWaybillBundleFileList(remaining);
    setWaybillBundleFiles(
      remaining
        .map((it) => it.originFileObj as File | undefined)
        .filter((it): it is File => !!it),
    );
    setHasValidated(false);
    setValidationSummary(null);
    setRowLimitExceeded(false);
  };
  const closeWaybillPreview = () => {
    if (waybillPreviewUrl) {
      URL.revokeObjectURL(waybillPreviewUrl);
    }
    waybillZipRef.current = null;
    setWaybillPreviewVisible(false);
    setWaybillPreviewType('');
    setWaybillPreviewName('');
    setWaybillPreviewUrl('');
    setWaybillPreviewEntryName('');
    setWaybillPreviewLoading(false);
    setWaybillPreviewError('');
    setWaybillArchiveEntries([]);
    setWaybillArchiveFilterText('');
    setWaybillArchivePage(1);
    setWaybillArchivePageSize(20);
  };
  const getPreviewKindByName = (name: string) => {
    const lowerName = String(name || '').toLowerCase();
    const imageExt = /\.(png|jpe?g|webp|gif|bmp)$/i.test(lowerName);
    const pdfExt = /\.pdf$/i.test(lowerName);
    if (imageExt) return 'image' as const;
    if (pdfExt) return 'pdf' as const;
    if (/\.zip$/i.test(lowerName)) return 'zip' as const;
    return '' as const;
  };
  const getArchiveEntryFileName = (entryPath: string) => {
    const normalized = String(entryPath || '').replace(/\\/g, '/');
    const seg = normalized.split('/').filter(Boolean);
    return seg.length > 0 ? seg[seg.length - 1] : normalized;
  };
  const resetWaybillPreviewStateForOpen = () => {
    if (waybillPreviewUrl) {
      URL.revokeObjectURL(waybillPreviewUrl);
    }
    waybillZipRef.current = null;
    setWaybillPreviewType('');
    setWaybillPreviewUrl('');
    setWaybillPreviewEntryName('');
    setWaybillPreviewLoading(false);
    setWaybillPreviewError('');
    setWaybillArchiveEntries([]);
    setWaybillArchiveFilterText('');
    setWaybillArchivePage(1);
    setWaybillArchivePageSize(20);
  };
  const previewZipEntry = async (entryPath: string) => {
    try {
      const zip = waybillZipRef.current;
      if (!zip) throw new Error('压缩包上下文不存在');
      const zipFile = zip.file(entryPath);
      if (!zipFile) throw new Error('未找到压缩包条目');
      const kind = getPreviewKindByName(entryPath);
      if (kind !== 'image' && kind !== 'pdf') {
        message.warning('当前仅支持预览压缩包中的图片/PDF文件。');
        return;
      }
      const ab = await zipFile.async('arraybuffer');
      const ext = String(entryPath || '').toLowerCase().split('.').pop() || '';
      const mime = kind === 'pdf'
        ? 'application/pdf'
        : (ext === 'jpg' || ext === 'jpeg') ? 'image/jpeg' : `image/${ext}`;
      if (waybillPreviewUrl) {
        URL.revokeObjectURL(waybillPreviewUrl);
      }
      const blobUrl = URL.createObjectURL(new Blob([ab], { type: mime }));
      const simpleName = String(entryPath || '').split('/').pop() || entryPath;
      setWaybillPreviewEntryName(simpleName);
      setWaybillPreviewType(kind);
      setWaybillPreviewUrl(blobUrl);
    } catch (e: any) {
      message.error(e?.message || '预览压缩包条目失败');
    }
  };
  const downloadZipEntry = async (entryPath: string) => {
    try {
      const zip = waybillZipRef.current;
      if (!zip) throw new Error('压缩包上下文不存在');
      const zipFile = zip.file(entryPath);
      if (!zipFile) throw new Error('未找到压缩包条目');
      const ab = await zipFile.async('arraybuffer');
      const simpleName = String(entryPath || '').split('/').pop() || 'entry';
      const href = URL.createObjectURL(new Blob([ab]));
      const a = document.createElement('a');
      a.href = href;
      a.download = simpleName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(href), 2000);
    } catch (e: any) {
      message.error(e?.message || '下载压缩包条目失败');
    }
  };
  const openWaybillPreview = (file: UploadFile) => {
    const rawFile = file.originFileObj as File | undefined;
    if (!rawFile) {
      message.warning('当前文件无法预览，请重新上传后重试。');
      return;
    }
    const fileName = String(file.name || rawFile.name || '').trim();
    const mime = String(rawFile.type || '').toLowerCase();
    const basicKind = getPreviewKindByName(fileName);
    const isImage = basicKind === 'image' || mime.startsWith('image/');
    const isPdf = basicKind === 'pdf' || mime === 'application/pdf';
    const isZip = basicKind === 'zip';
    if (!isImage && !isPdf && !isZip) {
      message.warning('当前仅支持图片/PDF/ZIP在线预览。');
      return;
    }
    resetWaybillPreviewStateForOpen();
    setWaybillPreviewName(fileName);
    setWaybillPreviewVisible(true);
    setWaybillPreviewLoading(true);
    (async () => {
      try {
        if (isImage || isPdf) {
          const previewUrl = URL.createObjectURL(rawFile);
          setWaybillPreviewType(isImage ? 'image' : 'pdf');
          setWaybillPreviewUrl(previewUrl);
          return;
        }
        const buf = await rawFile.arrayBuffer();
        const mod = await import('jszip');
        const JSZip = (mod as any).default || mod;
        const zip = await JSZip.loadAsync(buf);
        waybillZipRef.current = zip;
        const entries: WaybillArchiveEntry[] = [];
        zip.forEach((relPath: string, entry: any) => {
          if (!!entry?.dir) return;
          entries.push({
            path: relPath,
            dir: false,
            size: Number(entry?._dataUncompressedSize || entry?.uncompressedSize || 0) || 0,
          });
        });
        const sorted = entries.sort((a, b) => a.path.localeCompare(b.path));
        setWaybillArchiveEntries(sorted);
        setWaybillPreviewType('zip');
      } catch (e: any) {
        setWaybillPreviewError(e?.message || '压缩包预览失败');
      } finally {
        setWaybillPreviewLoading(false);
      }
    })();
  };
  const openMatchedWaybillPreviewByName = async (matchedFileName: string) => {
    const targetName = String(matchedFileName || '').trim();
    if (!targetName) {
      message.warning('未找到可预览的附件文件名。');
      return;
    }
    const normalizedTarget = targetName.toLowerCase();
    const direct = waybillBundleFileList.find((f) => (
      String(f.name || '').trim().toLowerCase() === normalizedTarget
      && !!f.originFileObj
    ));
    if (direct) {
      openWaybillPreview(direct);
      return;
    }

    const zipFiles = waybillBundleFileList.filter(
      (f) => /\.zip$/i.test(String(f.name || '').trim()) && !!f.originFileObj,
    );
    if (zipFiles.length === 0) {
      message.warning('未找到可在线预览的匹配附件文件。');
      return;
    }

    const toBaseName = (pathOrName: string) => {
      const normalized = String(pathOrName || '').replace(/\\/g, '/');
      const seg = normalized.split('/').filter(Boolean);
      return seg.length > 0 ? seg[seg.length - 1] : normalized;
    };
    const targetBase = toBaseName(targetName).toLowerCase();

    resetWaybillPreviewStateForOpen();
    setWaybillPreviewName(targetName);
    setWaybillPreviewVisible(true);
    setWaybillPreviewLoading(true);
    try {
      const mod = await import('jszip');
      const JSZip = (mod as any).default || mod;
      for (const zipUploadFile of zipFiles) {
        const rawZip = zipUploadFile.originFileObj as File | undefined;
        if (!rawZip) continue;
        const buf = await rawZip.arrayBuffer();
        const zip = await JSZip.loadAsync(buf);
        let foundPath = '';
        zip.forEach((relPath: string, entry: any) => {
          if (foundPath || entry?.dir) return;
          const relLower = String(relPath || '').trim().toLowerCase();
          const baseLower = toBaseName(relPath).toLowerCase();
          if (relLower === normalizedTarget || baseLower === targetBase) {
            foundPath = relPath;
          }
        });
        if (!foundPath) continue;
        const zipEntry = zip.file(foundPath);
        if (!zipEntry) continue;
        const kind = getPreviewKindByName(foundPath);
        if (kind !== 'image' && kind !== 'pdf') {
          setWaybillPreviewVisible(false);
          setWaybillPreviewLoading(false);
          message.warning('当前仅支持预览PDF或常见图片格式。');
          return;
        }
        const ab = await zipEntry.async('arraybuffer');
        const ext = String(foundPath || '').toLowerCase().split('.').pop() || '';
        const mime = kind === 'pdf'
          ? 'application/pdf'
          : (ext === 'jpg' || ext === 'jpeg') ? 'image/jpeg' : `image/${ext}`;
        const blobUrl = URL.createObjectURL(new Blob([ab], { type: mime }));
        setWaybillPreviewEntryName(toBaseName(foundPath));
        setWaybillPreviewType(kind);
        setWaybillPreviewUrl(blobUrl);
        setWaybillPreviewLoading(false);
        return;
      }
      setWaybillPreviewVisible(false);
      setWaybillPreviewLoading(false);
      message.warning('未找到可在线预览的匹配附件文件。');
    } catch (e: any) {
      setWaybillPreviewVisible(false);
      setWaybillPreviewLoading(false);
      message.error(e?.message || '预览匹配附件失败');
    }
  };
  const handlePreviewListFile = async () => {
    if (!uploadedFile) {
      message.warning('请先上传列表文件。');
      return;
    }
    setListFilePreviewVisible(true);
    setListFilePreviewLoading(true);
    setListFilePreviewPage(1);
    try {
      const parsed = await parseSheetFromFile(uploadedFile);
      setListFilePreviewHeaders(parsed.headers || []);
      setListFilePreviewRows(parsed.rows || []);
    } catch (error: any) {
      setListFilePreviewHeaders([]);
      setListFilePreviewRows([]);
      message.error(error?.message || '列表文件预览失败，请检查文件后重试。');
    } finally {
      setListFilePreviewLoading(false);
    }
  };

  const bulkCreatePath = location.pathname.replace(/\/instructions\/import$/, '/instructions/bulk-create');
  const applyAutoSameNameMapping = () => {
    if (parsedHeaders.length === 0) {
      message.warning('请先上传并解析文件。');
      return;
    }
    const next: FieldMapping = {};
    allScenarioFields.forEach((field) => {
      if (parsedHeaders.includes(field)) next[field] = field;
    });
    setFieldMapping(next);
    setHasValidated(false);
    setValidationSummary(null);
    message.success('已按同名字段自动映射。');
  };

  const setFieldMode = (field: string, modeCfg: FieldModeConfig | undefined) => {
    setTransformRules((prev) => {
      const nextModes = { ...(prev.field_modes || {}) };
      if (modeCfg) nextModes[field] = modeCfg; else delete nextModes[field];
      return { ...prev, field_modes: nextModes };
    });
    setHasValidated(false);
    setValidationSummary(null);
  };

  const setFieldTransform = (field: string, transformType: TransformType | undefined) => {
    setTransformRules((prev) => {
      const nextTransforms = { ...(prev.field_transforms || {}) };
      if (transformType) nextTransforms[field] = transformType; else delete nextTransforms[field];
      return { ...prev, field_transforms: nextTransforms };
    });
    setHasValidated(false);
    setValidationSummary(null);
  };

  // 弹窗里展示的行 = 已经配置过对照的取值 ∪ 这次上传文件里这一列出现的取值——不再
  // 局限于“这次文件里有什么就只能配什么”。两边按“忽略大小写和首尾空格”折叠成同一行，
  // 已配置的 key 优先沿用（避免因为大小写不同产生一个几乎重复的新 key）。没有上传文件
  // 时（parsedRows 为空）也允许打开弹窗，只是这时候只能看到已配置的条目、手动新增，
  // 看不到“文件里还有哪些新值没配”这部分。
  const openValueMapModal = async (field: string) => {
    const modeCfg = (transformRules.field_modes || {})[field];
    const existingValueMap = (modeCfg && modeCfg.mode === 'value_map') ? modeCfg.valueMap : {};
    const existingKeys = Object.keys(existingValueMap);
    const existingKeyByNorm = new Map<string, string>();
    existingKeys.forEach((k) => existingKeyByNorm.set(normalizeForCaseInsensitiveMatch(k), k));

    const mergedKeys = [...existingKeys];
    const notInFile = new Set(existingKeys);
    if (parsedRows.length > 0) {
      const mappedHeader = fieldMapping[field] || field;
      const seenNorms = new Set<string>();
      parsedRows.forEach((r) => {
        const rawVal = normalizeCellText(getRowValueByHeaderName(r, mappedHeader));
        if (!rawVal) return;
        const norm = normalizeForCaseInsensitiveMatch(rawVal);
        if (seenNorms.has(norm)) return;
        seenNorms.add(norm);
        const existingKey = existingKeyByNorm.get(norm);
        if (existingKey) {
          notInFile.delete(existingKey);
        } else {
          mergedKeys.push(rawVal);
        }
      });
    }
    setValueMapDistinctValues(mergedKeys);
    setValueMapKeysNotInFile(notInFile);
    setNewValueMapKeyInput('');
    setNewValueMapTargetInput(undefined);
    const carrierFields = ['运单所属物流承运商', '偏好物流承运商'];
    if (field === '仓库' || field === '项目' || field === '国家' || carrierFields.includes(field)) {
      try {
        const options = await buildTemplateDropdownOptions(isAdminContainer ? adminActingCustomerId : undefined);
        setValueMapCanonicalOptions(
          field === '仓库' ? options.warehouseOptions
            : field === '项目' ? options.projectOptions
            : field === '国家' ? options.countryOptions
            : options.providerOptions,
        );
      } catch {
        setValueMapCanonicalOptions([]);
      }
    } else {
      setValueMapCanonicalOptions([]);
    }
    setValueMapModalField(field);
  };

  const updateValueMapEntry = (field: string, rawVal: string, targetVal: string | undefined) => {
    setTransformRules((prev) => {
      const nextModes = { ...(prev.field_modes || {}) };
      const existing = nextModes[field];
      const existingValueMap = (existing && existing.mode === 'value_map') ? existing.valueMap : {};
      const nextValueMap = { ...existingValueMap };
      if (targetVal) nextValueMap[rawVal] = targetVal; else delete nextValueMap[rawVal];
      nextModes[field] = { mode: 'value_map', valueMap: nextValueMap };
      return { ...prev, field_modes: nextModes };
    });
    setHasValidated(false);
    setValidationSummary(null);
  };

  // 弹窗里点“删除”这一行对照：既要清掉存储里的这个 key，也要把它从弹窗当前展示的
  // 行列表里移除（否则删完了那一行还会留在弹窗里，只是没有目标值，容易误以为没删掉）。
  const handleDeleteValueMapEntry = (field: string, rawVal: string) => {
    updateValueMapEntry(field, rawVal, undefined);
    setValueMapDistinctValues((prev) => prev.filter((k) => k !== rawVal));
    setValueMapKeysNotInFile((prev) => {
      const next = new Set(prev);
      next.delete(rawVal);
      return next;
    });
  };

  // 弹窗里“手动新增一条对照”：不依赖这次文件里有没有出现这个值，客户可以提前把
  // 来源系统已知的取值都配好。如果输入的值跟已有某个 key 只是大小写/空格不同，
  // 更新那个已有 key，不新增一个几乎重复的 key。
  const handleAddValueMapEntry = () => {
    const field = valueMapModalField;
    if (!field) return;
    const raw = newValueMapKeyInput.trim();
    if (!raw) {
      message.warning('请输入需要新增对照的原始取值。');
      return;
    }
    const norm = normalizeForCaseInsensitiveMatch(raw);
    const existingKey = valueMapDistinctValues.find((k) => normalizeForCaseInsensitiveMatch(k) === norm);
    const keyToUse = existingKey || raw;
    updateValueMapEntry(field, keyToUse, newValueMapTargetInput || undefined);
    if (!existingKey) {
      setValueMapDistinctValues((prev) => [...prev, keyToUse]);
      setValueMapKeysNotInFile((prev) => new Set(prev).add(keyToUse));
    } else {
      message.info('该取值已存在，已更新其对照目标。');
    }
    setNewValueMapKeyInput('');
    setNewValueMapTargetInput(undefined);
  };

  // “设置项目归属”弹窗里为某个取值选定项目（或清除人工选择、回退到自动反查结果）。
  const updateProjectLookupOverride = (field: string, rawVal: string, projectCode: string | undefined) => {
    setTransformRules((prev) => {
      const nextModes = { ...(prev.field_modes || {}) };
      const existing = nextModes[field];
      if (!existing || existing.mode !== 'rule' || existing.ruleType !== 'lookup_project') return prev;
      const nextOverrides = { ...(existing.params.manualOverrides || {}) };
      if (projectCode) nextOverrides[rawVal] = projectCode; else delete nextOverrides[rawVal];
      nextModes[field] = { mode: 'rule', ruleType: 'lookup_project', params: { ...existing.params, manualOverrides: nextOverrides } };
      return { ...prev, field_modes: nextModes };
    });
    setHasValidated(false);
    setValidationSummary(null);
  };

  // 打开“设置项目归属”弹窗时，拉一份这个客户真实的项目列表，供手动指定项目时选择——
  // 跟“值映射”给“项目”字段配置对照时用的是同一份数据源，格式也保持一致（"编码 - 名称"）。
  useEffect(() => {
    if (!projectLookupModalField) return;
    let cancelled = false;
    buildTemplateDropdownOptions(isAdminContainer ? adminActingCustomerId : undefined)
      .then((options) => { if (!cancelled) setProjectLookupProjectOptions(options.projectOptions); })
      .catch(() => { if (!cancelled) setProjectLookupProjectOptions([]); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectLookupModalField]);

  // “字段映射设置”抽屉打开时拉一份仓库/项目/承运商候选列表，供“固定值”映射方式下这几个
  // 字段的输入框做候选提示——见 constantDropdownOptions 声明处的说明。
  useEffect(() => {
    if (!mappingDrawerOpen) return;
    let cancelled = false;
    buildTemplateDropdownOptions(isAdminContainer ? adminActingCustomerId : undefined)
      .then((options) => { if (!cancelled) setConstantDropdownOptions(options); })
      .catch(() => { if (!cancelled) setConstantDropdownOptions(null); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingDrawerOpen]);

  // 保存：activeProfileId 有值表示当前编辑的是一份已存在的配置，做真正的更新（PATCH）；
  // 否则是新建。之前这里不分情况永远调用新建接口，选中已有配置改几个字段后点保存会因为
  // 撞上（tenant_id, source_type, scenario, name）唯一约束而报错——这里是那个问题的修复。
  const handleSaveMappingProfile = async () => {
    const name = String(profileNameInput || '').trim();
    if (!name) {
      message.warning('请输入映射配置名称。');
      return;
    }
    setSavingProfile(true);
    try {
      const actingAsCustomerId = isAdminContainer ? adminActingCustomerId : undefined;
      if (activeProfileId) {
        const updated = await updateInstructionImportMappingProfile(activeProfileId, {
          name,
          field_mapping: fieldMapping,
          transform_rules: transformRules,
        }, actingAsCustomerId);
        setActiveProfileId(updated.id);
        message.success('映射配置已更新。');
      } else {
        const created = await createInstructionImportMappingProfile({
          source_type: 'excel_csv',
          scenario,
          name,
          field_mapping: fieldMapping,
          transform_rules: transformRules,
          acting_as_customer_id: actingAsCustomerId,
        });
        setActiveProfileId(created.id);
        message.success('映射配置已保存。');
      }
      await loadProfiles(scenario);
      setMappingDrawerView('list');
      setSaveNameModalOpen(false);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '保存映射配置失败');
    } finally {
      setSavingProfile(false);
    }
  };

  // 另存为：不管当前是否在编辑一份已有配置，永远走新建接口——用于"在现有配置基础上
  // 改出一份新的，不覆盖原配置"的场景。名称必须和已有配置不同，否则会被后端唯一约束拒绝。
  const handleSaveAsNewProfile = async () => {
    const name = String(profileNameInput || '').trim();
    if (!name) {
      message.warning('请输入映射配置名称。');
      return;
    }
    setSavingProfile(true);
    try {
      const created = await createInstructionImportMappingProfile({
        source_type: 'excel_csv',
        scenario,
        name,
        field_mapping: fieldMapping,
        transform_rules: transformRules,
        acting_as_customer_id: isAdminContainer ? adminActingCustomerId : undefined,
      });
      setActiveProfileId(created.id);
      await loadProfiles(scenario);
      message.success('已另存为新的映射配置。');
      setMappingDrawerView('list');
      setSaveNameModalOpen(false);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '另存为新配置失败，请检查名称是否已存在');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSelectProfile = (profileId: number) => {
    setActiveProfileId(profileId);
    const profile = scenarioProfiles.find((p) => p.id === profileId);
    if (!profile) return;
    setFieldMapping((profile.field_mapping || {}) as FieldMapping);
    setTransformRules((profile.transform_rules || {}) as MappingTransformRules);
    setHasValidated(false);
    setValidationSummary(null);
    setProfileNameInput(profile.name);
  };

  // 管理员选定/切换了"客户"之后，重新拉一次映射配置列表——每次切换客户都要重新加载，
  // 不能沿用上一个客户的列表；客户账号自己打开这个页面 adminActingCustomerId 恒为
  // undefined，这个 effect 不会因为它触发（依赖数组里另一半 mappingDrawerOpen 才是
  // 客户账号打开抽屉时的正常刷新入口，见下面 openMappingDrawer）。
  useEffect(() => {
    if (!isAdminContainer || !mappingDrawerOpen || !adminActingCustomerId) return;
    loadProfiles(scenario);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminContainer, mappingDrawerOpen, adminActingCustomerId]);

  // 打开"字段映射设置"抽屉：默认回到配置列表页，并刷新一次列表，避免展示过期数据。
  // 管理员入口打开时，顺带把客户列表拉出来，供下面的"客户"选择器用。
  const openMappingDrawer = () => {
    setMappingDrawerView('list');
    setMappingDrawerOpen(true);
    if (isAdminContainer) {
      void ensureAdminCustomers();
    } else {
      loadProfiles(scenario);
    }
  };

  // 列表页点"新建映射配置"：清空当前编辑状态，进入空白的编辑器视图。
  const handleCreateNewProfile = () => {
    setActiveProfileId(undefined);
    setFieldMapping({});
    setTransformRules({});
    setProfileNameInput('');
    setMappingDrawerView('editor');
  };

  // 列表页点"编辑"：把这份配置加载进当前编辑状态，并切到编辑器视图——这时候标题会
  // 明确显示"正在编辑《xxx》"，不会和"新建"混淆。
  const handleEditProfile = (profileId: number) => {
    handleSelectProfile(profileId);
    setMappingDrawerView('editor');
  };

  // 列表页点"应用到当前上传"：把这份配置的映射规则应用到当前正在处理的文件上，
  // 不进入编辑器，直接关闭抽屉回到步骤2；同时标记 pendingApplyModalCheck，让上面
  // 那个校验 effect 在这次状态更新生效后自动重新校验一次表头，校验不通过就弹出
  // "表头校验未通过"弹窗显示结果，不是应用完什么反馈都没有。
  const handleApplyProfileFromList = (profileId: number) => {
    handleSelectProfile(profileId);
    setMappingDrawerOpen(false);
    // 还没上传文件时没有可校验的对象，不设置这个标记——否则会在用户之后第一次上传文件时
    // 意外弹出一次跟这次点击无关的校验弹窗。
    if (uploadedFile) {
      setPendingApplyModalCheck(true);
    }
  };

  const handleDeleteProfile = async (profileId: number) => {
    try {
      await deleteInstructionImportMappingProfile(profileId, isAdminContainer ? adminActingCustomerId : undefined);
      if (activeProfileId === profileId) {
        setActiveProfileId(undefined);
        setProfileNameInput('');
      }
      await loadProfiles(scenario);
      message.success('映射配置已删除。');
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '删除映射配置失败');
    }
  };

  const handleCreateImportJob = async (autoExecute: boolean = false) => {
    if (!selectedFileName) {
      message.warning('请先上传文件。');
      return;
    }
    if (rowLimitExceeded) {
      message.error('当前文件数据行数超过1000，无法创建导入任务，请拆分后重试。');
      return;
    }
    if (!validationSummary) {
      message.warning('缺少校验结果，请先完成文件解析。');
      return;
    }
    if (!precheckDone) {
      message.warning('请先完成预审后再创建导入任务。');
      return;
    }
    if (effectiveBlockingCount > 0) {
      message.warning('预审存在错误，请修正后重新预审。');
      return;
    }
    if (scenario === 'upload_immediate' && waybillBundleFiles.length === 0) {
      message.warning('当前场景必须上传运单附件后才能创建导入任务。');
      return;
    }
    if (scenario === 'upload_immediate' && waybillMatchPendingCount > 0) {
      message.warning(`仍有 ${waybillMatchPendingCount} 条运单匹配待处理，请先完成人工匹配。`);
      return;
    }
    setSubmittingJob(true);
    setExecuteResultError('');
    try {
      const actingAsCustomerId = isAdminContainer ? adminActingCustomerId : undefined;
      const job = await createInstructionImportJob({
        source_type: 'excel_csv',
        scenario,
        file_name: selectedFileName,
        file: uploadedFile || undefined,
        waybillBundleFiles: waybillBundleFiles.length > 0 ? waybillBundleFiles : undefined,
        mapping_profile: activeProfileId,
        field_mapping_snapshot: fieldMapping,
        transform_rules_snapshot: transformRules,
        summary: validationSummary,
        meta: scenario === 'upload_immediate' ? {
          match_confirmed: true,
          enable_smart_match: true,
          pre_match_warning: attachmentCandidatesWarning || '',
          manual_match_by_serial: waybillMatchMeta,
          precheck_blocking_count: effectiveBlockingCount,
          precheck_warning_count: Number(precheckResult?.warning_count || 0),
        } : undefined,
        acting_as_customer_id: actingAsCustomerId,
      });
      await loadJobs(scenario);
      setActiveJobId(job.id);
      setSessionJobId(job.id);
      if (autoExecute) {
        setExecutingJob(true);
        try {
          await executeInstructionImportJob(job.id, { mode: 'all' }, actingAsCustomerId);
          await loadJobs(scenario);
          await loadJobItems(job.id);
        } catch (execError: any) {
          setExecuteResultError(execError?.response?.data?.detail || '自动执行导入任务失败，请稍后重试。');
          message.error(execError?.response?.data?.detail || '自动执行导入任务失败，请稍后重试。');
        } finally {
          setExecutingJob(false);
        }
      }
    } catch (error: any) {
      if (autoExecute) {
        setExecuteResultError(error?.response?.data?.detail || '创建导入任务失败，无法自动执行。');
      }
      message.error(error?.response?.data?.detail || '创建导入任务失败');
    } finally {
      setSubmittingJob(false);
    }
  };

  const handleExecuteJob = async (mode: 'all' | 'failed_only' | 'skipped_force' = 'all') => {
    if (!activeJobId) {
      message.warning('请先选择一个导入任务。');
      return;
    }
    setExecutingJob(true);
    try {
      const result = await executeInstructionImportJob(activeJobId, { mode }, isAdminContainer ? adminActingCustomerId : undefined);
      const modeLabel = mode === 'failed_only'
        ? '失败行重试'
        : (mode === 'skipped_force' ? '强制创建跳过项' : '全量执行');
      message.success(`${modeLabel}已入队，后台处理中，请稍后查看结果。`);
      await loadJobs(scenario);
      await loadJobItems(activeJobId);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '执行导入任务失败');
    } finally {
      setExecutingJob(false);
    }
  };
  const handleCancelJob = async () => {
    if (!activeJobId) {
      message.warning('请先选择一个导入任务。');
      return;
    }
    setCancellingJob(true);
    try {
      const res = await cancelInstructionImportJob(activeJobId, isAdminContainer ? adminActingCustomerId : undefined);
      message.success(`已提交取消请求，任务状态：${res.status}`);
      await loadJobs(scenario);
      await loadJobItems(activeJobId);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || '取消任务失败');
    } finally {
      setCancellingJob(false);
    }
  };

  const parsedRowBySourceNo = useMemo(() => {
    const m = new Map<number, ParsedRow>();
    parsedRows.forEach((r) => {
      const no = Number(r.__sourceRowNo || 0);
      if (no > 0) m.set(no, r);
    });
    return m;
  }, [parsedRows]);
  const validationDetailRows = useMemo(() => {
    const details = validationSummary?.invalidDetails || [];
    const rows = details.map((item, idx) => {
      const row = parsedRowBySourceNo.get(Number(item.rowIndex));
      const serialNo = row ? getMappedValue(row, '指令序号', fieldMapping) : '';
      return {
        key: `invalid-${item.rowIndex}-${idx}`,
        rowIndex: item.rowIndex,
        serialNo: serialNo || '-',
        issues: item.issues,
        sourceRow: row || null,
        __serialSortValue: /^\d+$/.test(serialNo) ? Number(serialNo) : Number.MAX_SAFE_INTEGER,
      };
    });
    rows.sort((a, b) => {
      if (a.__serialSortValue !== b.__serialSortValue) return a.__serialSortValue - b.__serialSortValue;
      if (a.serialNo !== b.serialNo) return String(a.serialNo).localeCompare(String(b.serialNo), 'zh-CN');
      return Number(a.rowIndex) - Number(b.rowIndex);
    });
    const groupColorMap = new Map<string, string>();
    let groupIndex = 0;
    rows.forEach((item) => {
      const groupKey = String(item.serialNo || '-');
      if (!groupColorMap.has(groupKey)) {
        groupColorMap.set(groupKey, groupIndex % 2 === 0 ? '#f6ffed' : '#e6f4ff');
        groupIndex += 1;
      }
    });
    return rows.map((item) => ({
      ...item,
      __groupBgColor: groupColorMap.get(String(item.serialNo || '-')) || '#ffffff',
    }));
  }, [validationSummary, parsedRowBySourceNo, fieldMapping]);
  const activeValidationRow = useMemo(
    () => (activeValidationRowIndex != null ? parsedRowBySourceNo.get(Number(activeValidationRowIndex)) || null : null),
    [activeValidationRowIndex, parsedRowBySourceNo],
  );
  const renderValidationIssueGroups = (issues: string[]) => {
    const missingRequiredFields: string[] = [];
    const missingDetailFields: string[] = [];
    const inconsistentFields: string[] = [];
    const serialFormatIssues: string[] = [];
    const otherIssues: string[] = [];
    (issues || []).forEach((issueRaw) => {
      const normalized = String(issueRaw || '').trim();
      if (!normalized) return;
      const splitIssues = normalized
        .split(/[；;\n]/g)
        .map((s) => String(s || '').trim())
        .filter(Boolean);
      splitIssues.forEach((issue) => {
        if (issue.startsWith('缺少必填字段：')) {
          const fields = issue.replace('缺少必填字段：', '').split('、').map((s) => String(s || '').trim()).filter(Boolean);
          missingRequiredFields.push(...fields);
          return;
        }
        const detailMissingMatch = issue.match(/^同一指令序号下的明细行必须填写「(.+?)」$/);
        if (detailMissingMatch?.[1]) {
          missingDetailFields.push(String(detailMissingMatch[1]).trim());
          return;
        }
        const inconsistentMatch = issue.match(/^字段「(.+?)」需与该序号首行一致或留空$/);
        if (inconsistentMatch?.[1]) {
          inconsistentFields.push(String(inconsistentMatch[1]).trim());
          return;
        }
        if (/指令序号.*纯数字/.test(issue)) {
          serialFormatIssues.push('指令序号必须为纯数字');
          return;
        }
        otherIssues.push(issue);
      });
    });
    const uniq = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));
    const required = uniq(missingRequiredFields);
    const detailRequired = uniq(missingDetailFields);
    const inconsistent = uniq(inconsistentFields);
    const serialFormats = uniq(serialFormatIssues);
    return (
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        {required.length > 0 && (
          <div>
            <Typography.Text style={{ marginRight: 8 }}>缺少必填列:</Typography.Text>
            <Space size={[6, 6]} wrap>
              {required.map((field) => (
                <Tag key={`req-${field}`} color="red" style={{ borderRadius: 999, paddingInline: 10 }}>
                  {field}
                </Tag>
              ))}
            </Space>
          </div>
        )}
        {detailRequired.length > 0 && (
          <div>
            <Typography.Text style={{ marginRight: 8 }}>此行为副行,缺少必填列:</Typography.Text>
            <Space size={[6, 6]} wrap>
              {detailRequired.map((field) => (
                <Tag key={`detail-${field}`} color="orange" style={{ borderRadius: 999, paddingInline: 10 }}>
                  {field}
                </Tag>
              ))}
            </Space>
          </div>
        )}
        {inconsistent.length > 0 && (
          <div>
            <Typography.Text style={{ marginRight: 8 }}>此行为副行,与主行不一致的字段:</Typography.Text>
            <Space size={[6, 6]} wrap>
              {inconsistent.map((field) => (
                <Tag key={`cons-${field}`} color="gold" style={{ borderRadius: 999, paddingInline: 10 }}>
                  {field}
                </Tag>
              ))}
            </Space>
          </div>
        )}
        {serialFormats.length > 0 && (
          <div>
            <Typography.Text style={{ marginRight: 8 }}>指令序号格式错误:</Typography.Text>
            <Space size={[6, 6]} wrap>
              {serialFormats.map((issue) => (
                <Tag key={`serial-format-${issue}`} color="purple" style={{ borderRadius: 999, paddingInline: 10 }}>
                  {issue}
                </Tag>
              ))}
            </Space>
          </div>
        )}
        {otherIssues.length > 0 && (
          <Space size={[6, 6]} wrap>
            {otherIssues.map((issue, idx) => (
              <Tag key={`other-${idx}`} color="purple" style={{ borderRadius: 999, paddingInline: 10 }}>
                {issue}
              </Tag>
            ))}
          </Space>
        )}
      </Space>
    );
  };
  const selectedJob = useMemo(
    () => (activeJobId ? (jobs.find((j) => j.id === activeJobId) || null) : null),
    [jobs, activeJobId],
  );
  const jobTerminal = ['done', 'failed', 'cancelled'].includes(String(selectedJob?.status || '').toLowerCase());
  const hasRunningOrQueuedJob = useMemo(
    () => (jobs || []).some((j) => ['running', 'queued'].includes(String(j.status || '').toLowerCase())),
    [jobs],
  );
  const selectedSummary = useMemo(
    () => ((selectedJob?.summary || {}) as Record<string, unknown>),
    [selectedJob],
  );
  const queuedStuckMinutes = useMemo(() => {
    if (String(selectedJob?.status || '').toLowerCase() !== 'queued') return 0;
    const ts = String(selectedJob?.updated_at || selectedJob?.created_at || '').trim();
    if (!ts) return 0;
    const t = new Date(ts).getTime();
    if (!Number.isFinite(t) || t <= 0) return 0;
    return Math.max(0, Math.floor((Date.now() - t) / 60000));
  }, [selectedJob]);
  const queuedLikelyStuck = queuedStuckMinutes >= 10;
  const progressTotal = Number(selectedSummary.total_rows || selectedSummary.executed_total_rows || 0) || 0;
  const progressDone = Number(selectedSummary.processed_count || 0) || 0;
  const progressPercent = progressTotal > 0 ? Math.min(100, Math.round((progressDone / progressTotal) * 100)) : 0;
  const etaSeconds = Number(selectedSummary.eta_seconds ?? NaN);
  const allBlockingIssues = precheckResult?.blocking_issues || [];
  const allWarningIssues = precheckResult?.warnings || [];
  const waybillBlockingTypes = new Set(['waybill_unmatched', 'waybill_bundle_missing']);
  const waybillWarningTypes = new Set(['tracking_duplicate_in_file', 'tracking_duplicate_history']);
  const inventoryBlockingIssues = allBlockingIssues.filter((it) => !waybillBlockingTypes.has(String(it.type || '')));
  const inventoryWarningIssues = allWarningIssues.filter((it) => !waybillWarningTypes.has(String(it.type || '')));
  const waybillBlockingIssues = allBlockingIssues.filter((it) => waybillBlockingTypes.has(String(it.type || '')));
  const waybillWarningIssues = allWarningIssues.filter((it) => waybillWarningTypes.has(String(it.type || '')));
  const hasPrecheckSystemError = !!String(precheckErrorText || '').trim();
  const unresolvedWaybillBlockingIssues = (scenario === 'upload_immediate' && waybillMatchPendingCount > 0)
    ? waybillBlockingIssues
    : [];
  const effectiveBlockingCount = inventoryBlockingIssues.length + unresolvedWaybillBlockingIssues.length;
  const canTriggerValidation = !!uploadedFile
    && headerCheck.status === 'ok'
    && (scenario !== 'upload_immediate' || waybillBundleFiles.length > 0);
  const filePrecheckHasError = headerCheck.status === 'missing'
    || headerCheck.status === 'error'
    || rowLimitExceeded
    || !!(validationSummary && validationSummary.invalidRows > 0);
  const waybillPrecheckHasError = scenario === 'upload_immediate'
    ? (waybillMatchPendingCount > 0 || hasPrecheckSystemError)
    : false;
  const inventoryPrecheckHasError = ((precheckResult?.inventory_summary || []).some((it) => Number(it.required_qty || 0) > Number(it.available_qty || 0))
    || inventoryBlockingIssues.some((it) => ['warehouse_not_found', 'project_missing', 'inventory_unit_not_found', 'inventory_unit_multiple', 'inventory_lookup_error', 'quantity_invalid', 'inventory_insufficient', 'insurance_amount_invalid', 'serial_no_missing', 'carrier_missing', 'tracking_no_missing'].includes(String(it.type || ''))));
  const outlinedGreenTagStyle: React.CSSProperties = { borderRadius: 999, paddingInline: 10, color: '#389e0d', borderColor: '#95de64', background: 'transparent' };
  const outlinedRedTagStyle: React.CSSProperties = { borderRadius: 999, paddingInline: 10, color: '#cf1322', borderColor: '#ff7875', background: 'transparent' };
  const renderPrecheckTabLabel = (title: string, hasError: boolean) => (
    <Space size={6}>
      <span>{title}</span>
      {hasError ? <CloseCircleFilled style={{ color: '#ff7875' }} /> : <CheckCircleFilled style={{ color: '#389e0d' }} />}
    </Space>
  );
  const getBlockingGroupLabel = (issueType: string) => {
    const t = String(issueType || '');
    if (['warehouse_not_found', 'project_missing', 'quantity_invalid', 'insurance_amount_invalid', 'serial_no_missing', 'carrier_missing', 'tracking_no_missing'].includes(t)) return '基础字段错误';
    if (['inventory_unit_not_found', 'inventory_unit_multiple', 'inventory_lookup_error', 'inventory_insufficient'].includes(t)) return '库存错误';
    if (['waybill_unmatched', 'waybill_bundle_missing'].includes(t)) return '运单匹配错误';
    return '其他错误';
  };
  const getWarningGroupLabel = (issueType: string) => {
    const t = String(issueType || '');
    if (['tracking_duplicate_in_file', 'tracking_duplicate_history'].includes(t)) return '物流重复警告';
    if (['waybill_fields_will_be_ignored'].includes(t)) return '场景规则警告';
    return '其他警告';
  };
  const groupIssueList = (
    list: Array<{ row_no?: number; type?: string; message?: string }>,
    getGroupLabel: (issueType: string) => string,
  ) => {
    const groupMap = new Map<string, Array<{ row_no?: number; type?: string; message?: string }>>();
    (list || []).forEach((item) => {
      const label = getGroupLabel(String(item?.type || ''));
      const arr = groupMap.get(label) || [];
      arr.push(item);
      groupMap.set(label, arr);
    });
    return Array.from(groupMap.entries());
  };
  const duplicateTrackingSerialsMap = useMemo(() => {
    const pairMap = new Map<string, { carrier: string; tracking: string; serials: Set<string> }>();
    (parsedRows || []).forEach((row) => {
      const carrier = String(getMappedValue(row, '运单所属物流承运商', fieldMapping) || '').trim();
      const tracking = String(getMappedValue(row, '物流跟踪号', fieldMapping) || '').trim();
      const serial = String(getMappedValue(row, '指令序号', fieldMapping) || '').trim();
      if (!carrier || !tracking || !serial) return;
      const key = `${carrier.toLowerCase()}||${tracking.toLowerCase()}`;
      const entry = pairMap.get(key) || { carrier, tracking, serials: new Set<string>() };
      entry.serials.add(serial);
      pairMap.set(key, entry);
    });
    return pairMap;
  }, [parsedRows, fieldMapping]);
  const parseCarrierAndTrackingFromWarning = (warningType: string, warningMessage: string) => {
    const msg = String(warningMessage || '').trim();
    if (warningType === 'tracking_duplicate_in_file') {
      const matched = msg.match(/同一物流信息在当前文件中重复：(.+?)\s*\/\s*(.+?)（出现\d+次）/);
      if (matched) {
        return { carrier: String(matched[1] || '').trim(), tracking: String(matched[2] || '').trim() };
      }
    }
    if (warningType === 'tracking_duplicate_history') {
      const matched = msg.match(/物流信息可能已在历史记录中使用：(.+?)\s*\/\s*(.+)$/);
      if (matched) {
        return { carrier: String(matched[1] || '').trim(), tracking: String(matched[2] || '').trim() };
      }
    }
    return { carrier: '', tracking: '' };
  };
  const formatBlockingIssueText = (item: { row_no?: number; type?: string; message?: string }) => {
    const issueType = String(item?.type || '');
    const rawMessage = String(item?.message || '').trim();
    if (issueType === 'waybill_unmatched') {
      const serialMatched = rawMessage.match(/序号([^）)]+)[）)]/);
      const serialNo = String(serialMatched?.[1] || '').trim();
      if (serialNo) {
        return `行${item?.row_no || '-'}(指令序号${serialNo})未匹配到运单附件.`;
      }
      return `行${item?.row_no || '-'}未匹配到运单附件.`;
    }
    if (issueType !== 'inventory_insufficient') {
      return `行${item?.row_no || '-'}：${rawMessage}`;
    }
    return rawMessage || '库存不足';
  };
  const formatWarningIssueText = (item: { row_no?: number; type?: string; message?: string }) => {
    const issueType = String(item?.type || '');
    const rawMessage = String(item?.message || '').trim();
    if (issueType === 'tracking_duplicate_in_file' || issueType === 'tracking_duplicate_history') {
      const { carrier, tracking } = parseCarrierAndTrackingFromWarning(issueType, rawMessage);
      if (carrier && tracking) {
        const key = `${carrier.toLowerCase()}||${tracking.toLowerCase()}`;
        const serials = Array.from((duplicateTrackingSerialsMap.get(key)?.serials || new Set<string>()).values());
        const serialText = serials.length > 0 ? serials.join(' ') : '-';
        return `同一物流信息(${carrier} | ${tracking}) 在 ${serials.length} 条记录(指令序号 ${serialText})中重复出现.`;
      }
      return rawMessage || '物流重复警告';
    }
    return `行${item?.row_no || '-'}：${rawMessage}`;
  };
  // 后端返回的错误文字可能是多行的（如库存单元预审：一行总述 + 每个不可用库存单元一行 "- 编码：原因"），
  // 直接放进 div 里换行会被折叠成一大段。这里把首行当总述、后面各行渲染成带缩进的列表项。
  const renderIssueText = (text: string) => {
    const lines = String(text || '').split('\n').map((l) => l.trimEnd()).filter((l) => l.trim());
    if (lines.length <= 1) return text;
    const [head, ...rest] = lines;
    return (
      <div>
        <div>{head}</div>
        {rest.map((line, idx) => {
          const isItem = /^-\s+/.test(line);
          return (
            <div
              key={`issue-line-${idx}`}
              style={{ marginLeft: isItem ? 20 : 0, marginTop: 2, paddingLeft: isItem ? 12 : 0, textIndent: isItem ? -12 : 0 }}
            >
              {isItem ? `• ${line.replace(/^-\s+/, '')}` : line}
            </div>
          );
        })}
      </div>
    );
  };
  const buildGroupedIssueText = (
    grouped: Array<[string, Array<{ row_no?: number; type?: string; message?: string }>]>,
    formatter: (item: { row_no?: number; type?: string; message?: string }) => string,
  ) => grouped.map(([groupLabel, items]) => {
    const lines = items.map((item) => formatter(item));
    return [`${groupLabel}（${items.length}）`, ...lines].join('\n');
  }).join('\n\n');
  const copyIssueText = async (text: string, successText: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(successText);
    } catch {
      message.error('复制失败，请稍后重试。');
    }
  };
  useEffect(() => {
    setInventoryTablePage(1);
  }, [precheckResult]);
  useEffect(() => {
    setInventoryStatsExpanded(false);
  }, [precheckResult, scenario]);
  useEffect(() => {
    setWaybillMatchTablePage(1);
  }, [waybillMatchGroups.length]);
  const precheckDisabledReason = (() => {
    if (!uploadedFile) return '请先上传订单文件后再预审';
    if (headerCheck.status === 'checking') return '正在执行列表文件表头校验，请稍候';
    if (headerCheck.status === 'missing') return '列表文件表头校验未通过，请先修正必填列表头';
    if (headerCheck.status === 'error') return '列表文件表头校验失败，请检查文件后重试';
    if (scenario === 'upload_immediate' && waybillBundleFiles.length === 0) return '请先上传订单文件和运单附件后再预审';
    return '';
  })();
  const openPrecheckGuideModal = () => {
    Modal.info({
      title: '预审说明',
      width: 860,
      okText: '知道了',
      content: (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Typography.Text strong style={{ fontSize: 15 }}>一、预审执行步骤</Typography.Text>
          <Typography.Text>1. 列表文件预审：校验表头、必填字段、行级规则与行数限制。</Typography.Text>
          <Typography.Text>2. 库存与业务预审：校验仓库/项目/库存单元可用性与业务规则。</Typography.Text>
          <Typography.Text>
            3. 运单附件预审（仅“由客户提供运单-立即上传”场景）：先自动匹配，再支持人工修正。
          </Typography.Text>

          <Divider style={{ margin: '4px 0' }} />

          <Typography.Text strong style={{ fontSize: 15 }}>二、运单附件匹配顺序</Typography.Text>
          <Typography.Text style={{ color: '#ad4e00' }}>
            仅“由客户提供运单-立即上传”场景需要用到本节内容——该场景下才会在创建时上传运单附件、
            才需要匹配。“先申请包装数据”“仓库提供运单”这两个场景不涉及运单附件匹配，可以跳过本节。
          </Typography.Text>
          <Typography.Text>
            先文件名匹配(使用列表文件中的"物流运单附件文件名"和上传的附件文件名执行匹配) →
            再智能匹配(对运单附件执行:PDF文本提取/条码解析/图像文本识别(OCR) 后 与列表文件中填写的物流跟踪号/收件人名/地址第一栏信息模糊匹配) →
            最后人工匹配。
          </Typography.Text>
          <Typography.Text style={{ color: '#ad4e00' }}>
            请尽量在列表文件中填写准确的“物流运单附件文件名”。文件名匹配速度最快、准确性最高；智能匹配耗时更长且准确性相对较低。
          </Typography.Text>
          <Typography.Text>如自动匹配结果不理想，可在“人工匹配”列手动修正。</Typography.Text>
          <Typography.Text strong style={{ color: '#cf1322' }}>
            进入下一步前，列表文件中的所有指令必须匹配一个运单附件，且一个运单附件只能匹配给一条指令记录。
          </Typography.Text>

          <Divider style={{ margin: '4px 0' }} />

          <Typography.Text strong style={{ fontSize: 15 }}>三、字段映射配置</Typography.Text>
          <Typography.Text>
            如果上传的文件不是标准模板格式（比如第三方系统直接导出的原始表格），可以通过“字段映射
            配置”，把系统需要的标准字段（仓库、项目、库存单元标识、发货数量、承运商、跟踪号等）
            对应到文件里实际的列名，不需要先手工整理成标准模板再上传。
          </Typography.Text>
          <Typography.Text>
            除了直接对应来源列，还支持几种取值方式：填一个固定值、按文件行号自动生成序号、把源
            文件里的原始取值换算成系统标准值（值映射）、按某一列的取值分组生成指令序号或反查所属
            项目（规则模式），用来覆盖各种非标准表格的情况。
          </Typography.Text>
        </Space>
      ),
    });
  };
  const filePrecheckPassed = headerCheck.status === 'ok'
    && !!validationSummary
    && !rowLimitExceeded
    && Number(validationSummary?.invalidRows || 0) === 0;
  const overallPrecheckPassed = filePrecheckPassed && effectiveBlockingCount === 0 && !hasPrecheckSystemError;
  const canProceedToExecute = hasValidated
    && headerCheck.status === 'ok'
    && !!validationSummary
    && !rowLimitExceeded
    && Number(validationSummary.invalidRows || 0) === 0
    && precheckDone
    && effectiveBlockingCount === 0
    && !hasPrecheckSystemError
    && (scenario !== 'upload_immediate' || waybillBundleFiles.length > 0)
    && (scenario !== 'upload_immediate' || waybillMatchPendingCount === 0);
  const canJumpToStep = (targetStep: number) => {
    if (targetStep <= currentStep) return true;
    if (targetStep === 1) return true;
    if (targetStep === 2) return canProceedToExecute;
    return false;
  };
  const quickFailedCount = Number(selectedSummary.failed_count || 0) || 0;
  const quickCreatedCount = Number(selectedSummary.created_count || 0) || 0;
  const quickSkippedCount = Number(selectedSummary.skipped_count || 0) || 0;
  const groupedExecutionStats = useMemo(() => {
    const toPositiveInt = (v: unknown) => {
      try {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) return Math.floor(n);
      } catch {}
      return 0;
    };
    const groupMap = new Map<string, { serialNo: string; rowNos: Set<number>; qty: number; qtyFromInventoryUnits: boolean }>();
    // 优先使用执行结果（jobItems）重建统计，保证切页返回后第3步可恢复。
    // 数量口径：优先用 meta.inventory_units——这是后端在库存单元解析成功后算出来的权威结果
    // （不管这一行最终是成功创建、命中幂等被跳过、还是库存单元解析成功之后才在别的环节失败，
    // 都会带这份数据），跟"产品统计"tab（currentInventoryUnitStats）用的是同一个数据源，
    // 两处不会对不上。它是整组共享的同一份结果（不是这一行独有的），所以每组只应该按第一次
    // 遇到时算一次，不能按行累加。
    // 只有库存单元解析还没跑到、就已经失败的行（比如缺必填字段、项目/仓库不存在）才没有这份
    // 权威数据，这种情况退回读后端按这一行字段映射/规则解析出的 meta.resolved_quantity——
    // 这个是按行独立的，需要按行累加，不再靠猜字面列名（发货数量/quantity/qty/expected_qty）
    // 或者读一个从来就不存在的 mapped_row.quantity。
    (jobItems || []).forEach((it) => {
      const rowNo = Number(it.row_no || 0);
      if (rowNo <= 0) return;
      const mapped = ((it.mapped_row || {}) as Record<string, unknown>);
      const raw = ((it as any)?.raw_row || {}) as Record<string, unknown>;
      const meta = ((it as any)?.meta || {}) as Record<string, unknown>;
      const serialNo = String(mapped.serial_no || raw['指令序号'] || '').trim();
      const groupKey = serialNo || `row-${rowNo}`;
      const entry = groupMap.get(groupKey) || { serialNo: serialNo || '-', rowNos: new Set<number>(), qty: 0, qtyFromInventoryUnits: false };
      entry.rowNos.add(rowNo);
      const units = Array.isArray(meta.inventory_units) ? (meta.inventory_units as Record<string, unknown>[]) : null;
      if (units) {
        if (!entry.qtyFromInventoryUnits) {
          entry.qty = units.reduce((sum, u) => sum + toPositiveInt((u as Record<string, unknown>).quantity), 0);
          entry.qtyFromInventoryUnits = true;
        }
      } else if (!entry.qtyFromInventoryUnits) {
        entry.qty += toPositiveInt(meta.resolved_quantity);
      }
      groupMap.set(groupKey, entry);
    });
    // 兜底：若当前尚无 jobItems，则回退用第2步解析数据。
    if (groupMap.size === 0) {
      (parsedRows || []).forEach((row, idx) => {
        const rowNo = Number(row.__sourceRowNo || idx + 2);
        const serialNo = String(getMappedValue(row, '指令序号', fieldMapping) || '').trim();
        const groupKey = serialNo || `row-${rowNo}`;
        const qtyRaw = String(getMappedValue(row, '发货数量', fieldMapping) || '').trim();
        const qty = toPositiveInt(qtyRaw);
        const entry = groupMap.get(groupKey) || { serialNo: serialNo || '-', rowNos: new Set<number>(), qty: 0, qtyFromInventoryUnits: false };
        entry.rowNos.add(rowNo);
        entry.qty += qty;
        groupMap.set(groupKey, entry);
      });
    }

    const createdRecords: ExecutionGroupRecord[] = [];
    const failedRecords: ExecutionGroupRecord[] = [];
    const skippedRecords: ExecutionGroupRecord[] = [];
    const total = {
      orders: groupMap.size,
      qty: Array.from(groupMap.values()).reduce((sum, it) => sum + Number(it.qty || 0), 0),
    };

    const itemByRowNo = new Map<number, { status: string; code: string; error: string }>();
    (jobItems || []).forEach((it) => {
      const rowNo = Number(it.row_no || 0);
      if (rowNo > 0) {
        itemByRowNo.set(rowNo, {
          status: String(it.status || '').toLowerCase(),
          code: String(it.created_instruction_system_code || '').trim(),
          error: String(it.error_message || '').trim(),
        });
      }
    });

    groupMap.forEach((g, groupKey) => {
      const statuses = Array.from(g.rowNos.values())
        .map((rowNo) => String((itemByRowNo.get(rowNo) || {}).status || '').toLowerCase())
        .filter(Boolean);
      if (statuses.length === 0) return;
      const stSet = new Set(statuses);
      const rowNos = Array.from(g.rowNos.values()).sort((a, b) => a - b);
      const createdCodes = Array.from(new Set(
        rowNos
          .map((rowNo) => String((itemByRowNo.get(rowNo) || {}).code || '').trim())
          .filter(Boolean),
      ));
      const errors = Array.from(new Set(
        rowNos
          .map((rowNo) => String((itemByRowNo.get(rowNo) || {}).error || '').trim())
          .filter(Boolean),
      ));
      const rec: ExecutionGroupRecord = {
        groupKey,
        serialNo: g.serialNo || '-',
        rowNos,
        qty: Number(g.qty || 0),
        createdCodes,
        errors,
      };
      if (stSet.has('created')) {
        createdRecords.push(rec);
        return;
      }
      if (stSet.has('failed')) {
        failedRecords.push(rec);
        return;
      }
      if (stSet.has('skipped')) {
        skippedRecords.push(rec);
      }
    });

    const sumQty = (records: ExecutionGroupRecord[]) => records.reduce((sum, it) => sum + Number(it.qty || 0), 0);
    if (groupMap.size === 0 && (quickCreatedCount > 0 || quickFailedCount > 0 || quickSkippedCount > 0)) {
      return {
        total: { orders: quickCreatedCount + quickFailedCount + quickSkippedCount, qty: 0 },
        created: { orders: quickCreatedCount, qty: 0, records: [] as ExecutionGroupRecord[] },
        failed: { orders: quickFailedCount, qty: 0, records: [] as ExecutionGroupRecord[] },
        skipped: { orders: quickSkippedCount, qty: 0, records: [] as ExecutionGroupRecord[] },
      };
    }
    return {
      total,
      created: { orders: createdRecords.length, qty: sumQty(createdRecords), records: createdRecords },
      failed: { orders: failedRecords.length, qty: sumQty(failedRecords), records: failedRecords },
      skipped: { orders: skippedRecords.length, qty: sumQty(skippedRecords), records: skippedRecords },
    };
  }, [parsedRows, fieldMapping, jobItems, quickCreatedCount, quickFailedCount, quickSkippedCount]);
  const createdJobItems = groupedExecutionStats.created.records;
  const failedJobItems = groupedExecutionStats.failed.records;
  const skippedJobItems = groupedExecutionStats.skipped.records;
  const currentExecutionDetail = useMemo(() => {
    if (executionListView === 'created') {
      return {
        title: '已创建记录',
        records: createdJobItems,
        reasonColumnTitle: '已创建指令系统编码',
        reasonText: (it: ExecutionGroupRecord) => it.createdCodes.join(' / ') || '-',
      };
    }
    if (executionListView === 'failed') {
      return {
        title: '失败记录',
        records: failedJobItems,
        reasonColumnTitle: '失败原因',
        reasonText: (it: ExecutionGroupRecord) => it.errors.join('；') || '创建失败',
      };
    }
    if (executionListView === 'skipped') {
      return {
        title: '跳过记录',
        records: skippedJobItems,
        reasonColumnTitle: '跳过原因',
        reasonText: (_it: ExecutionGroupRecord) => '已存在关键信息相同(如物流跟踪号-商品信息-参考号 均相同)的代发指令,已跳过重复创建;仅适用于"由客户提供运单-立即上传"场景',
      };
    }
    return null;
  }, [executionListView, createdJobItems, failedJobItems, skippedJobItems]);
  const currentExecutionClassStats = useMemo(() => {
    const records = currentExecutionDetail?.records || [];
    const result = {
      singleSingle: { orders: 0, qty: 0 },
      singleMulti: { orders: 0, qty: 0 },
      multiMulti: { orders: 0, qty: 0 },
    };
    records.forEach((it) => {
      const skuCount = Math.max(1, Number((it.rowNos || []).length || 0));
      const qty = Math.max(0, Number(it.qty || 0));
      if (skuCount === 1 && qty <= 1) {
        result.singleSingle.orders += 1;
        result.singleSingle.qty += qty;
        return;
      }
      if (skuCount === 1) {
        result.singleMulti.orders += 1;
        result.singleMulti.qty += qty;
        return;
      }
      result.multiMulti.orders += 1;
      result.multiMulti.qty += qty;
    });
    return result;
  }, [currentExecutionDetail]);
  const currentInventoryUnitStats = useMemo(() => {
    type UnitStat = {
      inventoryUnitId: number | null;
      inventoryUnitCode: string;
      warehouseName: string;
      projectName: string;
      inventoryUnitTagDisplay: string;
      availableQtyBefore: number | null;
      totalQty: number;
    };
    const records = currentExecutionDetail?.records || [];
    const itemByRowNo = new Map<number, InstructionImportJobItem>();
    (jobItems || []).forEach((it) => {
      const rowNo = Number(it.row_no || 0);
      if (rowNo > 0) itemByRowNo.set(rowNo, it);
    });
    const toPositiveInt = (v: unknown) => {
      try {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) return Math.floor(n);
      } catch {}
      return 0;
    };
    // 按库存单元真实身份（inventory_unit_id）聚合，不按客户在列表文件里填写的
    // 库存单元标识文本分组——同一个库存单元可能被客户填成不同的标识（如 BC1/BC2），
    // 解析后其实是同一个库存单元，这里只应统计一次。
    const agg = new Map<string, UnitStat>();
    const bump = (key: string, base: Omit<UnitStat, 'totalQty'>, qty: number) => {
      const prev = agg.get(key) || { ...base, totalQty: 0 };
      prev.totalQty += qty;
      agg.set(key, prev);
    };
    records.forEach((rec) => {
      const rowNos = rec.rowNos || [];
      // 同一个订单组（一个"指令序号"）落库时，组内每一行的 meta.inventory_units
      // 存的是整组共享的同一份解析结果（并非每行各自一份），所以只能取组内第一行
      // 处理一次；如果按行遍历会把同一份数据重复累加到组内行数那么多倍。
      const firstItem = rowNos.length > 0 ? itemByRowNo.get(rowNos[0]) : undefined;
      const meta = (firstItem?.meta || {}) as Record<string, unknown>;
      const units = Array.isArray(meta.inventory_units) ? (meta.inventory_units as Record<string, unknown>[]) : [];
      if (units.length > 0) {
        units.forEach((u) => {
          const inventoryUnitId = toPositiveInt(u.inventory_unit_id) || null;
          const projectCode = String(u.project_code || '').trim();
          const key = inventoryUnitId ? `id-${inventoryUnitId}` : `tag-${projectCode}-${String(u.inventory_unit_tag_display || '')}`;
          // available_qty_before 只在这个库存单元第一次被 bump（也就是第一次在本次
          // 任务里遇到）时通过 base 记录下来；之后同一个单元再被别的订单组引用，只会
          // 累加 totalQty，不会覆盖这个值——保持“本次任务开始动它之前”的基准不变。
          const availableQtyBeforeRaw = u.available_qty_before;
          const availableQtyBefore = availableQtyBeforeRaw === null || availableQtyBeforeRaw === undefined
            ? null
            : toPositiveInt(availableQtyBeforeRaw) || Number(availableQtyBeforeRaw) || 0;
          bump(key, {
            inventoryUnitId,
            inventoryUnitCode: String(u.inventory_unit_code || '').trim() || '-',
            warehouseName: String(u.warehouse_name || u.warehouse_code || '-').trim() || '-',
            projectName: String(u.project_name || projectCode || '-').trim() || '-',
            inventoryUnitTagDisplay: String(u.inventory_unit_tag_display || '').trim() || '-',
            availableQtyBefore,
          }, toPositiveInt(u.quantity));
        });
        return;
      }
      // 退化：未解析成功的失败/跳过记录，没有 meta.inventory_units（本来就没能解析出
      // 真实库存单元）。这种情况下原始 raw_row 在组内每一行确实各不相同（不同SKU的行），
      // 所以仍需按行遍历兜底展示，无法去重合并同一库存单元的不同填写方式。
      rowNos.forEach((rowNo) => {
        const item = itemByRowNo.get(rowNo);
        if (!item) return;
        const raw = ((item as any)?.raw_row || {}) as Record<string, unknown>;
        const projectCode = String(raw['项目'] || '').trim();
        const warehouseCode = String(raw['仓库'] || '').trim();
        const inventoryUnitTag = String(raw['库存单元标识'] || '').trim() || '-';
        const qty = toPositiveInt(raw['发货数量']);
        const key = `tag-${projectCode}-${inventoryUnitTag}`;
        bump(key, {
          inventoryUnitId: null,
          inventoryUnitCode: '-',
          warehouseName: warehouseCode || '-',
          projectName: projectCode || '-',
          inventoryUnitTagDisplay: inventoryUnitTag,
          availableQtyBefore: null,
        }, qty);
      });
    });
    return Array.from(agg.values()).sort((a, b) => b.totalQty - a.totalQty);
  }, [currentExecutionDetail, jobItems]);
  const downloadInventoryUnitStats = async () => {
    if (currentInventoryUnitStats.length === 0) return;
    const ExcelJSImport = await import('exceljs');
    const ExcelJS: any = (ExcelJSImport as any)?.default || ExcelJSImport;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('产品统计');
    const headers = ['仓库', '所属项目', '库存单元系统编码', '库存单元标识', '本次创建总数量', '本次创建后可用库存'];
    sheet.addRow(headers);
    currentInventoryUnitStats.forEach((it) => {
      const afterQty = it.availableQtyBefore === null ? '-' : it.availableQtyBefore - it.totalQty;
      sheet.addRow([it.warehouseName, it.projectName, it.inventoryUnitCode, it.inventoryUnitTagDisplay, it.totalQty, afterQty]);
    });
    headers.forEach((header, idx) => {
      const col = sheet.getColumn(idx + 1);
      col.width = Math.max(18, header.length + 8);
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `产品统计_${stamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  const currentExecutionDetailTheme = useMemo(() => {
    if (executionListView === 'created') {
      return { bg: '#f6ffed', border: '#95de64', text: '#389e0d' };
    }
    if (executionListView === 'failed') {
      return { bg: '#fff1f0', border: '#ff7875', text: '#cf1322' };
    }
    if (executionListView === 'skipped') {
      return { bg: '#fff7e6', border: '#ffd591', text: '#d46b08' };
    }
    return { bg: '#fafafa', border: '#d9d9d9', text: '#595959' };
  }, [executionListView]);
  const downloadExecutionDetailRecords = () => {
    if (!currentExecutionDetail || currentExecutionDetail.records.length <= 0) return;
    const escapeCsv = (v: unknown) => {
      const text = String(v ?? '');
      if (text.includes('"') || text.includes(',') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };
    const header = ['指令序号', '涉及行号', currentExecutionDetail.reasonColumnTitle];
    const rows = currentExecutionDetail.records.map((it) => ([
      it.serialNo,
      it.rowNos.join(','),
      currentExecutionDetail.reasonText(it),
    ]));
    const content = [header, ...rows].map((cols) => cols.map((col) => escapeCsv(col)).join(',')).join('\n');
    const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `${currentExecutionDetail.title}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };
  const copyExecutionDetailStats = async () => {
    if (!currentExecutionDetail) return;
    const lines = [
      `${currentExecutionDetail.title}分类统计`,
      `单款单件 ${currentExecutionClassStats.singleSingle.orders}条记录 | ${currentExecutionClassStats.singleSingle.qty}件产品`,
      `单款多件 ${currentExecutionClassStats.singleMulti.orders}条记录 | ${currentExecutionClassStats.singleMulti.qty}件产品`,
      `多款多件 ${currentExecutionClassStats.multiMulti.orders}条记录 | ${currentExecutionClassStats.multiMulti.qty}件产品`,
    ];
    await copyIssueText(lines.join('\n'), '统计结果已复制');
  };
  const executionRunning = submittingJob || executingJob || ['running', 'queued'].includes(String(selectedJob?.status || '').toLowerCase());
  const stepCtaButtonStyle: React.CSSProperties = {
    minWidth: 190,
    height: 44,
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    boxShadow: '0 6px 14px rgba(22, 119, 255, 0.22)',
  };
  const stepBackButtonStyle: React.CSSProperties = {
    minWidth: 130,
    height: 44,
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    borderColor: '#91caff',
    color: '#1d39c4',
    background: '#f0f5ff',
  };
  const formatEta = (sec: number) => {
    if (!Number.isFinite(sec) || sec < 0) return '-';
    if (sec < 60) return `${Math.max(0, Math.round(sec))}秒`;
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    if (m < 60) return `${m}分${s}秒`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}小时${mm}分`;
  };

  useEffect(() => {
    if (!hasRunningOrQueuedJob) return;
    const timer = window.setInterval(() => {
      loadJobs(scenario);
      if (activeJobId) loadJobItems(activeJobId);
    }, 5000);
    return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRunningOrQueuedJob, scenario, activeJobId]);
  useEffect(() => {
    if (executionRunning) {
      setExecutionListView(null);
    }
  }, [executionRunning]);
  useEffect(() => {
    const pending = pendingExecutionUiRestoreRef.current;
    if (pending) {
      pendingExecutionUiRestoreRef.current = null;
      setExecutionDetailTabKey(pending.tabKey);
      setExecutionDetailListPage(pending.listPage);
      setExecutionDetailListPageSize(pending.listPageSize);
      setProductStatsPage(pending.productStatsPage);
      setProductStatsPageSize(pending.productStatsPageSize);
      return;
    }
    setExecutionDetailTabKey('list');
    setExecutionDetailListPage(1);
    setExecutionDetailListPageSize(10);
  }, [executionListView]);

  // 管理员从管理员入口打开这个页面时，第一步必须先选定"代为操作的客户"——选定之前，
  // 场景选择/上传文件/字段映射配置这些正式内容一律不展示，避免任何一个入口（哪怕是
  // 以后新增的）绕开这一步、在没有明确客户身份的情况下就动了数据。选定后这个 id 在
  // 整个页面生命周期内保持不变，后续所有涉及 tenant 的操作都读这一个页面级别的状态，
  // 不需要每个功能入口各自重新判断一次。客户账号自己登录，isAdminContainer 恒为
  // false，这个早退分支永远不会命中，完全不受影响。这个 return 必须放在所有 Hook
  // 调用之后（React Hooks 规则），只影响这一次渲染输出什么，不影响 Hook 的调用顺序。
  if (isAdminContainer && !adminActingCustomerId) {
    return (
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Typography.Title level={4} style={{ marginBottom: 8 }}>
          Excel/CSV 导入创建一件代发指令
        </Typography.Title>
        <Card title="请先选择要代为操作的客户">
          <Typography.Paragraph type="secondary">
            管理员账号需要先选定要代为操作的客户，之后在这个页面里新建的映射配置、创建的
            导入任务，都会记在这个客户名下，以后该客户自己登录即可直接使用/看到。
          </Typography.Paragraph>
          <Select
            style={{ width: 360 }}
            showSearch
            allowClear
            placeholder="选择客户"
            loading={adminCustomerLoading}
            options={adminCustomerOptions}
            optionFilterProp="label"
            value={adminActingCustomerId}
            onFocus={ensureAdminCustomers}
            onChange={(cid?: number) => setAdminActingCustomerId(cid || undefined)}
          />
        </Card>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <Typography.Title level={4} style={{ marginBottom: 8 }}>
            Excel/CSV 导入创建一件代发指令
          </Typography.Title>
          <Typography.Text type="secondary">
            按步骤完成：选择场景与模板 → 上传并校验 → 开始批量创建。
          </Typography.Text>
        </div>
        <Space>
          <Button onClick={() => navigate(bulkCreatePath)}>返回批量创建页面</Button>
          <Button
            danger
            onClick={() => {
              Modal.confirm({
                title: '开始新的导入？',
                content: '将清空当前导入会话缓存并回到步骤1，是否继续？',
                okText: '确认清空',
                cancelText: '取消',
                okButtonProps: { danger: true },
                onOk: clearImportSession,
              });
            }}
          >
            开始新的导入
          </Button>
        </Space>
      </div>

      <Steps
        current={currentStep}
        onChange={(next) => {
          if (canJumpToStep(next)) {
            setCurrentStep(next);
          } else {
            message.warning('请先完成前置步骤后再跳转。');
          }
        }}
        items={[
          { title: '选择场景与模板' },
          { title: '上传与校验' },
          { title: '执行与结果' },
        ]}
      />

      {currentStep === 0 && (
      <Card title="步骤1：选择场景与下载模板">
        <Tabs
          activeKey={scenario}
          onChange={handleScenarioChange}
          items={SCENARIO_KEYS.map((key) => ({
            key,
            label: SCENARIO_META[key].label,
          }))}
        />

        <Card
          size="small"
          style={{ marginBottom: 10, borderColor: '#b7eb8f', background: '#f6ffed' }}
          title={(
            <Space size={6}>
              <Typography.Text strong style={{ color: '#237804' }}>适用场景</Typography.Text>
              <Tag color="green">{SCENARIO_META[scenario].label}</Tag>
            </Space>
          )}
        >
          <Typography.Text style={{ color: '#135200' }}>
            {displayScenarioUsageText}
          </Typography.Text>
        </Card>

        <Card
          size="small"
          style={{ marginBottom: 10, borderColor: '#ffd591', background: '#fff7e6' }}
          title={(
            <Space size={6}>
              <Typography.Text strong style={{ color: '#ad4e00' }}>必填字段与附件</Typography.Text>
              <Tag color="orange">{displayRequiredFields.length}</Tag>
              <Button type="link" size="small" onClick={() => setRequiredExpanded((prev) => !prev)}>
                {requiredExpanded ? '收起' : '展开'}
              </Button>
            </Space>
          )}
        >
          {requiredExpanded && (
            <Space size={[8, 8]} wrap>
              {displayRequiredFields.map((field) => (
                <Tag key={`required-${field}`} color="red" style={{ paddingInline: 10, borderRadius: 999 }}>
                  {field}
                </Tag>
              ))}
            </Space>
          )}
        </Card>
        <Card
          size="small"
          style={{ marginBottom: 14, borderColor: '#d6e4ff', background: '#f0f5ff' }}
          title={(
            <Space size={6}>
              <Typography.Text strong style={{ color: '#1d39c4' }}>非必填字段与附件（可留空）</Typography.Text>
              <Tag color="blue">{displayOptionalFields.length}</Tag>
              <Button type="link" size="small" onClick={() => setOptionalExpanded((prev) => !prev)}>
                {optionalExpanded ? '收起' : '展开'}
              </Button>
            </Space>
          )}
        >
          {optionalExpanded && (
            <Space size={[8, 8]} wrap>
              {displayOptionalFields.map((field) => (
                <Tag key={`optional-${field}`} color="blue" style={{ paddingInline: 10, borderRadius: 999 }}>
                  {field}
                </Tag>
              ))}
            </Space>
          )}
        </Card>

        <Space wrap>
          <Button icon={<DownloadOutlined />} loading={downloadingTemplate} onClick={handleDownloadTemplate}>
            下载当前场景模板
          </Button>
        </Space>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              background: '#fff566',
              border: '1px solid #ffd666',
              borderRadius: 8,
              padding: '8px 12px',
              color: '#cf1322',
              fontWeight: 700,
            }}
          >
            已选择场景：{SCENARIO_META[scenario].label}
          </div>
          <Button type="primary" size="large" style={stepCtaButtonStyle} onClick={() => setCurrentStep(1)}>
            下一步：上传文件
          </Button>
        </div>
      </Card>
      )}

      {ADVANCED_MAPPING_ENABLED && (
      <Drawer
        title={mappingDrawerView === 'list'
          ? '字段映射配置'
          : (activeProfileId ? `编辑映射配置：${profileNameInput || ''}` : '新建映射配置')}
        open={mappingDrawerOpen}
        onClose={() => setMappingDrawerOpen(false)}
        width={880}
        destroyOnClose={false}
      >
        {mappingDrawerView === 'list' && (
          <>
            <Typography.Paragraph type="secondary">
              用于直接上传客户自己 ERP/聚合系统导出的源文件（如领星），不需要每次手动把数据搬到标准模板里——
              配置一次映射规则并保存，以后同一来源的文件可以直接复用。当前场景：
              <Tag color="blue">{SCENARIO_META[scenario].label}</Tag>
            </Typography.Paragraph>
            {isAdminContainer && (
              <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                当前代操作客户：
                <Tag color="blue">
                  {adminCustomerOptions.find((o) => o.value === adminActingCustomerId)?.label || adminActingCustomerId}
                </Tag>
                <Button type="link" size="small" onClick={() => setAdminActingCustomerId(undefined)}>
                  切换客户
                </Button>
              </Typography.Paragraph>
            )}
            <div style={{ marginBottom: 12 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNewProfile}>
                新建映射配置
              </Button>
            </div>
            {scenarioProfiles.length === 0 ? (
              <Empty description="该场景下暂无已保存的映射配置" />
            ) : (
              <List
                dataSource={scenarioProfiles}
                rowKey={(p) => String(p.id)}
                renderItem={(profile) => (
                  <List.Item
                    actions={[
                      <Button key="apply" size="small" onClick={() => handleApplyProfileFromList(profile.id)}>
                        应用到当前上传
                      </Button>,
                      <Button key="edit" size="small" icon={<EditOutlined />} onClick={() => handleEditProfile(profile.id)}>
                        编辑
                      </Button>,
                      <Popconfirm
                        key="delete"
                        title="确认删除这份映射配置？"
                        description="删除后无法恢复，已经用它创建过的指令不受影响。"
                        okText="确认删除"
                        okButtonProps={{ danger: true }}
                        cancelText="取消"
                        onConfirm={() => handleDeleteProfile(profile.id)}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                      </Popconfirm>,
                    ]}
                  >
                    <List.Item.Meta
                      title={profile.name}
                      description={`更新于 ${new Date(String(profile.updated_at || '')).toLocaleString()}`}
                    />
                  </List.Item>
                )}
              />
            )}
          </>
        )}

        {mappingDrawerView === 'editor' && (
          <>
            <Space wrap style={{ width: '100%', marginBottom: 12 }}>
              <Button onClick={() => setMappingDrawerView('list')}>返回列表</Button>
              <Button onClick={applyAutoSameNameMapping}>按同名字段自动映射</Button>
              <Button size="small" onClick={() => setMappingHelpExpanded((prev) => !prev)}>
                {mappingHelpExpanded ? '收起说明' : '展开说明'}
              </Button>
            </Space>
            {mappingHelpExpanded && (
              <>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 4 }}>
                  下表里的“标准字段”是系统创建指令要用到的字段（如“项目”“仓库”“库存单元标识”等）；默认按“来源列”直接取值，即要求源文件里存在与标准字段同名的列，这跟标准模板导入完全一致——“按同名字段自动映射”会把源文件表头里名称跟标准字段完全相同的列自动关联起来，省去逐个手动选择。以下都是可选的高级能力，用于源文件表头跟标准字段对不上、需要直接上传客户自己ERP导出的源文件的场景：
                </Typography.Paragraph>
                <ul style={{ color: 'rgba(0, 0, 0, 0.45)', paddingLeft: 20, marginTop: 0, marginBottom: 12 }}>
                  <li>
                    <b>来源列</b>：直接使用文件里某一列的原始值。例：文件里有一列“Order Notes”，想填入系统“买家备注”字段，来源列选“Order Notes”即可。
                  </li>
                  <li>
                    <b>值映射</b>：来源列存在，但取值需要翻译成系统认识的标准值。例：来源文件“仓库”列写的是“美东仓”，系统对应的仓库编码是“US-1-NH”，配置一条“美东仓 → US-1-NH”的对照。
                  </li>
                  <li>
                    <b>固定值</b>：该字段在源文件里不存在，但对这份文件来说取值永远相同。例：这份源文件的所有记录都属于同一个项目“26061”，把“所属项目”直接设为固定值“26061”，不需要再从源文件中映射。
                  </li>
                  <li>
                    <b>自动生成</b>（仅“指令序号”可选）：源文件没有任何单号概念，且一行就是一个独立指令，按文件行号直接生成序号。例：文件每一行是一票独立订单，没有单号列，选自动生成后第2行生成序号2、第3行生成序号3，依次类推。
                  </li>
                  <li>
                    <b>按规则映射</b>：需要一段固定算法才能算出结果的场景，目前有两条：
                    <ul style={{ paddingLeft: 18, marginTop: 4, marginBottom: 0 }}>
                      <li>
                        <b>按取值分组生成序号</b>（仅“指令序号”可选）：源文件里有一列能代表“哪些行属于同一个订单”（如出库单号），但这一列不是纯数字、不能直接当指令序号用；这条规则会按这一列的取值自动分组，取值相同的行会生成相同的（纯数字）指令序号。如果同一个订单在文件里往往只有第一行写了单号、后面几行这一列是空的，可以勾选“空值沿用上一行的取值”。例：“出库单号”列依次显示 WO1001、WO1001、WO2002，选来源列“出库单号”后，会自动生成指令序号 1、1、2——表示前两行是同一个指令的两款产品，第三行是另一个独立指令。
                      </li>
                      <li>
                        <b>按库存单元反查项目</b>（仅“项目”可选）：源文件没有“项目”这一列，但有一列可以唯一定位到某个产品（如产品条码）；这条规则会用这一列的取值去查这个产品在系统里归属哪个项目，唯一匹配就自动采用。如果同一个条码在这个客户名下被多个不同项目使用，会转成需要人工确认——点旁边的“设置项目归属”按钮，为有歧义的取值手动选一个项目，选过的会保存进这份配置，以后同样的取值不会再问。例：“产品条码”列的值是“ABC-123”，系统里只有项目“26061”下有这个条码，会自动把“项目”设为“26061”；如果“26060”和“26061”两个项目都用了这个条码，就需要手动选一个。
                      </li>
                    </ul>
                  </li>
                  <li>
                    <b>数值清洗</b>：用于去除货币符号等干扰字符，可以和“来源列”“值映射”叠加使用；“固定值”“自动生成”“按规则映射”产出的已经是确定值，不支持叠加数值清洗。
                  </li>
                </ul>
              </>
            )}
            {parsedHeaders.length === 0 && (
              <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                “来源列”下拉和“值映射”里的取值列表、以及“按规则映射”的来源列选项，需要先在步骤2上传一份示例文件才能看到。
              </Typography.Paragraph>
            )}
            <Table
              size="small"
              pagination={false}
              rowKey={(row) => String(row.standardField)}
              dataSource={allScenarioFields.map((f) => ({ standardField: f, required: getScenarioValidationRequiredFields(scenario).includes(f) }))}
              columns={[
                { title: '标准字段', dataIndex: 'standardField', key: 'standardField', width: 140 },
                {
                  title: '是否必填',
                  dataIndex: 'required',
                  key: 'required',
                  width: 80,
                  render: (required: boolean) => (
                    <Tag color={required ? 'red' : 'green'}>{required ? '是' : '否'}</Tag>
                  ),
                },
                {
                  title: '映射方式',
                  key: 'mode',
                  width: 130,
                  render: (_, row: { standardField: string }) => {
                    const modeCfg = (transformRules.field_modes || {})[row.standardField];
                    const modeValue: 'column' | 'constant' | 'auto_serial' | 'value_map' | 'rule' = modeCfg?.mode || 'column';
                    const applicableRules = getApplicableRuleTypes(row.standardField);
                    const options: Array<{ label: string; value: 'column' | 'constant' | 'auto_serial' | 'value_map' | 'rule' }> = [
                      { label: '来源列', value: 'column' },
                      { label: '值映射', value: 'value_map' },
                      { label: '固定值', value: 'constant' },
                      ...(row.standardField === '指令序号' ? [{ label: '自动生成', value: 'auto_serial' as const }] : []),
                      ...(applicableRules.length > 0 ? [{ label: '按规则映射', value: 'rule' as const }] : []),
                    ];
                    return (
                      <Select
                        style={{ width: 110 }}
                        value={modeValue}
                        options={options}
                        onChange={(value: 'column' | 'constant' | 'auto_serial' | 'value_map' | 'rule') => {
                          if (value === 'column') setFieldMode(row.standardField, undefined);
                          else if (value === 'constant') setFieldMode(row.standardField, { mode: 'constant', value: '' });
                          else if (value === 'auto_serial') setFieldMode(row.standardField, { mode: 'auto_serial' });
                          else if (value === 'value_map') setFieldMode(row.standardField, { mode: 'value_map', valueMap: {} });
                          else if (value === 'rule') {
                            const defaultRuleType = applicableRules[0]?.id || 'group_by_column';
                            setFieldMode(row.standardField, buildDefaultRuleModeConfig(defaultRuleType));
                          }
                        }}
                      />
                    );
                  },
                },
                {
                  title: '来源列 / 固定值 / 规则参数',
                  key: 'mapping',
                  render: (_, row: { standardField: string }) => {
                    const modeCfg = (transformRules.field_modes || {})[row.standardField];
                    if (modeCfg?.mode === 'rule') {
                      const applicableRules = getApplicableRuleTypes(row.standardField);
                      const sourceColumn = modeCfg.params?.sourceColumn || '';
                      const handleSourceColumnChange = (value?: string) => {
                        if (modeCfg.ruleType === 'lookup_project') {
                          setFieldMode(row.standardField, {
                            mode: 'rule',
                            ruleType: 'lookup_project',
                            params: { ...modeCfg.params, sourceColumn: value || '' },
                          });
                        } else {
                          setFieldMode(row.standardField, {
                            mode: 'rule',
                            ruleType: 'group_by_column',
                            params: { ...modeCfg.params, sourceColumn: value || '' },
                          });
                        }
                      };
                      return (
                        <Space direction="vertical" size={4} style={{ minWidth: 240 }}>
                          {applicableRules.length > 1 && (
                            <Select
                              size="small"
                              style={{ width: '100%' }}
                              value={modeCfg.ruleType}
                              options={applicableRules.map((r) => ({ label: r.label, value: r.id }))}
                              onChange={(ruleType) => setFieldMode(row.standardField, buildDefaultRuleModeConfig(ruleType))}
                            />
                          )}
                          <Select
                            allowClear
                            showSearch
                            size="small"
                            style={{ width: '100%' }}
                            placeholder={parsedHeaders.length > 0 ? '选择来源列' : '请先上传文件'}
                            value={sourceColumn || undefined}
                            options={parsedHeaders.map((h) => ({ label: h, value: h }))}
                            onChange={handleSourceColumnChange}
                          />
                          {modeCfg.ruleType === 'group_by_column' && (
                            <Checkbox
                              checked={!!modeCfg.params.forwardFill}
                              onChange={(e) => setFieldMode(row.standardField, {
                                mode: 'rule',
                                ruleType: 'group_by_column',
                                params: { ...modeCfg.params, forwardFill: e.target.checked },
                              })}
                            >
                              空值沿用上一行的取值
                            </Checkbox>
                          )}
                          {modeCfg.ruleType === 'lookup_project' && (() => {
                            const values = lookupProjectFieldSourceValues[row.standardField] || [];
                            const overrides = modeCfg.params.manualOverrides || {};
                            const ambiguousCount = values.filter((v) => {
                              if (overrides[v]) return false;
                              const r = projectLookupResults[v];
                              return r?.status === 'ambiguous';
                            }).length;
                            return (
                              <Button
                                size="small"
                                danger={ambiguousCount > 0}
                                loading={projectLookupLoading}
                                onClick={() => setProjectLookupModalField(row.standardField)}
                              >
                                设置项目归属{ambiguousCount > 0 ? `（${ambiguousCount}项待确认）` : ''}
                              </Button>
                            );
                          })()}
                        </Space>
                      );
                    }
                    if (modeCfg?.mode === 'constant') {
                      const constantOptionKey: Partial<Record<string, keyof TemplateDropdownOptions>> = {
                        '仓库': 'warehouseOptions',
                        '项目': 'projectOptions',
                        '运单所属物流承运商': 'providerOptions',
                        '偏好物流承运商': 'providerOptions',
                        '国家': 'countryOptions',
                      };
                      const optionKey = constantOptionKey[row.standardField];
                      if (optionKey) {
                        const candidates = constantDropdownOptions?.[optionKey] || [];
                        return (
                          <AutoComplete
                            style={{ minWidth: 220 }}
                            placeholder="输入固定值，或从候选列表中选择"
                            value={modeCfg.value}
                            options={candidates.map((o) => ({ value: o }))}
                            filterOption={(input, option) => String(option?.value || '').toLowerCase().includes(input.toLowerCase())}
                            onChange={(value) => setFieldMode(row.standardField, { mode: 'constant', value })}
                          />
                        );
                      }
                      return (
                        <Input
                          style={{ minWidth: 220 }}
                          placeholder="输入固定值"
                          value={modeCfg.value}
                          onChange={(e) => setFieldMode(row.standardField, { mode: 'constant', value: e.target.value })}
                        />
                      );
                    }
                    if (modeCfg?.mode === 'auto_serial') {
                      return <Typography.Text type="secondary">按文件行号自动生成，无需选择来源列</Typography.Text>;
                    }
                    return (
                      <Select
                        allowClear
                        showSearch
                        style={{ minWidth: 220 }}
                        placeholder={parsedHeaders.length > 0 ? '选择来源列' : '请先上传文件'}
                        value={fieldMapping[row.standardField]}
                        onChange={(value) => {
                          setFieldMapping((prev) => ({ ...prev, [row.standardField]: value || '' }));
                          setHasValidated(false);
                          setValidationSummary(null);
                        }}
                        options={parsedHeaders.map((h) => ({ label: h, value: h }))}
                      />
                    );
                  },
                },
                {
                  title: '值映射',
                  key: 'valueMap',
                  width: 150,
                  render: (_, row: { standardField: string }) => {
                    const modeCfg = (transformRules.field_modes || {})[row.standardField];
                    if (modeCfg?.mode !== 'value_map') return null;
                    const count = Object.keys(modeCfg.valueMap || {}).length;
                    return (
                      <Button size="small" onClick={() => openValueMapModal(row.standardField)}>
                        设置对照{count > 0 ? `（已配置${count}项）` : ''}
                      </Button>
                    );
                  },
                },
                {
                  title: '数值清洗',
                  key: 'transform',
                  width: 130,
                  render: (_, row: { standardField: string }) => {
                    // 固定值/自动生成/规则算出来的已经是结构化的确定值，不应该再叠加字符串
                    // 清洗规则，这里直接不展示这一列的控件，避免配置了也不会生效造成误解。
                    const modeCfg = (transformRules.field_modes || {})[row.standardField];
                    if (modeCfg && ['rule', 'constant', 'auto_serial'].includes(modeCfg.mode)) {
                      return <Typography.Text type="secondary">不适用</Typography.Text>;
                    }
                    return (
                      <Select
                        allowClear
                        style={{ width: 120 }}
                        placeholder="不清洗"
                        value={(transformRules.field_transforms || {})[row.standardField]}
                        onChange={(value) => setFieldTransform(row.standardField, value || undefined)}
                        options={[
                          { label: '去货币符号取数字', value: 'strip_currency' },
                          { label: '去首尾空白', value: 'trim' },
                          { label: '只保留数字', value: 'digits_only' },
                          { label: '转大写', value: 'uppercase' },
                        ]}
                      />
                    );
                  },
                },
              ]}
            />
            <Space wrap style={{ width: '100%', marginTop: 16 }}>
              <Button
                type="primary"
                loading={savingProfile}
                onClick={() => { setSaveNameModalMode('save'); setSaveNameModalOpen(true); }}
              >
                {activeProfileId ? '保存修改' : '保存映射配置'}
              </Button>
              {activeProfileId && (
                <Button
                  loading={savingProfile}
                  onClick={() => { setSaveNameModalMode('saveAs'); setSaveNameModalOpen(true); }}
                >
                  另存为新配置
                </Button>
              )}
            </Space>
          </>
        )}
      </Drawer>
      )}

      {ADVANCED_MAPPING_ENABLED && (
      <Modal
        title={saveNameModalMode === 'saveAs' ? '另存为新配置' : (activeProfileId ? '保存映射配置修改' : '保存映射配置')}
        open={saveNameModalOpen}
        onCancel={() => setSaveNameModalOpen(false)}
        onOk={saveNameModalMode === 'saveAs' ? handleSaveAsNewProfile : handleSaveMappingProfile}
        confirmLoading={savingProfile}
        okText="确认保存"
        cancelText="取消"
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          {saveNameModalMode === 'saveAs'
            ? '将在当前映射规则基础上另存为一份新的配置，名称需要和已有配置不同。'
            : '为这份映射配置命名，方便以后在列表中找到并复用它。'}
        </Typography.Paragraph>
        <Input
          placeholder="输入映射配置名称"
          value={profileNameInput}
          onChange={(e) => setProfileNameInput(e.target.value)}
          onPressEnter={saveNameModalMode === 'saveAs' ? handleSaveAsNewProfile : handleSaveMappingProfile}
          autoFocus
        />
      </Modal>
      )}

      {ADVANCED_MAPPING_ENABLED && (
      <Modal
        title={`值映射设置${valueMapModalField ? `：${valueMapModalField}` : ''}`}
        open={!!valueMapModalField}
        onCancel={() => setValueMapModalField(null)}
        onOk={() => setValueMapModalField(null)}
        width={760}
      >
        <Typography.Paragraph type="secondary">
          已配置 {valueMapDistinctValues.length} 个取值的对照（含已上传文件里发现的新值）；取值比对时忽略大小写和首尾空格（如“UPS”和“ups”视为同一个值），配置一次可长期复用，不受某一次上传的文件限制。为每个值指定对应的系统标准值，留空则按原始值直接使用。
        </Typography.Paragraph>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {valueMapDistinctValues.map((val) => {
            const modeCfg = valueMapModalField ? (transformRules.field_modes || {})[valueMapModalField] : undefined;
            const currentTarget = (modeCfg && modeCfg.mode === 'value_map') ? modeCfg.valueMap[val] : undefined;
            const notInFile = valueMapKeysNotInFile.has(val);
            return (
              <Space key={val} style={{ width: '100%' }}>
                <Space direction="vertical" size={0} style={{ width: 200 }}>
                  <Typography.Text ellipsis={{ tooltip: val }}>{val}</Typography.Text>
                  {notInFile && <Typography.Text type="secondary" style={{ fontSize: 12 }}>未出现在本次文件中</Typography.Text>}
                </Space>
                <Typography.Text type="secondary">→</Typography.Text>
                {valueMapCanonicalOptions.length > 0 ? (
                  <Select
                    style={{ minWidth: 320 }}
                    allowClear
                    showSearch
                    placeholder="选择系统标准值（留空则按原始值直接使用）"
                    value={currentTarget}
                    options={valueMapCanonicalOptions.map((o) => ({ label: o, value: o }))}
                    onChange={(v) => updateValueMapEntry(valueMapModalField as string, val, v || undefined)}
                  />
                ) : (
                  <Input
                    style={{ minWidth: 320 }}
                    placeholder="输入系统标准值（留空则按原始值直接使用）"
                    defaultValue={currentTarget}
                    onBlur={(e) => updateValueMapEntry(valueMapModalField as string, val, e.target.value || undefined)}
                  />
                )}
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteValueMapEntry(valueMapModalField as string, val)}
                />
              </Space>
            );
          })}
          {valueMapDistinctValues.length === 0 && (
            <Typography.Text type="secondary">还没有配置任何对照，可以在下方手动新增，或者先上传一份文件自动发现取值。</Typography.Text>
          )}
        </Space>
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
          <Typography.Text strong>手动新增一条对照</Typography.Text>
          <Typography.Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 8 }}>
            不需要等这个值出现在某次上传的文件里，提前知道来源系统会出现的取值就可以先配好。
          </Typography.Paragraph>
          <Space style={{ width: '100%' }}>
            <Input
              style={{ width: 200 }}
              placeholder="输入源文件里的原始取值"
              value={newValueMapKeyInput}
              onChange={(e) => setNewValueMapKeyInput(e.target.value)}
            />
            <Typography.Text type="secondary">→</Typography.Text>
            {valueMapCanonicalOptions.length > 0 ? (
              <Select
                style={{ minWidth: 320 }}
                allowClear
                showSearch
                placeholder="选择系统标准值（留空则按原始值直接使用）"
                value={newValueMapTargetInput}
                options={valueMapCanonicalOptions.map((o) => ({ label: o, value: o }))}
                onChange={(v) => setNewValueMapTargetInput(v || undefined)}
              />
            ) : (
              <Input
                style={{ minWidth: 320 }}
                placeholder="输入系统标准值（留空则按原始值直接使用）"
                value={newValueMapTargetInput}
                onChange={(e) => setNewValueMapTargetInput(e.target.value || undefined)}
              />
            )}
            <Button type="primary" onClick={handleAddValueMapEntry}>添加</Button>
          </Space>
        </div>
      </Modal>
      )}

      {ADVANCED_MAPPING_ENABLED && (
      <Modal
        title={`设置项目归属${projectLookupModalField ? `：${projectLookupModalField}` : ''}`}
        open={!!projectLookupModalField}
        onCancel={() => setProjectLookupModalField(null)}
        onOk={() => setProjectLookupModalField(null)}
        width={720}
      >
        <Typography.Paragraph type="secondary">
          系统会先按来源列的取值自动反查所属项目；如果同一个取值命中了多个不同项目，或者反查结果不对，可以在这里手动指定，手动指定会保存进这份映射配置，下次同样的取值不会再需要重新确认。
        </Typography.Paragraph>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {(projectLookupModalField ? (lookupProjectFieldSourceValues[projectLookupModalField] || []) : []).map((val) => {
            const modeCfg = projectLookupModalField ? (transformRules.field_modes || {})[projectLookupModalField] : undefined;
            const overrides = (modeCfg && modeCfg.mode === 'rule' && modeCfg.ruleType === 'lookup_project')
              ? (modeCfg.params.manualOverrides || {})
              : {};
            const currentOverride = overrides[val];
            const lookupResult = projectLookupResults[val];
            let statusTag: { color: string; text: string } = { color: 'default', text: '查询中' };
            let statusDetail: React.ReactNode = '正在查询...';
            if (lookupResult) {
              if (lookupResult.status === 'resolved') {
                statusTag = { color: 'green', text: '已自动识别' };
                const resolvedProjectName = String(lookupResult.project_name || '').trim();
                statusDetail = (
                  <>
                    自动识别：<Typography.Text style={{ color: '#1677ff' }}>{lookupResult.project_code}</Typography.Text>
                    {resolvedProjectName ? `(${resolvedProjectName})` : ''}
                  </>
                );
              } else if (lookupResult.status === 'ambiguous') {
                statusTag = { color: 'orange', text: '待确认' };
                statusDetail = (
                  <>
                    命中多个项目，需要手动选择：
                    {lookupResult.candidates.map((c, idx) => {
                      const candidateName = String(c.project_name || '').trim();
                      return (
                        <React.Fragment key={c.project_code || idx}>
                          {idx > 0 ? '、' : ''}
                          <Typography.Text style={{ color: '#1677ff' }}>{c.project_code}</Typography.Text>
                          {candidateName ? `(${candidateName})` : ''}
                        </React.Fragment>
                      );
                    })}
                  </>
                );
              } else if (lookupResult.status === 'not_found') {
                statusTag = { color: 'red', text: '未找到' };
                statusDetail = '在该客户名下所有项目中均未找到匹配的库存单元，可手动指定项目';
              } else {
                statusTag = { color: 'red', text: '查询失败' };
                statusDetail = lookupResult.message || '反查失败，可手动指定项目';
              }
            }
            return (
              <div key={val} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 10 }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                  <Typography.Text strong ellipsis={{ tooltip: val }} style={{ maxWidth: 260 }}>{val}</Typography.Text>
                  <Tag color={statusTag.color}>{statusTag.text}</Tag>
                </Space>
                <Typography.Paragraph type="secondary" style={{ margin: '4px 0' }}>{statusDetail}</Typography.Paragraph>
                <Select
                  allowClear
                  showSearch
                  style={{ width: '100%' }}
                  placeholder="手动指定项目（留空则使用自动识别结果）"
                  value={currentOverride}
                  options={projectLookupProjectOptions.map((o) => ({ label: o, value: o }))}
                  onChange={(v) => updateProjectLookupOverride(projectLookupModalField as string, val, v || undefined)}
                />
              </div>
            );
          })}
        </Space>
      </Modal>
      )}

      {currentStep === 1 && (
        <>
          {precheckRunning && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1200,
                background: 'rgba(255,255,255,0.72)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
                <style>{`@keyframes precheckDotRotate{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
                <div style={{ position: 'relative', width: 24, height: 24, animation: 'precheckDotRotate 1.1s linear infinite' }}>
                  <span style={{ position: 'absolute', top: 0, left: '50%', width: 6, height: 6, marginLeft: -3, borderRadius: '50%', background: '#1677ff' }} />
                  <span style={{ position: 'absolute', top: '50%', right: 0, width: 6, height: 6, marginTop: -3, borderRadius: '50%', background: '#1677ff' }} />
                  <span style={{ position: 'absolute', bottom: 0, left: '50%', width: 6, height: 6, marginLeft: -3, borderRadius: '50%', background: '#1677ff' }} />
                  <span style={{ position: 'absolute', top: '50%', left: 0, width: 6, height: 6, marginTop: -3, borderRadius: '50%', background: '#1677ff' }} />
                </div>
                <Typography.Text strong>正在预审，请耐心等待...</Typography.Text>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
            <div style={{ flex: '0 0 calc(50% - 8px)', maxWidth: 'calc(50% - 8px)' }}>
              <Card title="上传列表文件" style={{ height: '100%' }}>
                <Upload.Dragger {...uploadProps}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">点击或拖拽上传订单列表文件</p>
                  <p className="ant-upload-hint">支持 .xlsx / .xls / .csv，每次仅支持一个文件,每个文件最多1000条记录</p>
                </Upload.Dragger>
                {selectedFileName && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '8px 12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      background: '#fafafa',
                    }}
                  >
                    <Typography.Text strong ellipsis style={{ maxWidth: 'calc(100% - 40px)' }}>
                      已上传文件({Math.max(1, fileList.length)}): {selectedFileName}
                    </Typography.Text>
                    <Space size={4}>
                      {(headerCheck.status === 'missing' || headerCheck.status === 'error') && (
                        <Button type="link" danger onClick={handleViewHeaderError}>
                          查看错误
                        </Button>
                      )}
                      <Button type="link" onClick={handlePreviewListFile}>
                        预览
                      </Button>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          setSelectedFileName('');
                          setUploadedFile(null);
                          setFileList([]);
                          setHeaderCheck({ status: 'idle' });
                          setParsedHeaders([]);
                          setParsedRows([]);
                          setValidationSummary(null);
                          setHasValidated(false);
                          setRowLimitExceeded(false);
                          setListFilePreviewVisible(false);
                          setListFilePreviewHeaders([]);
                          setListFilePreviewRows([]);
                        }}
                      />
                    </Space>
                  </div>
                )}
              </Card>
            </div>
            <div style={{ flex: '0 0 calc(50% - 8px)', maxWidth: 'calc(50% - 8px)' }}>
              {scenario === 'upload_immediate' ? (
                <Card title="上传物流运单附件" style={{ height: '100%' }}>
                  <Upload.Dragger {...waybillBundleUploadProps}>
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">点击或拖拽上传运单附件</p>
                    <p className="ant-upload-hint">
                      支持上传 zip 压缩包或 pdf 及常用图片文件，最多上传1000个附件文件。
                      如果是第三方系统导出的一份多页合并 PDF（第N页对应列表文件第N行），
                      也可以直接整份上传，预审时会自动按页拆分并匹配到对应订单，不需要提前手工拆分成一个订单一个文件。
                    </p>
                  </Upload.Dragger>
                  {waybillBundleFileList.length > 0 && (
                    <Card
                      size="small"
                      style={{ marginTop: 12 }}
                      title={(
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ whiteSpace: 'nowrap' }}>已上传文件（{waybillBundleFileList.length}）</span>
                          <Input
                            allowClear
                            style={{ width: 340, maxWidth: '60%' }}
                            placeholder="按文件名筛选（自动忽略首尾空格与大小写）"
                            value={waybillFileFilterText}
                            onChange={(e) => setWaybillFileFilterText(e.target.value)}
                          />
                        </div>
                      )}
                      extra={(
                        <Button type="link" danger onClick={clearWaybillUploadList}>
                          清空全部
                        </Button>
                      )}
                    >
                      <div style={{ maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {filteredWaybillFileList.map((file) => (
                          <div
                            key={file.uid}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: 8,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                              background: '#fafafa',
                            }}
                          >
                            <Typography.Text ellipsis style={{ maxWidth: 'calc(100% - 40px)' }}>
                              {file.name}
                            </Typography.Text>
                            <Space size={2}>
                              <Button type="link" onClick={() => openWaybillPreview(file)}>
                                预览
                              </Button>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeWaybillFileByUid(file.uid)}
                              />
                            </Space>
                          </div>
                        ))}
                        {filteredWaybillFileList.length === 0 && (
                          <Typography.Text type="secondary">未匹配到文件，请调整筛选关键字。</Typography.Text>
                        )}
                        </Space>
                      </div>
                    </Card>
                  )}
                </Card>
              ) : (
                <div style={{ height: '100%' }} />
              )}
            </div>
          </div>

          <Card size="small">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {ADVANCED_MAPPING_ENABLED ? (
                <Button onClick={openMappingDrawer}>
                  字段映射配置{scenarioProfiles.length > 0 ? `（已保存${scenarioProfiles.length}份）` : ''}
                </Button>
              ) : <div />}
              <Space size={8}>
                <Button onClick={openPrecheckGuideModal}>说明</Button>
                <Tooltip title={canTriggerValidation ? '' : precheckDisabledReason}>
                  <span>
                    <Button type="primary" size="large" style={stepCtaButtonStyle} loading={precheckRunning} disabled={!canTriggerValidation || precheckRunning} onClick={handleRunPrecheck}>
                      {precheckDone ? '重新预审' : '开始预审'}
                    </Button>
                  </span>
                </Tooltip>
              </Space>
            </div>
          </Card>

          {(precheckRunning || precheckDone || hasValidated) && (
            <Card>
              {hasPrecheckSystemError && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="预审未完成"
                  description={precheckErrorText}
                />
              )}
              {precheckDone && precheckResult && (
                <div
                  style={{
                    marginBottom: 12,
                    borderRadius: 8,
                    padding: '10px 12px',
                    background: overallPrecheckPassed ? '#f6ffed' : '#fff1f0',
                    border: `1px solid ${overallPrecheckPassed ? '#b7eb8f' : '#ffa39e'}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  {overallPrecheckPassed ? (
                    <CheckCircleFilled style={{ color: '#389e0d', fontSize: 18 }} />
                  ) : (
                    <CloseCircleFilled style={{ color: '#ff7875', fontSize: 18 }} />
                  )}
                  <Typography.Text style={{ color: overallPrecheckPassed ? '#135200' : '#820014', whiteSpace: 'nowrap' }}>
                    {overallPrecheckPassed ? '预审已全部通过,可进行下一步:执行导入' : '预审存在错误,请根据下方分区结果修改'}
                  </Typography.Text>
                </div>
              )}
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Card size="small" title={renderPrecheckTabLabel('预审1:列表文件', filePrecheckHasError)}>
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {!hasValidated && (
                      <Typography.Text type="secondary">请点击“开始预审”后查看列表文件预审结果。</Typography.Text>
                    )}
                    {hasValidated && (
                      (
                        (headerCheck.status === 'missing'
                          || headerCheck.status === 'error'
                          || (headerCheck.status === 'ok' && validationSummary && (validationSummary.invalidRows > 0 || rowLimitExceeded)))
                          ? (
                            <div
                              style={{
                                background: '#fff1f0',
                                border: '1px solid #ffa39e',
                                borderRadius: 8,
                                padding: '10px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                              }}
                            >
                              {headerCheck.status === 'missing' && (
                                <>
                                  <Typography.Text>表头预审未通过：缺少必填列</Typography.Text>
                                  <Space size={[6, 6]} wrap>
                                    {(headerCheck.missingDetails || []).map((d) => (
                                      <Tooltip
                                        key={`missing-header-${d.field}`}
                                        title={ADVANCED_MAPPING_ENABLED && d.reason === 'profile_drift'
                                          ? `映射配置中该字段对应的来源列「${d.mappedColumn}」在本次上传的文件中不存在，请检查文件表头是否变化，或重新设置映射。`
                                          : '文件中缺少该必填列，请修改源文件后重新上传。'}
                                      >
                                        <Tag style={outlinedRedTagStyle}>{d.field}</Tag>
                                      </Tooltip>
                                    ))}
                                  </Space>
                                </>
                              )}
                              {headerCheck.status === 'error' && (
                                <Typography.Text>表头预审未通过：{headerCheck.reason}</Typography.Text>
                              )}
                              {headerCheck.status === 'ok' && validationSummary && (validationSummary.invalidRows > 0 || rowLimitExceeded) && (
                                <>
                                  <Typography.Text>
                                    总行数 {validationSummary.totalRows}，校验通过 {validationSummary.validRows}，校验失败 {validationSummary.invalidRows}
                                    {rowLimitExceeded ? '（超过1000行，需拆分）' : ''}
                                  </Typography.Text>
                                  {validationSummary.invalidRows > 0 && (
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setValidationDetailPage(1);
                                        setValidationDetailVisible(true);
                                      }}
                                    >
                                      查看失败记录
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                            )
                          : null
                      )
                    )}
                    {hasValidated && ruleNonContiguousWarnings.length > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message="规则映射：以下取值在文件中不是连续出现的，已按相同取值合并为同一个指令，请确认是否符合预期"
                        description={(
                          <Space direction="vertical" size={2}>
                            {ruleNonContiguousWarnings.map((w) => (
                              <Typography.Text key={`${w.field}-${w.value}`}>
                                {w.field}：取值「{w.value}」出现在第 {w.rowNos.join('、')} 行
                              </Typography.Text>
                            ))}
                          </Space>
                        )}
                      />
                    )}
                  </Space>
                </Card>

                <Card
                  size="small"
                  title={(
                    <Space size={8}>
                      {renderPrecheckTabLabel('预审2:库存与业务信息', inventoryPrecheckHasError)}
                      <Button
                        size="small"
                        onClick={() => setInventoryStatsExpanded((prev) => !prev)}
                      >
                        {inventoryStatsExpanded ? '收起库存统计' : '展开库存统计'}
                      </Button>
                    </Space>
                  )}
                >
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {!precheckDone && (
                      <Typography.Text type="secondary">请先完成预审后查看库存与业务预审结果。</Typography.Text>
                    )}
                    {precheckDone && (
                      <>
                        {(inventoryBlockingIssues.length > 0 || inventoryWarningIssues.length > 0) && (
                          <div
                            style={{
                              background: inventoryPrecheckHasError ? '#fff1f0' : '#f6ffed',
                              border: `1px solid ${inventoryPrecheckHasError ? '#ffa39e' : '#b7eb8f'}`,
                              borderRadius: 8,
                              padding: '10px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              flexWrap: 'wrap',
                            }}
                          >
                            {inventoryBlockingIssues.length > 0 && (
                              <>
                                <Typography.Text>
                                  错误 {inventoryBlockingIssues.length} 个
                                </Typography.Text>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const list = inventoryBlockingIssues;
                                    const grouped = groupIssueList(list, getBlockingGroupLabel);
                                    const copyText = buildGroupedIssueText(grouped, formatBlockingIssueText);
                                    Modal.info({
                                      title: (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                          <span>错误详情</span>
                                          <Button size="small" onClick={() => copyIssueText(copyText, '错误详情已复制')}>
                                            复制信息
                                          </Button>
                                        </div>
                                      ),
                                      width: 980,
                                      okText: '关闭',
                                      content: (
                                        <div style={{ maxHeight: 360, overflow: 'auto' }}>
                                          {list.length === 0 ? (
                                            <Typography.Text type="secondary">当前无错误信息。</Typography.Text>
                                          ) : (
                                            grouped.map(([groupLabel, items]) => (
                                              <div key={`blocking-group-${groupLabel}`} style={{ marginBottom: 12 }}>
                                                <Typography.Text strong style={{ color: '#cf1322' }}>
                                                  {groupLabel}（{items.length}）
                                                </Typography.Text>
                                                <div style={{ marginTop: 6 }}>
                                                  {items.map((it, idx) => (
                                                    <div key={`blocking-detail-${it.type}-${it.row_no}-${idx}`} style={{ color: '#cf1322', marginBottom: 4 }}>
                                                      {renderIssueText(formatBlockingIssueText(it))}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      ),
                                    });
                                  }}
                                >
                                  查看详情
                                </Button>
                              </>
                            )}
                            {inventoryBlockingIssues.length > 0 && inventoryWarningIssues.length > 0 && <Typography.Text>；</Typography.Text>}
                            {inventoryWarningIssues.length > 0 && (
                              <>
                                <Typography.Text>
                                  警告 {inventoryWarningIssues.length} 个
                                </Typography.Text>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const list = inventoryWarningIssues;
                                    const grouped = groupIssueList(list, getWarningGroupLabel);
                                    const copyText = buildGroupedIssueText(grouped, formatWarningIssueText);
                                    Modal.info({
                                      title: (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                          <span>
                                            警告详情
                                            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                                              本页警告信息不影响正常提交
                                            </Typography.Text>
                                          </span>
                                          <Button size="small" onClick={() => copyIssueText(copyText, '警告详情已复制')}>
                                            复制信息
                                          </Button>
                                        </div>
                                      ),
                                      width: 980,
                                      okText: '关闭',
                                      content: (
                                        <div style={{ maxHeight: 360, overflow: 'auto' }}>
                                          {list.length === 0 ? (
                                            <Typography.Text type="secondary">当前无警告信息。</Typography.Text>
                                          ) : (
                                            grouped.map(([groupLabel, items]) => (
                                              <div key={`warning-group-${groupLabel}`} style={{ marginBottom: 12 }}>
                                                <Typography.Text strong style={{ color: '#d46b08' }}>
                                                  {groupLabel}（{items.length}）
                                                </Typography.Text>
                                                <div style={{ marginTop: 6 }}>
                                                  {items.map((it, idx) => (
                                                    <div key={`warning-detail-${it.type}-${it.row_no || 0}-${idx}`} style={{ color: '#d46b08', marginBottom: 4 }}>
                                                      {renderIssueText(formatWarningIssueText(it))}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      ),
                                    });
                                  }}
                                >
                                  查看详情
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                        {inventoryStatsExpanded && (
                          <Table
                            size="small"
                            rowKey={(row) => `${(row as any).warehouse_code}-${(row as any).inventory_unit_code}`}
                            pagination={{
                              current: inventoryTablePage,
                              pageSize: inventoryTablePageSize,
                              total: (precheckResult?.inventory_summary || []).length,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              pageSizeOptions: ['8', '20', '50', '100'],
                              showTotal: (total) => `共 ${total} 条`,
                              onChange: (page, pageSize) => {
                                setInventoryTablePage(page);
                                setInventoryTablePageSize(pageSize);
                              },
                              onShowSizeChange: (page, pageSize) => {
                                setInventoryTablePage(page);
                                setInventoryTablePageSize(pageSize);
                              },
                            }}
                            dataSource={precheckResult?.inventory_summary || []}
                            columns={[
                              { title: '仓库', dataIndex: 'warehouse_code', key: 'warehouse_code', width: 120 },
                              { title: '库存单元', dataIndex: 'inventory_unit_code', key: 'inventory_unit_code', width: 160 },
                              {
                                title: '库存单元标识',
                                key: 'inventory_unit_tag_display',
                                width: 260,
                                ellipsis: true,
                                render: (_: unknown, row: any) => String(row?.inventory_unit_tag_display || '-'),
                              },
                              { title: '可用库存', dataIndex: 'available_qty', key: 'available_qty', width: 100 },
                              { title: '需求数量', dataIndex: 'required_qty', key: 'required_qty', width: 100 },
                              {
                                title: '状态',
                                key: 'status',
                                width: 120,
                                render: (_: unknown, row: any) => (
                                  Number(row.required_qty || 0) > Number(row.available_qty || 0)
                                    ? <Tag style={outlinedRedTagStyle}>库存不足</Tag>
                                    : <Tag style={outlinedGreenTagStyle}>库存充足</Tag>
                                ),
                              },
                            ]}
                          />
                        )}
                      </>
                    )}
                  </Space>
                </Card>

                {scenario === 'upload_immediate' && (
                  <Card
                    size="small"
                    title={(
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8 }}>
                        {renderPrecheckTabLabel('预审3:运单附件匹配与处理', waybillPrecheckHasError)}
                        <Button size="small" onClick={() => setWaybillMatchListExpanded((prev) => !prev)}>
                          {waybillMatchListExpanded ? '收起' : '展开'}
                        </Button>
                      </div>
                    )}
                  >
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      {precheckDone && (unresolvedWaybillBlockingIssues.length > 0 || waybillWarningIssues.length > 0) && (
                        <div
                          style={{
                            background: unresolvedWaybillBlockingIssues.length > 0 ? '#fff1f0' : '#f6ffed',
                            border: `1px solid ${unresolvedWaybillBlockingIssues.length > 0 ? '#ffa39e' : '#b7eb8f'}`,
                            borderRadius: 8,
                            padding: '10px 12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Space size={8} wrap>
                            {unresolvedWaybillBlockingIssues.length > 0 && (
                              <>
                                <Typography.Text>
                                  错误 {unresolvedWaybillBlockingIssues.length} 个
                                </Typography.Text>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const list = unresolvedWaybillBlockingIssues;
                                    const grouped = groupIssueList(list, getBlockingGroupLabel);
                                    const copyText = buildGroupedIssueText(grouped, formatBlockingIssueText);
                                    Modal.info({
                                      title: (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                          <span>错误详情</span>
                                          <Button size="small" onClick={() => copyIssueText(copyText, '错误详情已复制')}>
                                            复制信息
                                          </Button>
                                        </div>
                                      ),
                                      width: 980,
                                      okText: '关闭',
                                      content: (
                                        <div style={{ maxHeight: 360, overflow: 'auto' }}>
                                          {list.length === 0 ? (
                                            <Typography.Text type="secondary">当前无错误信息。</Typography.Text>
                                          ) : (
                                            grouped.map(([groupLabel, items]) => (
                                              <div key={`waybill-blocking-group-${groupLabel}`} style={{ marginBottom: 12 }}>
                                                <Typography.Text strong style={{ color: '#cf1322' }}>
                                                  {groupLabel}（{items.length}）
                                                </Typography.Text>
                                                <div style={{ marginTop: 6 }}>
                                                  {items.map((it, idx) => (
                                                    <div key={`waybill-blocking-detail-${it.type}-${it.row_no}-${idx}`} style={{ color: '#cf1322', marginBottom: 4 }}>
                                                      {renderIssueText(formatBlockingIssueText(it))}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      ),
                                    });
                                  }}
                                >
                                  查看详情
                                </Button>
                              </>
                            )}
                            {unresolvedWaybillBlockingIssues.length > 0 && waybillWarningIssues.length > 0 && <Typography.Text>；</Typography.Text>}
                            {waybillWarningIssues.length > 0 && (
                              <>
                                <Typography.Text>
                                  警告 {waybillWarningIssues.length} 个
                                </Typography.Text>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    const list = waybillWarningIssues;
                                    const grouped = groupIssueList(list, getWarningGroupLabel);
                                    const copyText = buildGroupedIssueText(grouped, formatWarningIssueText);
                                    Modal.info({
                                      title: (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                        <span>
                                          警告详情
                                          <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                                            本页警告信息不影响正常提交
                                          </Typography.Text>
                                        </span>
                                          <Button size="small" onClick={() => copyIssueText(copyText, '警告详情已复制')}>
                                            复制信息
                                          </Button>
                                        </div>
                                      ),
                                      width: 980,
                                      okText: '关闭',
                                      content: (
                                        <div style={{ maxHeight: 360, overflow: 'auto' }}>
                                          {list.length === 0 ? (
                                            <Typography.Text type="secondary">当前无警告信息。</Typography.Text>
                                          ) : (
                                            grouped.map(([groupLabel, items]) => (
                                              <div key={`waybill-warning-group-${groupLabel}`} style={{ marginBottom: 12 }}>
                                                <Typography.Text strong style={{ color: '#d46b08' }}>
                                                  {groupLabel}（{items.length}）
                                                </Typography.Text>
                                                <div style={{ marginTop: 6 }}>
                                                  {items.map((it, idx) => (
                                                    <div key={`waybill-warning-detail-${it.type}-${it.row_no || 0}-${idx}`} style={{ color: '#d46b08', marginBottom: 4 }}>
                                                      {renderIssueText(formatWarningIssueText(it))}
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      ),
                                    });
                                  }}
                                >
                                  查看详情
                                </Button>
                              </>
                            )}
                          </Space>
                          <Typography.Text style={{ whiteSpace: 'nowrap' }}>
                            共{waybillMatchTotalCount}条记录 已匹配{waybillMatchCompletedCount}条 <span style={{ color: '#cf1322', fontWeight: 600 }}>未匹配{waybillMatchPendingCount}条</span>
                          </Typography.Text>
                        </div>
                      )}
                      {waybillMatchListExpanded && (
                        <Table
                          size="small"
                          rowKey={(r) => (r as WaybillMatchGroup).groupKey}
                          pagination={{
                            current: waybillMatchTablePage,
                            pageSize: waybillMatchTablePageSize,
                            total: waybillMatchGroups.length,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showTotal: (total, range) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                            onChange: (page, pageSize) => {
                              setWaybillMatchTablePage(page);
                              setWaybillMatchTablePageSize(pageSize);
                            },
                            onShowSizeChange: (page, pageSize) => {
                              setWaybillMatchTablePage(page);
                              setWaybillMatchTablePageSize(pageSize);
                            },
                          }}
                          dataSource={waybillMatchGroups}
                          locale={{ emptyText: '暂无需匹配的记录组' }}
                          onRow={(record) => {
                            const row = record as WaybillMatchGroup;
                            const isUnmatched = !['matched_auto', 'matched_manual'].includes(String(row.status || ''));
                            return isUnmatched ? { style: { background: '#fff1f0' } } : {};
                          }}
                          columns={[
                          { title: '指令序号', dataIndex: 'serialNo', key: 'serialNo', width: 90 },
                          {
                            title: <span style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>列表文件中的物流跟踪号</span>,
                            key: 'trackingNo',
                            width: 170,
                            render: (_: unknown, r: WaybillMatchGroup) => {
                              const text = String(r.trackingNo || '-');
                              return (
                                <Tooltip title={text}>
                                  <Typography.Text ellipsis style={{ maxWidth: 150 }}>
                                    {text}
                                  </Typography.Text>
                                </Tooltip>
                              );
                            },
                          },
                          {
                            title: <span style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>列表文件中的物流运单附件文件名</span>,
                            key: 'expectedFileName',
                            width: 190,
                            render: (_: unknown, r: WaybillMatchGroup) => {
                              const text = String(r.expectedFileName || '-');
                              return (
                                <Tooltip title={text}>
                                  <Typography.Text ellipsis style={{ maxWidth: 170 }}>
                                    {text}
                                  </Typography.Text>
                                </Tooltip>
                              );
                            },
                          },
                          {
                            title: '匹配状态',
                            key: 'status',
                            width: 110,
                            render: (_: unknown, r: WaybillMatchGroup) => {
                              if (r.status === 'matched_auto') return <Tag style={outlinedGreenTagStyle}>自动匹配</Tag>;
                              if (r.status === 'matched_manual') return <Tag color="blue">人工匹配</Tag>;
                              if (r.status === 'missing_expected') return <Tag style={outlinedRedTagStyle}>缺少文件名</Tag>;
                              return <Tag style={outlinedRedTagStyle}>未匹配</Tag>;
                            },
                          },
                          {
                            title: <span style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>已匹配运单附件文件</span>,
                            key: 'matchedFileName',
                            width: 210,
                            render: (_: unknown, r: WaybillMatchGroup) => {
                              const matched = String(r.matchedFileName || '').trim();
                              if (!matched) return '-';
                              const kind = getPreviewKindByName(matched);
                              const previewable = kind === 'image' || kind === 'pdf';
                              return (
                                <Space size={4} wrap>
                                  <Tooltip title={matched}>
                                    <Typography.Text ellipsis style={{ maxWidth: 120 }}>
                                      {matched}
                                    </Typography.Text>
                                  </Tooltip>
                                  <Button
                                    size="small"
                                    type="link"
                                    disabled={!previewable}
                                    onClick={() => openMatchedWaybillPreviewByName(matched)}
                                  >
                                    预览
                                  </Button>
                                </Space>
                              );
                            },
                          },
                          {
                            title: (
                              <Space size={4}>
                                <span>人工匹配</span>
                                <Tooltip title="请为未匹配的记录手动匹配运单附件文件,若选择已匹配的附件,则将解除旧匹配关系并重新绑定新匹配关系;">
                                  <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                                </Tooltip>
                              </Space>
                            ),
                            key: 'manual',
                            width: 160,
                            render: (_: unknown, r: WaybillMatchGroup) => {
                              return (
                                <Select
                                    key={`manual-select-${r.groupKey}-${manualMatchSelectResetTick}`}
                                    allowClear
                                    showSearch
                                    style={{ width: '100%' }}
                                    placeholder="选择附件文件"
                                    value={undefined}
                                    onChange={(value) => {
                                      handleManualWaybillMatchChange(r, value as string | undefined);
                                      setManualMatchSelectResetTick((prev) => prev + 1);
                                    }}
                                    options={attachmentCandidateNames.map((name) => {
                                      const occupied = occupiedAttachmentByFile.get(String(name || '').trim().toLowerCase());
                                      const occupiedByOther = !!occupied && occupied.groupKey !== r.groupKey;
                                      const occupiedText = occupiedByOther
                                        ? `(已匹配至指令序号${String(occupied?.serialNo || '-')})`
                                        : '';
                                      const displayLabel = `${name}${occupiedText}`;
                                      return {
                                        label: (
                                          <Tooltip title={displayLabel}>
                                            <span style={{ display: 'inline-block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {displayLabel}
                                            </span>
                                          </Tooltip>
                                        ),
                                        value: name,
                                        rawLabel: displayLabel,
                                      };
                                    })}
                                    filterOption={(input, option) => String((option as any)?.rawLabel || '').toLowerCase().includes(String(input || '').toLowerCase())}
                                  />
                              );
                            },
                          },
                          ]}
                        />
                      )}
                    </Space>
                  </Card>
                )}
              </Space>
            </Card>
          )}

          <Card size="small">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button size="large" style={stepBackButtonStyle} onClick={() => setCurrentStep(0)}>上一步</Button>
              <Tooltip title={
                (!!validationSummary && Number(validationSummary.invalidRows || 0) > 0)
                  ? '列表文件校验存在错误，请修正后重新校验'
                  : scenario === 'upload_immediate' && waybillBundleFiles.length === 0
                  ? '当前场景必须先上传运单附件，才能进入执行导入'
                  : !precheckDone
                    ? '请先完成预审'
                  : (effectiveBlockingCount > 0)
                      ? '预审存在错误，请修改'
                  : (scenario === 'upload_immediate' && waybillMatchPendingCount > 0)
                    ? `仍有 ${waybillMatchPendingCount} 条运单匹配待处理`
                    : ''
              }>
                <span>
                  <Button
                    type="primary"
                    size="large"
                    style={stepCtaButtonStyle}
                    disabled={!canProceedToExecute}
                    onClick={() => {
                      setCurrentStep(2);
                      void handleCreateImportJob(true);
                    }}
                  >
                    下一步：执行导入
                  </Button>
                </span>
              </Tooltip>
            </div>
          </Card>
        </>
      )}

      {currentStep === 2 && (
      <Card title="步骤3：导入执行结果">
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {executionRunning && (
            <div style={{ minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 2px' }}>
                <style>{`@keyframes importExecuteDotRotate{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
                <div style={{ position: 'relative', width: 24, height: 24, animation: 'importExecuteDotRotate 1.1s linear infinite' }}>
                  <span style={{ position: 'absolute', top: 0, left: '50%', width: 6, height: 6, marginLeft: -3, borderRadius: '50%', background: '#1677ff' }} />
                  <span style={{ position: 'absolute', top: '50%', right: 0, width: 6, height: 6, marginTop: -3, borderRadius: '50%', background: '#1677ff' }} />
                  <span style={{ position: 'absolute', bottom: 0, left: '50%', width: 6, height: 6, marginLeft: -3, borderRadius: '50%', background: '#1677ff' }} />
                  <span style={{ position: 'absolute', top: '50%', left: 0, width: 6, height: 6, marginTop: -3, borderRadius: '50%', background: '#1677ff' }} />
                </div>
                <Typography.Text strong>导入执行中，请稍候...</Typography.Text>
              </div>
              {queuedLikelyStuck && (
                <Alert
                  type="warning"
                  showIcon
                  message="任务可能卡住"
                  description={`当前任务已处于 queued 状态约 ${queuedStuckMinutes} 分钟。可先取消任务，再重新执行。`}
                  style={{ maxWidth: 560 }}
                />
              )}
              {queuedLikelyStuck && (
                <Space>
                  <Button danger loading={cancellingJob} disabled={!activeJobId} onClick={handleCancelJob}>
                    取消当前任务
                  </Button>
                </Space>
              )}
            </div>
          )}
          {executeResultError && (
            <Alert
              type="error"
              showIcon
              message="导入执行失败"
              description={executeResultError}
            />
          )}
          {!executionRunning && (
            <>
              <Space size={12} wrap>
                <Card size="small" style={{ minWidth: 260 }}>
                  <Typography.Text type="secondary">总计记录</Typography.Text>
                  <div style={{ fontSize: 20, fontWeight: 600 }}>{groupedExecutionStats.total.orders}条记录 | {groupedExecutionStats.total.qty}件产品</div>
                </Card>
                <Card
                  size="small"
                  hoverable
                  onClick={() => setExecutionListView((prev) => (prev === 'created' ? null : 'created'))}
                  style={{
                    minWidth: 260,
                    cursor: 'pointer',
                    borderColor: executionListView === 'created' ? '#95de64' : undefined,
                    background: groupedExecutionStats.created.orders > 0 ? '#f6ffed' : '#fafafa',
                  }}
                >
                  <Typography.Text style={{ color: groupedExecutionStats.created.orders > 0 ? '#389e0d' : '#8c8c8c' }}>
                    已创建记录
                  </Typography.Text>
                  <div style={{ fontSize: 20, fontWeight: 600, color: groupedExecutionStats.created.orders > 0 ? '#389e0d' : '#8c8c8c' }}>
                    {groupedExecutionStats.created.orders}条记录 | {groupedExecutionStats.created.qty}件产品
                  </div>
                </Card>
                <Card
                  size="small"
                  hoverable
                  onClick={() => setExecutionListView((prev) => (prev === 'failed' ? null : 'failed'))}
                  style={{
                    minWidth: 260,
                    cursor: 'pointer',
                    borderColor: executionListView === 'failed' ? '#ff7875' : undefined,
                    background: groupedExecutionStats.failed.orders > 0 ? '#fff1f0' : '#fafafa',
                  }}
                >
                  <Typography.Text style={{ color: groupedExecutionStats.failed.orders > 0 ? '#cf1322' : '#8c8c8c' }}>
                    失败记录
                  </Typography.Text>
                  <div style={{ fontSize: 20, fontWeight: 600, color: groupedExecutionStats.failed.orders > 0 ? '#cf1322' : '#8c8c8c' }}>
                    {groupedExecutionStats.failed.orders}条记录 | {groupedExecutionStats.failed.qty}件产品
                  </div>
                </Card>
                <Card
                  size="small"
                  hoverable
                  onClick={() => setExecutionListView((prev) => (prev === 'skipped' ? null : 'skipped'))}
                  style={{
                    minWidth: 260,
                    cursor: 'pointer',
                    borderColor: executionListView === 'skipped' ? '#ffd591' : undefined,
                    background: groupedExecutionStats.skipped.orders > 0 ? '#fff7e6' : '#fafafa',
                  }}
                >
                  <Typography.Text style={{ color: groupedExecutionStats.skipped.orders > 0 ? '#d46b08' : '#8c8c8c' }}>
                    跳过记录
                  </Typography.Text>
                  <div style={{ fontSize: 20, fontWeight: 600, color: groupedExecutionStats.skipped.orders > 0 ? '#d46b08' : '#8c8c8c' }}>
                    {groupedExecutionStats.skipped.orders}条记录 | {groupedExecutionStats.skipped.qty}件产品
                  </div>
                </Card>
              </Space>
              {executionListView && currentExecutionDetail && (
                <Card
                  size="small"
                  title={`${currentExecutionDetail.title} ${currentExecutionDetail.records.length}`}
                  headStyle={{
                    background: currentExecutionDetailTheme.bg,
                    borderColor: currentExecutionDetailTheme.border,
                    color: currentExecutionDetailTheme.text,
                  }}
                  style={{ borderColor: currentExecutionDetailTheme.border }}
                >
                  <Tabs
                    activeKey={executionDetailTabKey}
                    onChange={(key) => setExecutionDetailTabKey((key as 'list' | 'stats' | 'productStats') || 'list')}
                    items={[
                      {
                        key: 'list',
                        label: '记录列表',
                        children: currentExecutionDetail.records.length === 0 ? (
                          <Typography.Text type="secondary">暂无记录。</Typography.Text>
                        ) : (
                          <Space direction="vertical" size={10} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                              <Button
                                onClick={downloadExecutionDetailRecords}
                                disabled={currentExecutionDetail.records.length <= 0}
                              >
                                下载记录
                              </Button>
                              {executionListView === 'skipped' && (
                                <Button
                                  danger
                                  loading={executingJob}
                                  disabled={!activeJobId || groupedExecutionStats.skipped.orders <= 0}
                                  onClick={() => {
                                    Modal.confirm({
                                      title: '确认强制创建跳过项？',
                                      content: `将对 ${groupedExecutionStats.skipped.orders} 条跳过记录执行强制创建，可能产生重复指令。`,
                                      okText: '确认强制创建',
                                      cancelText: '取消',
                                      okButtonProps: { danger: true },
                                      onOk: () => handleExecuteJob('skipped_force'),
                                    });
                                  }}
                                >
                                  强制创建跳过项
                                </Button>
                              )}
                            </div>
                            <Table
                              size="small"
                              rowKey={(it) => `execution-detail-${(it as ExecutionGroupRecord).groupKey}`}
                              dataSource={currentExecutionDetail.records}
                              pagination={{
                                current: executionDetailListPage,
                                pageSize: executionDetailListPageSize,
                                total: currentExecutionDetail.records.length,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                showTotal: (total, range) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                                onChange: (page, pageSize) => {
                                  setExecutionDetailListPage(page);
                                  setExecutionDetailListPageSize(pageSize);
                                },
                                onShowSizeChange: (page, pageSize) => {
                                  setExecutionDetailListPage(page);
                                  setExecutionDetailListPageSize(pageSize);
                                },
                              }}
                              scroll={{ x: 980 }}
                              columns={[
                                { title: '指令序号', dataIndex: 'serialNo', key: 'serialNo', width: 120 },
                                {
                                  title: '涉及行号',
                                  key: 'rowNos',
                                  width: 180,
                                  render: (_: unknown, it: ExecutionGroupRecord) => it.rowNos.join(','),
                                },
                                {
                                  title: currentExecutionDetail.reasonColumnTitle,
                                  key: 'reason',
                                  render: (_: unknown, it: ExecutionGroupRecord) => currentExecutionDetail.reasonText(it),
                                },
                              ]}
                            />
                          </Space>
                        ),
                      },
                      {
                        key: 'stats',
                        label: '订单类型统计',
                        children: (
                          <Space direction="vertical" size={10} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                              <Button onClick={() => { void copyExecutionDetailStats(); }}>复制信息</Button>
                            </div>
                            <Card size="small">
                              <Typography.Text>
                                单款单件 {currentExecutionClassStats.singleSingle.orders}条记录 | {currentExecutionClassStats.singleSingle.qty}件产品
                              </Typography.Text>
                            </Card>
                            <Card size="small">
                              <Typography.Text>
                                单款多件 {currentExecutionClassStats.singleMulti.orders}条记录 | {currentExecutionClassStats.singleMulti.qty}件产品
                              </Typography.Text>
                            </Card>
                            <Card size="small">
                              <Typography.Text>
                                多款多件 {currentExecutionClassStats.multiMulti.orders}条记录 | {currentExecutionClassStats.multiMulti.qty}件产品
                              </Typography.Text>
                            </Card>
                          </Space>
                        ),
                      },
                      {
                        key: 'productStats',
                        label: '产品统计',
                        children: (
                          <Space direction="vertical" size={10} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                              <Button onClick={() => { void downloadInventoryUnitStats(); }}>下载记录</Button>
                            </div>
                            <Table
                              size="small"
                              rowKey={(it) => `unit-stat-${(it as { inventoryUnitId: number | null; inventoryUnitTagDisplay: string }).inventoryUnitId ?? (it as { inventoryUnitTagDisplay: string }).inventoryUnitTagDisplay}`}
                              dataSource={currentInventoryUnitStats}
                              pagination={{
                                current: productStatsPage,
                                pageSize: productStatsPageSize,
                                total: currentInventoryUnitStats.length,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                showTotal: (total, range) => `第${range[0]}-${range[1]}条 共${total}条`,
                                onChange: (page, pageSize) => {
                                  setProductStatsPage(page);
                                  setProductStatsPageSize(pageSize);
                                },
                                onShowSizeChange: (page, pageSize) => {
                                  setProductStatsPage(page);
                                  setProductStatsPageSize(pageSize);
                                },
                              }}
                              scroll={{ x: 900 }}
                              columns={[
                                { title: '仓库', dataIndex: 'warehouseName', key: 'warehouseName', width: 160 },
                                { title: '所属项目', dataIndex: 'projectName', key: 'projectName', width: 180 },
                                { title: '库存单元系统编码', dataIndex: 'inventoryUnitCode', key: 'inventoryUnitCode', width: 180 },
                                { title: '库存单元标识', dataIndex: 'inventoryUnitTagDisplay', key: 'inventoryUnitTagDisplay', width: 260 },
                                { title: '本次创建总数量', dataIndex: 'totalQty', key: 'totalQty', width: 130 },
                                {
                                  title: '本次创建后可用库存',
                                  key: 'availableQtyAfter',
                                  width: 150,
                                  render: (_: unknown, it: { availableQtyBefore: number | null; totalQty: number }) => (
                                    it.availableQtyBefore === null ? '-' : it.availableQtyBefore - it.totalQty
                                  ),
                                },
                              ]}
                            />
                          </Space>
                        ),
                      },
                    ].filter((it) => it.key !== 'productStats' || executionListView === 'created')}
                  />
                </Card>
              )}
            </>
          )}
        </Space>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-start' }}>
          <Button size="large" style={stepBackButtonStyle} onClick={() => setCurrentStep(1)}>上一步</Button>
        </div>
      </Card>
      )}
      <Modal
        width={1100}
        open={listFilePreviewVisible}
        title={`列表文件预览：${selectedFileName || ''}`}
        footer={null}
        onCancel={() => setListFilePreviewVisible(false)}
        destroyOnClose
      >
        <Table
          size="small"
          loading={listFilePreviewLoading}
          rowKey={(row) => String((row as ParsedRow).__sourceRowNo)}
          dataSource={listFilePreviewRows}
          pagination={{
            current: listFilePreviewPage,
            pageSize: listFilePreviewPageSize,
            total: listFilePreviewRows.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (total, range) => `第${range[0]}-${range[1]}条 共${total}条`,
            onChange: (page, pageSize) => {
              setListFilePreviewPage(page);
              setListFilePreviewPageSize(pageSize);
            },
            onShowSizeChange: (page, pageSize) => {
              setListFilePreviewPage(page);
              setListFilePreviewPageSize(pageSize);
            },
          }}
          scroll={{ x: 1100, y: 460 }}
          columns={[
            { title: '原始行号', key: '__sourceRowNo', width: 100, render: (_: unknown, row: ParsedRow) => row.__sourceRowNo || '-' },
            ...listFilePreviewHeaders.map((header) => ({
              title: header,
              key: `preview-${header}`,
              width: 180,
              render: (_: unknown, row: ParsedRow) => {
                const text = String(row[header] ?? '');
                return (
                  <Tooltip title={text}>
                    <Typography.Text ellipsis style={{ maxWidth: 160 }}>
                      {text || '-'}
                    </Typography.Text>
                  </Tooltip>
                );
              },
            })),
          ]}
          locale={{ emptyText: '暂无可预览的数据' }}
        />
      </Modal>
      <Modal
        width={1080}
        open={validationDetailVisible}
        title={`校验失败记录(共${validationSummary?.invalidRows || 0}条)`}
        footer={null}
        onCancel={() => setValidationDetailVisible(false)}
        destroyOnClose
      >
        <div
          style={{
            marginBottom: 12,
            background: '#f5f5f5',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            padding: '10px 12px',
            lineHeight: 1.8,
          }}
        >
          <Typography.Text>1 每个指令应具有唯一的纯数字序号(指令序号)</Typography.Text>
          <br />
          <Typography.Text>2 相同指令序号表示同一个指令的多条产品记录</Typography.Text>
          <br />
          <Typography.Text>3 同一指令除第一行(主行)外,其他行(副行)只需填写指令序号和产品信息,其他信息应与第一行保持一致或留空.</Typography.Text>
        </div>
        <Table
          size="small"
          rowKey="key"
          dataSource={validationDetailRows}
          pagination={{
            current: validationDetailPage,
            pageSize: validationDetailPageSize,
            total: validationDetailRows.length,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            onChange: (page, pageSize) => {
              setValidationDetailPage(page);
              setValidationDetailPageSize(pageSize);
            },
            showTotal: (total, range) => `共${total}条，显示${range[0]}-${range[1]}条`,
          }}
          scroll={{ x: 1400 }}
          columns={[
            { title: '原始行号', dataIndex: 'rowIndex', key: 'rowIndex', width: 90 },
            { title: '指令序号', dataIndex: 'serialNo', key: 'serialNo', width: 120 },
            {
              title: '错误原因',
              dataIndex: 'issues',
              key: 'issues',
              width: 420,
              render: (issues: string[]) => renderValidationIssueGroups(issues),
            },
            {
              title: '操作',
              key: 'ops',
              width: 140,
              render: (_: unknown, record: any) => (
                <Button
                  size="small"
                  onClick={() => {
                    setActiveValidationRowIndex(Number(record.rowIndex));
                    setValidationRecordVisible(true);
                  }}
                >
                  查看原记录
                </Button>
              ),
            },
          ]}
          onRow={(record: any) => ({
            style: { backgroundColor: String(record?.__groupBgColor || '#ffffff') },
          })}
          locale={{ emptyText: '当前无校验失败记录' }}
        />
      </Modal>
      <Modal
        width={760}
        open={validationRecordVisible}
        title={`原记录信息（行号 ${activeValidationRowIndex ?? '-'}）`}
        footer={null}
        onCancel={() => setValidationRecordVisible(false)}
        destroyOnClose
      >
        {!activeValidationRow && (
          <Typography.Text type="secondary">未找到该行原始记录。</Typography.Text>
        )}
        {activeValidationRow && (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Typography.Text>指令序号：{getMappedValue(activeValidationRow, '指令序号', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>仓库：{getMappedValue(activeValidationRow, '仓库', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>项目：{getMappedValue(activeValidationRow, '项目', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>库存单元标识：{getMappedValue(activeValidationRow, '库存单元标识', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>发货数量：{getMappedValue(activeValidationRow, '发货数量', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>收件人姓名：{getMappedValue(activeValidationRow, '收件人姓名', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>地址第一行：{getMappedValue(activeValidationRow, '地址第一行', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>城市：{getMappedValue(activeValidationRow, '城市', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>州/省：{getMappedValue(activeValidationRow, '州/省', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>邮编：{getMappedValue(activeValidationRow, '邮编', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>国家：{getMappedValue(activeValidationRow, '国家', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>物流跟踪号：{getMappedValue(activeValidationRow, '物流跟踪号', fieldMapping) || '-'}</Typography.Text>
            <Typography.Text>物流运单附件文件名：{getMappedValue(activeValidationRow, '物流运单附件文件名', fieldMapping) || '-'}</Typography.Text>
          </Space>
        )}
      </Modal>
      <Modal
        width={waybillPreviewType === 'zip' ? 1120 : 920}
        open={waybillPreviewVisible}
        title={waybillPreviewType === 'zip' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ whiteSpace: 'nowrap' }}>{waybillPreviewName || '附件预览'}</span>
            <Input
              allowClear
              style={{ width: 340, maxWidth: '55%' }}
              placeholder="按文件名筛选（自动忽略首尾空格与大小写）"
              value={waybillArchiveFilterText}
              onChange={(e) => setWaybillArchiveFilterText(e.target.value)}
            />
          </div>
        ) : `${waybillPreviewName || '附件预览'}${waybillPreviewEntryName ? ` / ${waybillPreviewEntryName}` : ''}`}
        footer={null}
        onCancel={() => {
          if ((waybillPreviewType === 'image' || waybillPreviewType === 'pdf') && waybillArchiveEntries.length > 0) {
            if (waybillPreviewUrl) {
              URL.revokeObjectURL(waybillPreviewUrl);
            }
            setWaybillPreviewUrl('');
            setWaybillPreviewEntryName('');
            setWaybillPreviewType('zip');
            return;
          }
          closeWaybillPreview();
        }}
        destroyOnClose
      >
        {waybillPreviewLoading && (
          <Typography.Text type="secondary">加载中...</Typography.Text>
        )}
        {!waybillPreviewLoading && waybillPreviewError && (
          <Typography.Text type="danger">{waybillPreviewError}</Typography.Text>
        )}
        {!waybillPreviewLoading && !waybillPreviewError && waybillPreviewType === 'zip' && (
          <Table
            size="small"
            rowKey={(r) => String((r as WaybillArchiveEntry).path)}
            dataSource={filteredWaybillArchiveEntries}
            pagination={{
              current: waybillArchivePage,
              pageSize: waybillArchivePageSize,
              total: filteredWaybillArchiveEntries.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setWaybillArchivePage(page);
                setWaybillArchivePageSize(pageSize);
              },
              showTotal: (total, range) => `共${total}项，显示${range[0]}-${range[1]}`,
            }}
            locale={{ emptyText: '未匹配到文件，请调整筛选关键字。' }}
            columns={[
              {
                title: '序号',
                key: '__idx',
                width: 80,
                align: 'center',
                render: (_: unknown, __: WaybillArchiveEntry, idx: number) => ((waybillArchivePage - 1) * waybillArchivePageSize + idx + 1),
              },
              {
                title: '文件名',
                dataIndex: 'path',
                key: 'path',
                render: (v: string) => getArchiveEntryFileName(v),
              },
              {
                title: '操作',
                key: 'ops',
                width: 220,
                render: (_: unknown, r: WaybillArchiveEntry) => {
                  return (
                    <Space size={8}>
                      <Button size="small" onClick={() => previewZipEntry(r.path)}>预览</Button>
                      <Button size="small" type="primary" onClick={() => downloadZipEntry(r.path)}>下载条目</Button>
                    </Space>
                  );
                },
              },
            ]}
          />
        )}
        {!waybillPreviewLoading && !waybillPreviewError && waybillPreviewType !== 'zip' && waybillArchiveEntries.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <Button
              size="small"
              onClick={() => {
                if (waybillPreviewUrl) {
                  URL.revokeObjectURL(waybillPreviewUrl);
                }
                setWaybillPreviewUrl('');
                setWaybillPreviewEntryName('');
                setWaybillPreviewType('zip');
              }}
            >
              返回压缩包条目列表
            </Button>
          </div>
        )}
        {!waybillPreviewLoading && !waybillPreviewError && waybillPreviewType === 'image' && waybillPreviewUrl && (
          <div style={{ textAlign: 'center', maxHeight: '70vh', overflow: 'auto' }}>
            <img src={waybillPreviewUrl} alt={waybillPreviewName || 'preview'} style={{ maxWidth: '100%', height: 'auto' }} />
          </div>
        )}
        {!waybillPreviewLoading && !waybillPreviewError && waybillPreviewType === 'pdf' && waybillPreviewUrl && (
          <iframe
            title={waybillPreviewName || 'pdf-preview'}
            src={waybillPreviewUrl}
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        )}
      </Modal>
      <Modal
        title={(
          <>
            列表文件表头校验未通过{' '}
            <span style={{ color: '#1677ff', fontWeight: 'normal' }}>
              {activeProfileName ? `已选择映射配置：${activeProfileName}` : '未使用任何映射配置'}
            </span>
          </>
        )}
        open={headerMismatchModalOpen}
        onCancel={() => setHeaderMismatchModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setHeaderMismatchModalOpen(false)}>关闭</Button>,
        ]}
      >
        {headerCheck.status === 'missing' && (
          <>
            <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 16 }}>
              {(headerCheck.missingDetails || []).map((d) => (
                <div key={`retry-missing-${d.field}`}>
                  <Tag color="red" style={{ borderRadius: 999, paddingInline: 10 }}>{d.field}</Tag>
                  {ADVANCED_MAPPING_ENABLED && d.reason === 'profile_drift' ? (
                    <Typography.Text type="secondary">
                      映射配置中该字段对应的来源列「{d.mappedColumn}」在本次上传的文件中不存在，请检查文件表头是否变化。
                    </Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">
                      文件中缺少该必填列。
                    </Typography.Text>
                  )}
                </div>
              ))}
            </Space>
            {ADVANCED_MAPPING_ENABLED ? (
              <>
                <Typography.Paragraph>
                  你可以修改源文件补齐这些必填列后重新上传；
                  {scenarioProfiles.length > 0
                    ? '或者选择该场景下已保存的映射配置，重新校验（可反复更换尝试），也可以新建一份：'
                    : '该场景暂无已保存的映射配置，可以新建一份：'}
                </Typography.Paragraph>
                <Space wrap>
                  {scenarioProfiles.length > 0 && (
                    <Select
                      style={{ minWidth: 320 }}
                      placeholder="选择已保存的映射配置"
                      value={activeProfileId}
                      onChange={applyProfileForRetry}
                      allowClear
                      onClear={() => applyProfileForRetry(undefined)}
                      options={scenarioProfiles.map((p) => ({
                        label: `${p.name}（${new Date(String(p.updated_at || '')).toLocaleString()}）`,
                        value: p.id,
                      }))}
                    />
                  )}
                  <Button
                    type={scenarioProfiles.length > 0 ? 'default' : 'primary'}
                    onClick={() => {
                      setHeaderMismatchModalOpen(false);
                      handleCreateNewProfile();
                      setMappingDrawerOpen(true);
                    }}
                  >
                    新建映射配置
                  </Button>
                </Space>
              </>
            ) : (
              <Typography.Paragraph>
                请检查并修改源文件后重新上传。
              </Typography.Paragraph>
            )}
          </>
        )}
        {headerCheck.status === 'error' && (
          <Typography.Text>表头预审未通过：{headerCheck.reason}</Typography.Text>
        )}
      </Modal>
    </Space>
  );
};

export default InstructionImportCreatePage;
